var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/auth/koala-token-payload.schema.ts
import { z } from "zod";

// src/auth/account-group.schema.ts
var AccountGroups = {
  KOALA: "KOALA",
  PARTNER: "PARTNER",
  THIRD_PARTY: "THIRD_PARTY"
};

// src/auth/koala-token-payload.schema.ts
var koalaTokenPayloadSchema = z.object({
  group: z.literal(AccountGroups.KOALA),
  name: z.string(),
  email: z.string().email().optional()
});

// src/auth/partner-token-payload.schema.ts
import { z as z2 } from "zod";
var partnerTokenPayloadSchema = z2.object({
  group: z2.literal(AccountGroups.PARTNER),
  name: z2.string(),
  partner: z2.strictObject({
    id: z2.string().uuid(),
    countryId: z2.string(),
    name: z2.string()
  })
});

// src/auth/subscribed-customer-token-payload.schema.ts
import { z as z3 } from "zod";
import { emailSchema } from "@repo/schema-common";
var SUBSCRIBED_CUSTOMER = "SUBSCRIBED_CUSTOMER";
var subscribedCustomerTokenPayloadSchema = z3.object({
  group: z3.literal("SUBSCRIBED_CUSTOMER"),
  name: z3.string(),
  email: emailSchema,
  bookingNumber: z3.string().min(1),
  subscriptionId: z3.string().uuid().optional()
});

// src/auth/third-party-token-payload.schema.ts
import { z as z4 } from "zod";
var thirdPartyTokenPayloadSchema = z4.object({
  group: z4.literal(AccountGroups.THIRD_PARTY),
  name: z4.string()
});

// src/common/account-number.schema.ts
import { extendApi } from "@anatine/zod-openapi";
import { z as z5 } from "zod";
var accountNumberSchema = extendApi(z5.string().trim().min(1).max(255), {
  description: "The number of the Bank Account.",
  example: "4876475938242",
  type: "string"
});

// src/common/address.schema.ts
import { z as z9 } from "zod";

// src/union-error-map.ts
import { z as z6 } from "zod";
var createUnionErrorMap = /* @__PURE__ */ __name((unionMemberNames) => (issue, ctx) => {
  if (issue.code === z6.ZodIssueCode.invalid_union) {
    const errorsPerUnionMember = issue.unionErrors.map((ue) => ue.errors.map((e) => {
      const fieldPath = e.path.join(".");
      return fieldPath ? `${fieldPath}: ${e.message}` : e.message;
    }));
    const message = errorsPerUnionMember.map((errors, i) => {
      const memberName = i < unionMemberNames.length ? unionMemberNames[i] : `possibility n\xB0${i + 1}`;
      const formattedErrors = errors.map((error) => `- ${error}`).join("\n");
      return `The input did not match the validation for ${memberName}: 
${formattedErrors}`;
    }).join("\n\n");
    return {
      message
    };
  }
  return {
    message: ctx.defaultError
  };
}, "createUnionErrorMap");

// src/common/country-only-address.schema.ts
import { extendApi as extendApi2 } from "@anatine/zod-openapi";
import { z as z7 } from "zod";
import { countryCodeSchema } from "@repo/schema-common";
var countryOnlyAddressSchema = extendApi2(z7.strictObject({
  countryCode: extendApi2(countryCodeSchema, {
    description: "ISO 3166-1 alpha-2 representation of the country.",
    example: "US"
  })
}), {
  title: "CountryOnlyAddressDto"
});

// src/common/rich-address.schema.ts
import { extendApi as extendApi3 } from "@anatine/zod-openapi";
import { z as z8 } from "zod";
var richAddressSchema = extendApi3(countryOnlyAddressSchema.extend({
  line1: extendApi3(z8.string().min(1), {
    description: "First line of the address.",
    example: "948 Fort Barry"
  }),
  line2: extendApi3(z8.string().min(1).optional(), {
    description: "Second line of the address if needed."
  }),
  line3: extendApi3(z8.string().min(1).optional(), {
    description: "Third line of the address if needed."
  }),
  line4: extendApi3(z8.string().min(1).optional(), {
    description: "Fourth line of the address if needed."
  }),
  locality: extendApi3(z8.string().min(1), {
    description: "Refer to the city/town/village.",
    example: "Sausalito"
  }),
  postcode: extendApi3(z8.string().min(1), {
    description: "Postcode of the address.",
    example: "94965"
  }),
  region: extendApi3(z8.string().min(1).optional(), {
    description: "When it makes sense, sub-national unit part of the address.",
    example: "CA"
  })
}), {
  title: "AddressDto"
});

// src/common/address.schema.ts
var addressSchema = z9.union([
  countryOnlyAddressSchema,
  richAddressSchema
], {
  errorMap: createUnionErrorMap([
    "CountryOnlyAddressDto",
    "RichAddressDto"
  ])
});

// src/common/bank-details.schema.ts
import { extendApi as extendApi10 } from "@anatine/zod-openapi";
import { z as z14 } from "zod";

// src/common/bank-details-account-number.schema.ts
import { extendApi as extendApi7 } from "@anatine/zod-openapi";

// src/common/beneficiary.schema.ts
import { extendApi as extendApi4 } from "@anatine/zod-openapi";
import { z as z10 } from "zod";
var beneficiarySchema = extendApi4(z10.strictObject({
  beneficiaryName: extendApi4(z10.string().trim().regex(/^[0-9A-Za-zÀ-ÖØ-öø-ÿ-_()'*,.\s]*$/).min(1).max(255), {
    description: "The name of the bank account owner. May only include letters, numbers, spaces, and the following special characters: / - ? : ( ) . , ' +",
    example: "Clovis Steuben",
    type: "string"
  })
}), {
  type: "object"
});

// src/common/beneficiary-address.schema.ts
import { extendApi as extendApi5 } from "@anatine/zod-openapi";
import { z as z11 } from "zod";
import { countryCodeSchema as countryCodeSchema2 } from "@repo/schema-common";
var beneficiaryAddressSchema = extendApi5(z11.strictObject({
  countryCode: extendApi5(countryCodeSchema2, {
    description: "ISO 3166-1 alpha-2 representation of the country.",
    example: "US"
  }),
  line1: extendApi5(z11.string().min(1), {
    description: "First line of the address.",
    example: "948 Fort Barry",
    type: "string"
  }),
  locality: extendApi5(z11.string().min(1), {
    description: "Refer to the city/town/village.",
    example: "Sausalito",
    type: "string"
  }),
  postcode: extendApi5(z11.string().min(1), {
    description: "Postcode of the address.",
    example: "94965",
    type: "string"
  })
}), {
  description: "Information about the bank account that will receive the money transfer",
  title: "BeneficiaryAddressDto",
  type: "object"
});

// src/common/bic.schema.ts
import { extendApi as extendApi6 } from "@anatine/zod-openapi";
import validator from "validator";
import { z as z12 } from "zod";
var bicSchema = extendApi6(z12.string().trim().min(1).refine(validator.isBIC, {
  message: "Expected BIC or SWIFT code"
}), {
  description: `The Business Identifier Code (BIC) as described in ISO 9362.
  Can be called Society for Worldwide Interbank Financial Telecommunication (SWIFT) in some cases.`,
  example: "COBADEFF",
  type: "string"
});

// src/common/bank-details-account-number.schema.ts
var bankDetailsAccountNumberSchema = extendApi7(beneficiarySchema.extend({
  accountNumber: accountNumberSchema,
  bic: bicSchema,
  beneficiaryAddress: beneficiaryAddressSchema.optional()
}), {
  description: "Bank details based on the account number.",
  title: "BankDetailsAccountNumberDto",
  type: "object"
});

// src/common/bank-details-iban.schema.ts
import { extendApi as extendApi9 } from "@anatine/zod-openapi";

// src/common/iban.schema.ts
import { extendApi as extendApi8 } from "@anatine/zod-openapi";
import validator2 from "validator";
import { z as z13 } from "zod";
var ibanSchema = extendApi8(z13.string().min(1).toUpperCase().transform((str) => str.replace(/\s+/g, "")).refine(validator2.isIBAN, {
  message: "Expected IBAN"
}), {
  description: "The International Bank Account Number (IBAN) as described in ISO 13616.",
  example: "ES4420957957066875565485",
  type: "string"
});

// src/common/bank-details-iban.schema.ts
var bankDetailsIbanSchema = extendApi9(beneficiarySchema.extend({
  bic: bicSchema,
  iban: ibanSchema
}), {
  description: "Bank details based on the IBAN.",
  title: "BankDetailsIbanDto",
  type: "object"
});

// src/common/bank-details.schema.ts
var bankDetailsSchema = extendApi10(z14.union([
  extendApi10(bankDetailsIbanSchema, {
    type: "object"
  }),
  extendApi10(bankDetailsAccountNumberSchema, {
    type: "object"
  })
]), {
  description: "The bank details where the refund should be sent.",
  type: "object"
});

// src/common/birthday.schema.ts
import { extendApi as extendApi11 } from "@anatine/zod-openapi";
import { z as z15 } from "zod";
var birthdaySchema = extendApi11(z15.string().date(), {
  description: "Birthday date of the traveler.",
  example: "1990-02-10",
  type: "string",
  minLength: 10,
  maxLength: 10,
  pattern: "^\\d{4}-\\d{2}-\\d{2}$"
});

// src/common/claim-data.schema.ts
import { extendApi as extendApi12 } from "@anatine/zod-openapi";
import { DateTime } from "luxon";
import { z as z16 } from "zod";
import { ClaimEligibilityConditionNames, ClaimStatusEnum, FraudProductConditionNameEnum, OtherProductConditionNameEnum } from "@repo/lib-koala";
import { priceSchema } from "@repo/schema-common";
var codeEnums = [
  z16.nativeEnum(ClaimEligibilityConditionNames),
  z16.nativeEnum(FraudProductConditionNameEnum),
  z16.nativeEnum(OtherProductConditionNameEnum)
];
var codeEnumValues = [
  ...Object.values(ClaimEligibilityConditionNames),
  ...Object.values(FraudProductConditionNameEnum),
  ...Object.values(OtherProductConditionNameEnum)
];
var claimDataSchema = extendApi12(z16.strictObject({
  compensation: extendApi12(priceSchema.optional(), {
    description: "Amount of the compensation."
  }),
  refundDate: extendApi12(z16.string().optional(), {
    description: "The date when the claim status switch to REFUNDED.",
    example: DateTime.utc().plus({
      months: 1
    }).startOf("hour").plus({
      hours: 8,
      minutes: 21
    }).toString(),
    format: "date-time",
    type: "string"
  }),
  lastStatusChangeDate: extendApi12(z16.string().optional(), {
    description: "The date when the claim status last changed.",
    example: DateTime.utc().plus({
      months: 1
    }).startOf("hour").plus({
      hours: 8,
      minutes: 21
    }).toString(),
    format: "date-time",
    type: "string"
  }),
  reasons: extendApi12(z16.array(z16.strictObject({
    code: extendApi12(z16.union(codeEnums), {
      description: `The code of the reason.</br>
  The list of codes for which a claim is in <code>${ClaimStatusEnum.MANUAL_VERIFICATION_NEEDED}</code> status is: [<code>${Object.values(FraudProductConditionNameEnum).join("</code>, <code>")}</code>].`,
      enum: codeEnumValues,
      oneOf: void 0,
      type: "string"
    }),
    message: extendApi12(z16.string(), {
      description: "The details of the reason."
    })
  })).optional(), {
    description: "Reasons why the claim is in this status."
  }),
  status: extendApi12(z16.nativeEnum(ClaimStatusEnum), {
    description: `The claim status are the following:
  - <code>${ClaimStatusEnum.ACCEPTED}</code>: The travelers are eligible for compensation; we are waiting to receive their bank details.
  - <code>${ClaimStatusEnum.REFUNDABLE}</code>: The bank details have been received; the bank transfer has not been processed yet.
  - <code>${ClaimStatusEnum.REFUNDED}</code>: The bank transfer has been processed; the travelers have received their compensation or will receive it shortly.
  - <code>${ClaimStatusEnum.MORE_INFO_NEEDED}</code>: Additional information is needed to determine the eligibility of the travelers.
  - <code>${ClaimStatusEnum.MANUAL_VERIFICATION_NEEDED}</code>: Claim eligibility requires manual verification or there is potential fraud; the claim requires manual verification.
  - <code>${ClaimStatusEnum.REJECTED}</code>: Based on the information provided, the travelers are not eligible for compensation.
  - <code>${ClaimStatusEnum.AUTOMATICALLY_REJECTED}</code>: The claim was automatically rejected following a flight status update.
  - <code>${ClaimStatusEnum.REFUND_ERROR}</code>: The refund could not be processed because information is missing.
  - <code>${ClaimStatusEnum.CLAIM_ERROR}</code>: The claim process failed unexpectedly; an investigation is needed on our side.`,
    example: ClaimStatusEnum.REFUNDED
  })
}), {
  description: "Claim linked to this contract."
});

// src/common/contract-document.schema.ts
import { z as z17 } from "zod";
import { ContractDocumentStatuses, ContractDocumentTypes, ContractDocumentUploaders, ContractDocumentUploadStatuses } from "@repo/lib-koala";
import { dateTimeWithUtcOffsetSchema } from "@repo/schema-common";
var contractDocumentSchema = z17.strictObject({
  id: z17.string().uuid(),
  contractId: z17.string().uuid(),
  type: z17.nativeEnum(ContractDocumentTypes),
  status: z17.nativeEnum(ContractDocumentStatuses),
  uploadStatus: z17.nativeEnum(ContractDocumentUploadStatuses),
  uploader: z17.nativeEnum(ContractDocumentUploaders),
  originalDocumentName: z17.string().optional(),
  rejectionReason: z17.string().optional(),
  preSignedUrlExpiresAt: dateTimeWithUtcOffsetSchema.optional(),
  fileSizeInBytes: z17.number().optional(),
  mimeType: z17.string().optional(),
  replacedById: z17.string().uuid().optional(),
  replacedAt: dateTimeWithUtcOffsetSchema.optional(),
  createdAt: dateTimeWithUtcOffsetSchema,
  updatedAt: dateTimeWithUtcOffsetSchema
});

// src/common/currencies.schema.ts
import { z as z18 } from "zod";
import { CURRENCIES } from "@repo/lib-koala-types";
var currenciesSchema = z18.enum(CURRENCIES);

// src/common/distribution-context.schema.ts
import { extendApi as extendApi15 } from "@anatine/zod-openapi";
import { z as z21 } from "zod";
import { countryCodeSchema as countryCodeSchema3 } from "@repo/schema-common";

// src/common/distribution-context-travel-agent.schema.ts
import { extendApi as extendApi13 } from "@anatine/zod-openapi";
import { z as z19 } from "zod";
var distributionContextTravelAgentSchema = extendApi13(z19.strictObject({
  id: extendApi13(z19.string().min(1).max(255), {
    description: "Stable identifier of the agent in the identity provider of the partner."
  })
}), {
  description: "The travel agent who created the quote on behalf of the customer."
});

// src/common/omio-ticket-refund-context.schema.ts
import { extendApi as extendApi14 } from "@anatine/zod-openapi";
import { z as z20 } from "zod";
import { priceSchema as priceSchema2 } from "@repo/schema-common";
var OMIO_PROVIDER_REFUNDABILITY_CATEGORIES = [
  "REFUNDABLE",
  "SEMI_REFUNDABLE",
  "NON_REFUNDABLE"
];
var omioProviderCancellationFeeSchema = z20.strictObject({
  type: extendApi14(z20.enum([
    "PERCENT",
    "FIXED"
  ]), {
    description: "Whether the cancellation fee is a percentage of the ticket price or a fixed amount."
  }),
  value: extendApi14(z20.number().min(0), {
    description: "The fee value: a percentage (0-100) when type is PERCENT, an amount in the ticket currency otherwise.",
    example: 30
  }),
  validUntilHoursBeforeDeparture: extendApi14(z20.number().optional(), {
    description: "Number of hours before departure until which this fee applies.",
    example: 0
  })
});
var omioFareSchema = z20.strictObject({
  provider: extendApi14(z20.string().min(1).max(255), {
    description: "The provider identifier of the fare.",
    example: "renfeConnect"
  }),
  fareId: extendApi14(z20.string().min(1).max(255).optional(), {
    description: "The provider fare identifier.",
    example: "renfeConnect.turista.standard.choose"
  }),
  fareTypeName: extendApi14(z20.string().min(1).max(255).optional(), {
    description: "The provider fare type name.",
    example: "turista.standard.choose"
  }),
  fareTypeDisplayName: extendApi14(z20.string().min(1).max(255).optional(), {
    description: "The fare display name shown to the customer.",
    example: "Choice"
  })
});
var omioTicketRefundContextSchema = z20.strictObject({
  ticketOfferId: extendApi14(z20.string().min(1).max(255), {
    description: "The identifier of the ticket offer this refund context applies to."
  }),
  coveredSegmentIds: extendApi14(z20.array(z20.string().min(1).max(255)).min(1), {
    description: "The segments covered by the ticket, matching the <code>externalId</code> of <code>data.trips[].journeys[].segments[]</code>."
  }),
  ticketPrice: extendApi14(priceSchema2, {
    description: "The price of the ticket."
  }),
  providerRefundabilityCategory: extendApi14(z20.enum(OMIO_PROVIDER_REFUNDABILITY_CATEGORIES), {
    description: "The refundability category of the ticket from the provider standpoint."
  }),
  providerCancellationFee: extendApi14(omioProviderCancellationFeeSchema.optional(), {
    description: "The provider cancellation fee applicable at quote time."
  }),
  estimatedProviderRefund: extendApi14(priceSchema2.optional(), {
    description: "The amount the provider is expected to refund at quote time."
  }),
  estimatedProviderUncoveredAmount: extendApi14(priceSchema2.optional(), {
    description: "The part of the ticket price the provider is not expected to refund at quote time."
  }),
  fare: extendApi14(omioFareSchema.optional(), {
    description: "The provider fare of the ticket."
  }),
  carrier: extendApi14(z20.string().min(1).max(255).optional(), {
    description: "The carrier operating the covered segments.",
    example: "Renfe"
  })
});

// src/common/distribution-context.schema.ts
var clientCompanySchema = z21.strictObject({
  id: z21.string().min(0).max(255),
  name: z21.string().min(0).max(255).optional(),
  countryCode: countryCodeSchema3.optional()
});
var distributionContextSchema = extendApi15(z21.strictObject({
  clientCompany: clientCompanySchema.optional(),
  device: z21.enum([
    "MOBILE",
    "WEB"
  ]).optional(),
  ticketFare: z21.string().min(1).max(255).optional(),
  sessionId: z21.string().min(1).max(255).optional(),
  travelAgent: distributionContextTravelAgentSchema.optional(),
  omioTicketRefundContexts: extendApi15(z21.array(omioTicketRefundContextSchema).min(1).max(50).optional(), {
    description: "Omio-specific refund contexts of the tickets covered by the quote, one per ticket offer."
  })
}), {
  hideDefinitions: [
    "omioTicketRefundContexts"
  ]
});

// src/common/flight.schema.ts
import { extendApi as extendApi16 } from "@anatine/zod-openapi";
import { DateTime as DateTime2 } from "luxon";
import { z as z22 } from "zod";
import { airlineIataSchema, dateTimeWithoutUtcOffsetSchema, dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema2, flightNumberSchema, iataSchema, priceSchema as priceSchema3 } from "@repo/schema-common";
var baseFlightSchema = z22.strictObject({
  airlineIata: extendApi16(airlineIataSchema, {
    description: "IATA code of the carrier.",
    example: "DL"
  }),
  arrivalAirportIata: extendApi16(iataSchema, {
    description: "IATA code of the arrival airport.",
    example: "JFK"
  }),
  departureAirportIata: extendApi16(iataSchema, {
    description: "IATA code of the departure airport.",
    example: "CDG"
  }),
  number: extendApi16(flightNumberSchema, {
    description: "Flight number.",
    example: "263"
  }),
  price: extendApi16(priceSchema3.optional(), {
    description: "Price of the flight."
  })
});
var flightWithoutTimeZoneSchema = baseFlightSchema.extend({
  arrivalDate: z22.string().transform((date) => DateTime2.fromISO(date)),
  departureDate: z22.string().transform((date) => DateTime2.fromISO(date))
});
var flightWithTimeZoneSchema = baseFlightSchema.extend({
  arrivalDate: dateTimeWithUtcOffsetSchema2,
  departureDate: dateTimeWithUtcOffsetSchema2
});
var flightWithLocalDatesSchema = baseFlightSchema.extend({
  localArrivalDate: dateTimeWithoutUtcOffsetSchema,
  localDepartureDate: dateTimeWithoutUtcOffsetSchema
});
var flightSchema = z22.union([
  flightWithoutTimeZoneSchema,
  flightWithTimeZoneSchema,
  flightWithLocalDatesSchema
]);

// src/common/flight-segment.schema.ts
import { extendApi as extendApi17 } from "@anatine/zod-openapi";
import { DateTime as DateTime3 } from "luxon";
import { z as z23 } from "zod";
import { RouteModeEnum } from "@repo/lib-koala-types";
import { dateTimeWithoutUtcOffsetSchema as dateTimeWithoutUtcOffsetSchema2, priceSchema as priceSchema4 } from "@repo/schema-common";
var flightSegmentSchema = extendApi17(baseFlightSchema.extend({
  arrivalDate: extendApi17(z23.strictObject({
    local: extendApi17(dateTimeWithoutUtcOffsetSchema2, {
      description: "Expected <strong>local date time</strong> following the ISO 8601 format without time zone.",
      example: DateTime3.utc().plus({
        months: 1
      }).startOf("hour").plus({
        hours: 8,
        minutes: 21
      }).toISO({
        includeOffset: false
      }),
      format: "date-time",
      type: "string"
    })
  }), {
    description: "Expected <strong>local</strong> arrival <strong>date time</strong> following the ISO 8601 format without time zone."
  }),
  departureDate: extendApi17(z23.strictObject({
    local: extendApi17(dateTimeWithoutUtcOffsetSchema2, {
      description: "Expected <strong>local date time</strong> following the ISO 8601 format without time zone.",
      example: DateTime3.utc().plus({
        months: 1
      }).startOf("hour").plus({
        hours: 8,
        minutes: 21
      }).toISO({
        includeOffset: false
      }),
      format: "date-time",
      type: "string"
    })
  }), {
    description: "Expected <strong>local</strong> departure <strong>date time</strong> following the ISO 8601 format without time zone."
  }),
  mode: extendApi17(z23.literal(RouteModeEnum.FLIGHT), {
    description: "Transportation mode used for this segment."
  }),
  price: extendApi17(priceSchema4.optional(), {
    description: "Price of the segment."
  })
}), {
  title: "FlightSegmentDto"
});

// src/common/generic-segment.schema.ts
import { extendApi as extendApi19 } from "@anatine/zod-openapi";
import { DateTime as DateTime4 } from "luxon";
import { z as z25 } from "zod";
import { RouteModeEnum as RouteModeEnum2 } from "@repo/lib-koala-types";
import { dateTimeWithoutUtcOffsetSchema as dateTimeWithoutUtcOffsetSchema3, priceSchema as priceSchema5 } from "@repo/schema-common";

// src/common/segment-stop-schema.ts
import { extendApi as extendApi18 } from "@anatine/zod-openapi";
import { z as z24 } from "zod";
import { countryCodeSchema as countryCodeSchema4, latitudeSchema, longitudeSchema, timezoneSchema, uicSchema } from "@repo/schema-common";
var segmentStopSchema = z24.strictObject({
  countryCode: extendApi18(countryCodeSchema4, {
    description: "ISO 3166-1 alpha-2 representation of the country.",
    example: "US"
  }),
  externalId: extendApi18(z24.string().min(1), {
    description: "Unique identifier of the stop in your Information System.",
    example: "train-station:London St Pancras"
  }),
  latitude: extendApi18(latitudeSchema.optional(), {
    description: "Latitude of the stop.",
    example: 48.862725
  }),
  longitude: extendApi18(longitudeSchema.optional(), {
    description: "Longitude of the stop.",
    example: 2.287592
  }),
  name: extendApi18(z24.string().min(1), {
    description: "Name of the stop. It might be used in communications sent to the travelers.",
    example: "John F. Kennedy International Airport"
  }),
  timezone: extendApi18(timezoneSchema, {
    description: "IANA time zone name of the current stop.",
    example: "America/New_York",
    externalDocs: {
      url: "https://www.iana.org/time-zones"
    }
  }),
  uic: extendApi18(uicSchema.optional(), {
    description: "The <strong>International Union of Railways</strong> identifier of the stop. It must be a string containing 7 numerals.",
    example: "8768600",
    externalDocs: {
      url: "https://wiki.openstreetmap.org/wiki/Key:uic_ref"
    },
    minLength: 7,
    maxLength: 7
  })
});

// src/common/generic-segment.schema.ts
var genericSegmentSchema = extendApi19(z25.strictObject({
  arrivalDate: extendApi19(z25.strictObject({
    local: extendApi19(dateTimeWithoutUtcOffsetSchema3, {
      description: "Expected <strong>local date time</strong> following the ISO 8601 format without time zone.",
      example: DateTime4.utc().plus({
        months: 1
      }).startOf("hour").plus({
        hours: 8,
        minutes: 21
      }).toISO({
        includeOffset: false
      }),
      format: "date-time",
      type: "string"
    })
  }), {
    description: "Expected <strong>local</strong> arrival <strong>date time</strong> following the ISO 8601 format without time zone."
  }),
  arrivalStop: extendApi19(segmentStopSchema, {
    description: "Arrival stop of the segment."
  }),
  departureDate: extendApi19(z25.strictObject({
    local: extendApi19(dateTimeWithoutUtcOffsetSchema3, {
      description: "Expected <strong>local date time</strong> following the ISO 8601 format without time zone.",
      example: DateTime4.utc().plus({
        months: 1
      }).startOf("hour").plus({
        hours: 8,
        minutes: 21
      }).toISO({
        includeOffset: false
      }),
      format: "date-time",
      type: "string"
    })
  }), {
    description: "Expected <strong>local</strong> departure <strong>date time</strong> following the ISO 8601 format without time zone."
  }),
  departureStop: extendApi19(segmentStopSchema, {
    description: "Departure stop of the segment."
  }),
  externalId: extendApi19(z25.string().min(1), {
    description: "Unique identifier in your Information System of the route made by this physical vehicle on this date.<br/>Example: <samp>SNCF:5842:2022-10-10</samp>",
    example: "SNCF:5842:2022-10-10"
  }),
  mode: extendApi19(z25.enum([
    RouteModeEnum2.BUS,
    RouteModeEnum2.FERRY,
    RouteModeEnum2.TRAIN
  ]), {
    description: "Transportation mode used for this segment.",
    example: RouteModeEnum2.TRAIN
  }),
  price: extendApi19(priceSchema5.optional(), {
    description: "Price of the segment."
  }),
  transportIdentifier: extendApi19(z25.string().transform((val) => val.trim() === "" ? "UNKNOWN" : val), {
    description: "Name and number of the transport used on this segment. It should be <strong>unique by day</strong>.<br/>Example: <samp>TGV InOui 8450</samp>",
    example: "TGV InOui 8450"
  })
}), {
  title: "GenericSegmentOutputDto"
});

// src/common/journey.schema.ts
import { z as z27 } from "zod";
import { priceSchema as priceSchema6 } from "@repo/schema-common";

// src/common/segment.schema.ts
import { z as z26 } from "zod";
var segmentSchema = z26.union([
  flightSegmentSchema,
  genericSegmentSchema
]);

// src/common/journey.schema.ts
var journeyWithFlightsSchema = z27.strictObject({
  flights: flightSchema.array().nonempty(),
  price: priceSchema6.optional(),
  segments: z27.array(z27.never()).default([])
});
var journeyWithSegmentsSchema = z27.strictObject({
  flights: z27.array(z27.never()).default([]),
  price: priceSchema6.optional(),
  segments: segmentSchema.array().nonempty()
});
var journeySchema = z27.union([
  journeyWithFlightsSchema,
  journeyWithSegmentsSchema
]);

// src/common/price-details.schema.ts
import { z as z28 } from "zod";
var priceDetailsSchema = z28.strictObject({
  additionalTaxAmount: z28.number(),
  brokerageFees: z28.number(),
  currency: currenciesSchema,
  distributionPartnerCommission: z28.number(),
  grossNetWrittenPremium: z28.number(),
  insuranceTaxAmount: z28.number(),
  retailPremiumTaxesIncluded: z28.number(),
  serviceTaxAmount: z28.number(),
  businessContributorCommission: z28.number().default(0)
});

// src/common/product-attachment.schema.ts
import { extendApi as extendApi20 } from "@anatine/zod-openapi";
import { z as z29 } from "zod";
import { ProductAttachmentTypeEnum, ProductTypeEnum } from "@repo/lib-koala-types";
var productAttachmentSchema = z29.strictObject({
  type: extendApi20(z29.nativeEnum(ProductAttachmentTypeEnum), {
    description: "Type of the document.",
    example: ProductAttachmentTypeEnum.TERMS_AND_CONDITIONS
  }),
  url: extendApi20(z29.string().url(), {
    description: "Link to the document."
  }),
  language: extendApi20(z29.string().min(2).max(5), {
    description: "Language of the document (using ISO 639-1 codes).",
    example: "en"
  }),
  productType: extendApi20(z29.nativeEnum(ProductTypeEnum), {
    description: `Product type associated to the document, it could be one of the following:
- <code>${ProductTypeEnum.FLIGHT_DISRUPTION}</code> for Flight Disruption product
- <code>${ProductTypeEnum.WITHDRAWAL}</code> for Flex product
- <code>${ProductTypeEnum.COMPREHENSIVE}</code> for Comprehensive product`,
    example: ProductTypeEnum.FLIGHT_DISRUPTION
  })
});

// src/common/side-effect.schema.ts
import { z as z30 } from "zod";
import { flexibleBooleanSchema } from "@repo/schema-common";
var sideEffectsSchema = z30.strictObject({
  sideEffects: flexibleBooleanSchema.default(true)
});

// src/common/stay.schema.ts
import { extendApi as extendApi21 } from "@anatine/zod-openapi";
import { DateTime as DateTime5 } from "luxon";
import { z as z31 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema3, priceSchema as priceSchema7 } from "@repo/schema-common";
var staySchema = z31.strictObject({
  address: extendApi21(addressSchema, {
    description: "Address of the place where the travelers stay."
  }),
  endDate: extendApi21(dateTimeWithUtcOffsetSchema3, {
    description: "<strong>Local</strong> end <strong>date time</strong> of the stay, following the ISO 8601 format.",
    example: DateTime5.utc().setZone("US/Pacific").plus({
      days: 15,
      months: 1
    }).startOf("hour").toISO(),
    format: "date-time",
    type: "string"
  }),
  name: extendApi21(z31.string().min(1).max(255), {
    description: "Name of the place where the travelers stay.",
    example: "Marin Headlands Visitor Center"
  }),
  price: extendApi21(priceSchema7.optional(), {
    description: "Price of the stay."
  }),
  startDate: extendApi21(dateTimeWithUtcOffsetSchema3, {
    description: "<strong>Local</strong> start <strong>date time</strong> of the stay, following the ISO 8601 format.",
    example: DateTime5.utc().setZone("US/Pacific").plus({
      months: 1
    }).startOf("hour").toISO(),
    format: "date-time",
    type: "string"
  })
});

// src/common/subscription-traveler.schema.ts
import { z as z32 } from "zod";
import { AgeRangeEnum } from "@repo/lib-koala-types";
import { countryCodeSchema as countryCodeSchema5 } from "@repo/schema-common";
var subscriptionTravelerSchema = z32.strictObject({
  ageRange: z32.nativeEnum(AgeRangeEnum).default(AgeRangeEnum.ADULT),
  birthday: z32.string().date().optional(),
  firstName: z32.string().max(255),
  lastName: z32.string().max(255),
  residenceCountryCode: countryCodeSchema5.optional()
});

// src/common/wise-account-requirement.schema.ts
import { z as z33 } from "zod";
var wiseAccountRequirementFieldGroupValueAllowedSchema = z33.object({
  key: z33.string(),
  name: z33.string().nullish()
}).passthrough();
var wiseAccountRequirementFieldGroupSchema = z33.object({
  key: z33.string(),
  name: z33.string().nullish(),
  type: z33.string(),
  refreshRequirementsOnChange: z33.boolean().nullish(),
  required: z33.boolean().nullish(),
  displayFormat: z33.string().nullish(),
  example: z33.string().nullish(),
  minLength: z33.number().nullish(),
  maxLength: z33.number().nullish(),
  validationRegexp: z33.string().nullish(),
  validationAsync: z33.unknown().nullish(),
  valuesAllowed: z33.array(wiseAccountRequirementFieldGroupValueAllowedSchema).nullish()
}).passthrough();
var wiseAccountRequirementFieldSchema = z33.object({
  name: z33.string(),
  group: z33.array(wiseAccountRequirementFieldGroupSchema)
}).passthrough();
var wiseAccountRequirementSchema = z33.object({
  type: z33.string(),
  title: z33.string(),
  usageInfo: z33.unknown().nullish(),
  fields: z33.array(wiseAccountRequirementFieldSchema)
}).passthrough();

// src/compute-price-details/compute-price-details-req-body.schema.ts
import { z as z417 } from "zod";
import { isoCurrencySchema as isoCurrencySchema13 } from "@repo/schema-common";

// src/partnership-configuration/bundles/bundle.schema.ts
import { z as z47 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema5 } from "@repo/schema-common";

// src/partnership-configuration/products/product-base.schema.ts
import { z as z46 } from "zod";
import { BusinessContributorEnum, ProductDistributionModeEnum, ProductPricingProviderEnum, ProductProviderEnum as ProductProviderEnum5, ProductTypeEnum as ProductTypeEnum2 } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema4 } from "@repo/schema-common";

// src/partnership-configuration/products/attachments/attachments.schema.ts
import { z as z35 } from "zod";

// src/partnership-configuration/products/languages.schema.ts
import { z as z34 } from "zod";
import { NON_STANDARD_LANGUAGES, STANDARD_LANGUAGES } from "@repo/lib-koala-types";
var languagesSchema = z34.union([
  z34.enum(NON_STANDARD_LANGUAGES),
  z34.enum(STANDARD_LANGUAGES)
]);

// src/partnership-configuration/products/attachments/attachments.schema.ts
var attachmentSchema = z35.strictObject({
  id: z35.string().uuid(),
  url: z35.string().url()
});
var attachmentsSchema = z35.record(languagesSchema, z35.strictObject({
  termsAndConditions: attachmentSchema,
  howTo: attachmentSchema.optional()
}));

// src/partnership-configuration/products/attachments/attachments-creation-data.schema.ts
import { z as z36 } from "zod";
var attachmentsCreationDataSchema = z36.record(languagesSchema, z36.strictObject({
  termsAndConditions: attachmentSchema,
  howTo: attachmentSchema.optional()
}));

// src/partnership-configuration/products/attachments/attachments-update-data.schema.ts
var attachmentsUpdateDataSchema = attachmentsSchema;

// src/partnership-configuration/products/attachments/comprehensive-product-attachments-creation-data.schema.ts
import { z as z37 } from "zod";
var comprehensiveProductAttachmentsCreationDataSchema = z37.record(languagesSchema, z37.strictObject({
  termsAndConditions: attachmentSchema,
  howTo: attachmentSchema.optional()
}));

// src/partnership-configuration/products/attachments/comprehensive-product-attachments-update-data.schema.ts
import { z as z38 } from "zod";
var comprehensiveProductAttachmentsUpdateDataSchema = z38.record(languagesSchema, z38.strictObject({
  termsAndConditions: attachmentSchema,
  howTo: attachmentSchema.optional()
}));

// src/partnership-configuration/products/communication-settings/communication-settings.schema.ts
import { z as z39 } from "zod";
var communicationSettingsSchema = z39.strictObject({
  id: z39.string().uuid(),
  brandedKoala: z39.boolean(),
  emailConfirmation: z39.boolean(),
  emailUpdateConfirmation: z39.boolean(),
  emailEligible: z39.boolean(),
  emailRefunded: z39.boolean(),
  emailRefundable: z39.boolean(),
  emailSuspiciousClaim: z39.boolean(),
  emailClaimRejected: z39.boolean(),
  emailClaimCreated: z39.boolean(),
  emailClaimMoreInfoNeeded: z39.boolean(),
  emailClaimManualVerificationNeeded: z39.boolean()
});

// src/partnership-configuration/products/communication-settings/communication-settings-creation-data.schema.ts
var communicationSettingsCreationDataSchema = communicationSettingsSchema;

// src/partnership-configuration/products/product-provider-external-data/ava-external-data.schema.ts
import { z as z40 } from "zod";
import { ProductProviderEnum } from "@repo/lib-koala-types";
var avaExternalDataSchema = z40.strictObject({
  productProvider: z40.literal(ProductProviderEnum.AVA),
  productId: z40.number().nonnegative(),
  destinationId: z40.number().nonnegative(),
  discountId: z40.string().optional(),
  vendorId: z40.number().default(0),
  distributorId: z40.number().default(0)
});

// src/partnership-configuration/products/product-provider-external-data/battleface-external-data.schema.ts
import { z as z41 } from "zod";
import { BattlefaceBundleEnum, ProductProviderEnum as ProductProviderEnum2 } from "@repo/lib-koala-types";
var battlefaceExternalDataSchema = z41.strictObject({
  productProvider: z41.literal(ProductProviderEnum2.BATTLEFACE),
  battlefaceBundle: z41.nativeEnum(BattlefaceBundleEnum),
  packages: z41.array(z41.string()).min(1)
});

// src/partnership-configuration/products/product-provider-external-data/meetch-external-data.schema.ts
import { z as z42 } from "zod";
import { MeetchProductType, ProductProviderEnum as ProductProviderEnum3 } from "@repo/lib-koala-types";
var meetchExternalDataSchema = z42.strictObject({
  productProvider: z42.literal(ProductProviderEnum3.MEETCH),
  production: z42.string().startsWith("prod_", {
    message: "Meetch product IDs start with 'prod_'"
  }),
  "non-production": z42.string().startsWith("prod_", {
    message: "Meetch product IDs start with 'prod_' even in non-production environments"
  }),
  meetchProductType: z42.nativeEnum(MeetchProductType)
});

// src/partnership-configuration/products/product-provider-external-data/product-provider-external-data.schema.ts
import { z as z44 } from "zod";

// src/partnership-configuration/products/product-provider-external-data/wakam-external-data.schema.ts
import { z as z43 } from "zod";
import { ProductProviderEnum as ProductProviderEnum4, WakamPartnershipCodeEnum } from "@repo/lib-koala-types";
var wakamExternalDataSchema = z43.strictObject({
  productProvider: z43.literal(ProductProviderEnum4.WAKAM),
  partnershipCode: z43.nativeEnum(WakamPartnershipCodeEnum)
});

// src/partnership-configuration/products/product-provider-external-data/product-provider-external-data.schema.ts
var productProviderExternalDataSchema = z44.discriminatedUnion("productProvider", [
  avaExternalDataSchema,
  battlefaceExternalDataSchema,
  meetchExternalDataSchema,
  wakamExternalDataSchema
]);

// src/partnership-configuration/products/subscription-settings/subscription-settings.schema.ts
import { z as z45 } from "zod";
import { AgeRangeEnum as AgeRangeEnum2, GeographicalAreaEnum, JSON_LOGIC_ALWAYS_TRUE_CONDITION } from "@repo/lib-koala-types";
import { durationSchema } from "@repo/schema-common";
var subscriptionSettingsSchema = z45.strictObject({
  id: z45.string().uuid(),
  allowedCountriesOfResidence: z45.array(z45.string().max(2)),
  allowedRegions: z45.array(z45.string().max(255)),
  flightsConnected: z45.boolean(),
  forbiddenAgeRanges: z45.array(z45.nativeEnum(AgeRangeEnum2)),
  forbiddenAirlines: z45.array(z45.string().max(2)),
  forbiddenAirports: z45.array(z45.string().max(3)),
  forbiddenFlights: z45.array(z45.string()),
  preConditions: z45.string().default(JSON.stringify(JSON_LOGIC_ALWAYS_TRUE_CONDITION)),
  minimumPeriodBeforeStart: durationSchema.optional(),
  maximumPeriodBeforeStart: durationSchema.optional(),
  minimumConnectingTime: durationSchema.optional(),
  minimumFlightsCount: z45.number().optional(),
  maximumFlightsCount: z45.number().optional(),
  maximumCompensationRatio: z45.number().optional(),
  allowedGeographicalArea: z45.nativeEnum(GeographicalAreaEnum).optional()
});

// src/partnership-configuration/products/subscription-settings/subscription-settings-creation-data.schema.ts
var subscriptionSettingsCreationDataSchema = subscriptionSettingsSchema.pick({
  id: true,
  allowedCountriesOfResidence: true,
  allowedGeographicalArea: true,
  allowedRegions: true,
  flightsConnected: true,
  forbiddenAgeRanges: true,
  forbiddenAirlines: true,
  forbiddenAirports: true,
  forbiddenFlights: true,
  maximumCompensationRatio: true,
  maximumFlightsCount: true,
  minimumFlightsCount: true,
  maximumPeriodBeforeStart: true,
  minimumPeriodBeforeStart: true,
  minimumConnectingTime: true,
  preConditions: true
});

// src/partnership-configuration/products/subscription-settings/subscription-settings-update-data.schema.ts
var subscriptionSettingsUpdateDataSchema = subscriptionSettingsSchema;

// src/partnership-configuration/products/product-base.schema.ts
var baseProductSchema = z46.strictObject({
  id: z46.string().uuid(),
  partnerId: z46.string().uuid(),
  type: z46.nativeEnum(ProductTypeEnum2),
  name: z46.string().min(1).regex(/^[^,]*$/, {
    message: "Commas are not allowed."
  }).regex(/^[^\n\r]*$/, {
    message: "Line breaks are not allowed."
  }),
  displayName: z46.string().min(1).regex(/^[^,]*$/, {
    message: "Commas are not allowed."
  }).regex(/^[^\n\r]*$/, {
    message: "Line breaks are not allowed."
  }),
  description: z46.string().regex(/^[^,]*$/, {
    message: "Commas are not allowed."
  }).regex(/^[^\n\r]*$/, {
    message: "Line breaks are not allowed."
  }),
  version: z46.number().int().min(0),
  rootProductId: z46.string().uuid(),
  pricingProvider: z46.nativeEnum(ProductPricingProviderEnum),
  provider: z46.enum([
    ProductProviderEnum5.AVA,
    ProductProviderEnum5.AXERIA,
    ProductProviderEnum5.KOALA,
    ProductProviderEnum5.MEETCH,
    ProductProviderEnum5.SEYNA,
    ProductProviderEnum5.WAKAM,
    ProductProviderEnum5.EIR,
    ProductProviderEnum5.BATTLEFACE
  ]),
  distributionMode: z46.nativeEnum(ProductDistributionModeEnum),
  subscriptionSettings: subscriptionSettingsSchema,
  communicationSettings: communicationSettingsSchema,
  attachments: attachmentsSchema,
  requiresAttachments: z46.boolean(),
  retractionDelay: z46.number().int().optional(),
  brokerageFeesRatio: z46.number().min(0).optional(),
  createdAt: dateTimeWithUtcOffsetSchema4,
  updatedAt: dateTimeWithUtcOffsetSchema4,
  productProviderExternalData: productProviderExternalDataSchema.optional(),
  businessContributor: z46.nativeEnum(BusinessContributorEnum).optional(),
  businessContributorCommissionRatio: z46.number().min(0).optional()
});

// src/partnership-configuration/bundles/bundle.schema.ts
var bundleSchema = z47.strictObject({
  id: z47.string().uuid(),
  rootBundleId: z47.string().uuid(),
  name: z47.string().min(1),
  productBundles: z47.array(baseProductSchema.pick({
    id: true,
    name: true,
    type: true,
    version: true,
    rootProductId: true
  }).extend({
    productBundleId: z47.string().uuid()
  })),
  version: z47.number().int().positive(),
  createdAt: dateTimeWithUtcOffsetSchema5,
  updatedAt: dateTimeWithUtcOffsetSchema5
});

// src/partnership-configuration/bundles/create-bundle/create-bundle-req-body.schema.ts
import { z as z48 } from "zod";
var createBundleReqBodySchema = bundleSchema.pick({
  name: true,
  id: true
}).extend({
  productBundles: z48.array(z48.strictObject({
    productId: z48.string().uuid(),
    productBundleId: z48.string().uuid()
  })).min(1, "Bundle must include at least one product bundle.")
});

// src/partnership-configuration/bundles/create-bundle/create-bundle-req-params.schema.ts
import { z as z49 } from "zod";
var createBundleReqParamsSchema = z49.strictObject({
  partnerId: z49.string().uuid()
});

// src/partnership-configuration/bundles/create-bundle/create-bundle-res-body.schema.ts
var createBundleResBodySchema = bundleSchema.pick({
  id: true,
  name: true,
  version: true,
  rootBundleId: true,
  createdAt: true,
  updatedAt: true,
  productBundles: true
});

// src/partnership-configuration/bundles/create-new-bundle-version/create-new-bundle-version-req-body.schema.ts
import { z as z50 } from "zod";
var createNewBundleVersionReqBodySchema = z50.strictObject({
  id: z50.string().uuid(),
  version: z50.number().int().positive().optional(),
  productBundles: z50.array(z50.strictObject({
    productId: z50.string().uuid(),
    productBundleId: z50.string().uuid()
  })).min(1)
});

// src/partnership-configuration/bundles/create-new-bundle-version/create-new-bundle-version-req-params.schema.ts
import { z as z51 } from "zod";
var createNewBundleVersionReqParamsSchema = z51.strictObject({
  partnerId: z51.string().uuid(),
  rootBundleId: z51.string().uuid()
});

// src/partnership-configuration/bundles/create-new-bundle-version/create-new-bundle-version-res-body.schema.ts
var createNewBundleVersionResBodySchema = bundleSchema.pick({
  id: true,
  name: true,
  version: true,
  rootBundleId: true,
  createdAt: true,
  updatedAt: true,
  productBundles: true
});

// src/partnership-configuration/bundles/get-bundle/get-bundle-req-params.schema.ts
import { z as z52 } from "zod";
var getBundleReqParamsSchema = z52.strictObject({
  partnerId: z52.string().uuid(),
  bundleId: z52.string().uuid()
});

// src/partnership-configuration/bundles/get-bundle/get-bundle-res-body.schema.ts
var getBundleResBodySchema = bundleSchema.pick({
  id: true,
  name: true,
  version: true,
  rootBundleId: true,
  createdAt: true,
  updatedAt: true,
  productBundles: true
});

// src/partnership-configuration/bundles/get-bundle-overview/get-bundle-overview-req-params.schema.ts
import { z as z53 } from "zod";
var getBundleOverviewReqParamsSchema = z53.strictObject({
  partnerId: z53.string().uuid(),
  bundleId: z53.string().uuid()
});

// src/partnership-configuration/bundles/get-bundle-overview/get-bundle-overview-res-body.schema.ts
import { z as z113 } from "zod";

// src/partnership-configuration/products/claim-settings/claim-settings.schema.ts
import { z as z54 } from "zod";
import { BankDetailsTypeEnum, ClaimPaymentFlowEnum } from "@repo/lib-koala-types";
import { durationSchema as durationSchema2 } from "@repo/schema-common";
var claimSettingsSchema = z54.strictObject({
  id: z54.string().uuid(),
  coversCancelation: z54.boolean(),
  coversDelay: z54.boolean(),
  coversDiversion: z54.boolean(),
  coversLoss: z54.boolean(),
  validityPeriod: durationSchema2,
  bankDetailsType: z54.nativeEnum(BankDetailsTypeEnum),
  claimPaymentFlow: z54.nativeEnum(ClaimPaymentFlowEnum),
  requireCancellationProof: z54.boolean(),
  minimumClaimPeriodAfterCreation: durationSchema2,
  cancelationCoveredPeriod: durationSchema2.optional(),
  minimumFlightDelay: durationSchema2.optional()
});

// src/partnership-configuration/products/claim-settings/claim-settings-creation-data.schema.ts
import { BankDetailsTypeEnum as BankDetailsTypeEnum2, ClaimPaymentFlowEnum as ClaimPaymentFlowEnum2 } from "@repo/lib-koala";
var claimSettingsCreationDataSchema = claimSettingsSchema.pick({
  id: true,
  bankDetailsType: true,
  claimPaymentFlow: true,
  cancelationCoveredPeriod: true,
  coversCancelation: true,
  coversDelay: true,
  coversDiversion: true,
  coversLoss: true,
  requireCancellationProof: true,
  minimumFlightDelay: true,
  minimumClaimPeriodAfterCreation: true,
  validityPeriod: true
}).refine((val) => {
  if (val.bankDetailsType === BankDetailsTypeEnum2.NOT_HANDLED && val.claimPaymentFlow === ClaimPaymentFlowEnum2.B2C) {
    return false;
  }
  return true;
}, {
  message: 'The claim payment flow of the product cannot be "B2C" if the bank details type is "NOT_HANDLED"'
});

// src/partnership-configuration/products/commission-settings/commission-settings.schema.ts
import { z as z56 } from "zod";

// src/partnership-configuration/products/commission-settings/commission-settings-item.schema.ts
import { z as z55 } from "zod";
import { PriceSettingTypeEnum, ProductCommissionSettingCoefficientPropertyEnum } from "@repo/lib-koala-types";
var commissionSettingsItemSchema = z55.strictObject({
  id: z55.string().uuid(),
  type: z55.nativeEnum(PriceSettingTypeEnum),
  isPerTraveler: z55.boolean(),
  value: z55.number().min(0),
  maximumValue: z55.number().nonnegative().optional(),
  minimumValue: z55.number().nonnegative().optional(),
  fixedValue: z55.number().nonnegative().optional(),
  coefficientValue: z55.number().nonnegative().optional(),
  coefficientProperty: z55.nativeEnum(ProductCommissionSettingCoefficientPropertyEnum)
});

// src/partnership-configuration/products/commission-settings/commission-settings.schema.ts
var commissionSettingsSchema = z56.strictObject({
  insurer: commissionSettingsItemSchema,
  partner: commissionSettingsItemSchema,
  koala: commissionSettingsItemSchema
});

// src/partnership-configuration/products/commission-settings/commission-settings-creation-data.schema.ts
import { z as z57 } from "zod";
var commissionsSettingsCreationDataSchema = z57.strictObject({
  insurer: commissionSettingsItemSchema,
  partner: commissionSettingsItemSchema,
  koala: commissionSettingsItemSchema
});

// src/partnership-configuration/products/commission-settings/commission-settings-update-data.schema.ts
import { z as z58 } from "zod";
var commissionsSettingsUpdateDataSchema = z58.strictObject({
  insurer: commissionSettingsItemSchema,
  partner: commissionSettingsItemSchema,
  koala: commissionSettingsItemSchema
});

// src/partnership-configuration/products/compensation-settings/base-compensation-settings.schema.ts
import { z as z59 } from "zod";
import { JSON_LOGIC_ALWAYS_TRUE_CONDITION as JSON_LOGIC_ALWAYS_TRUE_CONDITION2 } from "@repo/lib-koala-types";
var baseProductCompensationSettingSchema = z59.strictObject({
  id: z59.string().uuid(),
  maxValueAppliesPerBag: z59.boolean(),
  maxValueAppliesPerTraveler: z59.boolean(),
  maximumValue: z59.number().nonnegative().optional(),
  value: z59.number().nonnegative(),
  marketingExponentPrecision: z59.number().int().nonnegative(),
  conditions: z59.string().default(JSON.stringify(JSON_LOGIC_ALWAYS_TRUE_CONDITION2))
});

// src/partnership-configuration/products/compensation-settings/coefficient-compensation-settings.schema.ts
import { z as z60 } from "zod";
import { PriceSettingTypeEnum as PriceSettingTypeEnum2, ProductCompensationSettingPropertyEnum } from "@repo/lib-koala-types";
var coefficientCompensationSettingSchema = baseProductCompensationSettingSchema.extend({
  type: z60.literal(PriceSettingTypeEnum2.COEFFICIENT),
  coefficientProperty: z60.enum([
    ProductCompensationSettingPropertyEnum.TARGET_PRICE,
    ProductCompensationSettingPropertyEnum.CANCELLATION_FEES,
    ProductCompensationSettingPropertyEnum.NON_REFUNDABLE_TRIP_PRICE
  ])
});

// src/partnership-configuration/products/compensation-settings/compensation-settings.schema.ts
import { z as z62 } from "zod";

// src/partnership-configuration/products/compensation-settings/fixed-compensation-settings.schema.ts
import { z as z61 } from "zod";
import { PriceSettingTypeEnum as PriceSettingTypeEnum3 } from "@repo/lib-koala-types";
var fixedCompensationSettingSchema = baseProductCompensationSettingSchema.extend({
  type: z61.literal(PriceSettingTypeEnum3.FIXED)
});

// src/partnership-configuration/products/compensation-settings/compensation-settings.schema.ts
var compensationSettingSchema = z62.discriminatedUnion("type", [
  fixedCompensationSettingSchema,
  coefficientCompensationSettingSchema
]);

// src/partnership-configuration/products/compensation-settings/compensation-settings-creation-data.schema.ts
import { z as z63 } from "zod";
var compensationSettingsCreationDataSchema = z63.discriminatedUnion("type", [
  fixedCompensationSettingSchema.pick({
    id: true,
    type: true,
    value: true,
    conditions: true,
    marketingExponentPrecision: true,
    maximumValue: true,
    maxValueAppliesPerBag: true,
    maxValueAppliesPerTraveler: true
  }),
  coefficientCompensationSettingSchema.pick({
    id: true,
    type: true,
    value: true,
    coefficientProperty: true,
    maximumValue: true,
    maxValueAppliesPerBag: true,
    maxValueAppliesPerTraveler: true,
    conditions: true,
    marketingExponentPrecision: true
  })
]);

// src/partnership-configuration/products/create-product/base-product-creation-data.schema.ts
var baseProductCreationDataSchema = baseProductSchema.pick({
  id: true,
  type: true,
  name: true,
  displayName: true,
  description: true,
  version: true,
  requiresAttachments: true,
  distributionMode: true,
  pricingProvider: true,
  retractionDelay: true,
  provider: true,
  brokerageFeesRatio: true,
  rootProductId: true,
  productProviderExternalData: true,
  businessContributor: true,
  businessContributorCommissionRatio: true
}).extend({
  communicationSettings: communicationSettingsCreationDataSchema,
  subscriptionSettings: subscriptionSettingsCreationDataSchema
}).partial({
  rootProductId: true
});

// src/partnership-configuration/products/create-product/create-baggage-disruption-product-req-body.schema.ts
import { z as z80 } from "zod";
import { CoveredEventTypeEnum, ProductTypeEnum as ProductTypeEnum3 } from "@repo/lib-koala-types";

// src/partnership-configuration/products/per-currency-settings/comprehensive-per-currency-settings.schema.ts
import { z as z72 } from "zod";

// src/partnership-configuration/products/pricing-settings/base-coefficient-pricing-settings.schema.ts
import { z as z65 } from "zod";
import { PriceSettingTypeEnum as PriceSettingTypeEnum4, ProductPricingSettingCoefficientPropertyEnum } from "@repo/lib-koala-types";

// src/partnership-configuration/products/pricing-settings/base-pricing-settings.schema.ts
import { z as z64 } from "zod";
var basePricingSettingsSchema = z64.strictObject({
  id: z64.string().uuid(),
  isPerTraveler: z64.boolean(),
  value: z64.number().nonnegative(),
  maximumValue: z64.number().nonnegative().optional(),
  minimumValue: z64.number().nonnegative().optional(),
  marketingExponentPrecision: z64.number().int().nonnegative()
});

// src/partnership-configuration/products/pricing-settings/base-coefficient-pricing-settings.schema.ts
var baseCoefficientPricingSettingsSchema = basePricingSettingsSchema.extend({
  type: z65.literal(PriceSettingTypeEnum4.COEFFICIENT),
  coefficientProperty: z65.nativeEnum(ProductPricingSettingCoefficientPropertyEnum)
});

// src/partnership-configuration/products/pricing-settings/base-fixed-pricing-settings.schema.ts
import { z as z66 } from "zod";
import { PriceSettingTypeEnum as PriceSettingTypeEnum5 } from "@repo/lib-koala-types";
var baseFixedPricingSettingsSchema = basePricingSettingsSchema.extend({
  type: z66.literal(PriceSettingTypeEnum5.FIXED)
});

// src/partnership-configuration/products/pricing-settings/pricing-settings.schema.ts
import { z as z67 } from "zod";
var pricingSettingsSchema = z67.discriminatedUnion("type", [
  baseCoefficientPricingSettingsSchema,
  baseFixedPricingSettingsSchema
]);

// src/partnership-configuration/products/pricing-settings/pricing-settings-creation-data.schema.ts
import { z as z68 } from "zod";
var pricingSettingsCreationDataSchema = z68.discriminatedUnion("type", [
  baseCoefficientPricingSettingsSchema.pick({
    id: true,
    type: true,
    coefficientProperty: true,
    value: true,
    maximumValue: true,
    minimumValue: true,
    isPerTraveler: true,
    marketingExponentPrecision: true
  }),
  baseFixedPricingSettingsSchema.pick({
    id: true,
    value: true,
    type: true,
    isPerTraveler: true,
    marketingExponentPrecision: true,
    maximumValue: true,
    minimumValue: true
  })
]);

// src/partnership-configuration/products/pricing-settings/withdrawal-coefficient-pricing-settings.schema.ts
import { z as z69 } from "zod";
import { WITHDRAWAL_ALLOWED_COEFFICIENT_PROPERTIES } from "@repo/lib-koala-types";
var withdrawalCoefficientPricingSettingsSchema = baseCoefficientPricingSettingsSchema.pick({
  id: true,
  type: true,
  value: true,
  maximumValue: true,
  minimumValue: true,
  marketingExponentPrecision: true
}).extend({
  coefficientProperty: z69.enum(WITHDRAWAL_ALLOWED_COEFFICIENT_PROPERTIES),
  isPerTraveler: z69.literal(false),
  value: z69.number().nonnegative().max(1)
});

// src/partnership-configuration/products/pricing-settings/withdrawal-coefficient-pricing-settings-creation-data.schema.ts
var withdrawalCoefficientPricingSettingsCreationDataSchema = withdrawalCoefficientPricingSettingsSchema;

// src/partnership-configuration/products/pricing-settings/withdrawal-fixed-pricing-settings.schema.ts
var withdrawalFixedPricingSettingsSchema = baseFixedPricingSettingsSchema.pick({
  id: true,
  value: true,
  type: true,
  marketingExponentPrecision: true,
  maximumValue: true,
  minimumValue: true,
  isPerTraveler: true
});

// src/partnership-configuration/products/pricing-settings/withdrawal-fixed-pricing-settings-creation-data.schema.ts
var withdrawalFixedPricingSettingsCreationDataSchema = withdrawalFixedPricingSettingsSchema;

// src/partnership-configuration/products/pricing-settings/withdrawal-pricing-settings.schema.ts
import { z as z70 } from "zod";
var withdrawalPricingSettingsSchema = z70.discriminatedUnion("type", [
  withdrawalCoefficientPricingSettingsSchema,
  withdrawalFixedPricingSettingsSchema
]);

// src/partnership-configuration/products/pricing-settings/withdrawal-pricing-settings-creation-data.schema.ts
import { z as z71 } from "zod";
var withdrawalPricingSettingsCreationDataSchema = z71.discriminatedUnion("type", [
  withdrawalCoefficientPricingSettingsCreationDataSchema,
  withdrawalFixedPricingSettingsCreationDataSchema
]);

// src/partnership-configuration/products/per-currency-settings/comprehensive-per-currency-settings.schema.ts
var comprehensivePerCurrencySettingsSchema = z72.record(currenciesSchema, z72.strictObject({
  pricingSettings: pricingSettingsSchema,
  commissionSettings: commissionSettingsSchema
}));

// src/partnership-configuration/products/per-currency-settings/comprehensive-per-currency-settings-creation-data.schema.ts
import { z as z73 } from "zod";
var comprehensivePerCurrencySettingsCreationDataSchema = z73.record(currenciesSchema, z73.strictObject({
  pricingSettings: pricingSettingsCreationDataSchema,
  commissionSettings: commissionsSettingsCreationDataSchema
}));

// src/partnership-configuration/products/per-currency-settings/comprehensive-per-currency-settings-update-data.schema.ts
import { z as z74 } from "zod";
var comprehensivePerCurrencySettingsUpdateDataSchema = z74.record(currenciesSchema, z74.strictObject({
  pricingSettings: pricingSettingsCreationDataSchema,
  commissionSettings: commissionsSettingsCreationDataSchema
}));

// src/partnership-configuration/products/per-currency-settings/per-currency-settings.schema.ts
import { z as z75 } from "zod";
var perCurrencySettingsSchema = z75.record(currenciesSchema, z75.strictObject({
  pricingSettings: pricingSettingsSchema,
  commissionSettings: commissionSettingsSchema,
  compensationSettings: compensationSettingSchema.array()
}));

// src/partnership-configuration/products/per-currency-settings/per-currency-settings-creation-data.schema.ts
import { z as z76 } from "zod";
var perCurrencySettingsCreationDataSchema = z76.record(currenciesSchema, z76.strictObject({
  pricingSettings: pricingSettingsCreationDataSchema,
  commissionSettings: commissionsSettingsCreationDataSchema,
  compensationSettings: compensationSettingsCreationDataSchema.array()
}));

// src/partnership-configuration/products/per-currency-settings/per-currency-settings-update-data.schema.ts
import { z as z77 } from "zod";
var perCurrencySettingsUpdateDataSchema = z77.record(currenciesSchema, z77.strictObject({
  pricingSettings: pricingSettingsCreationDataSchema,
  commissionSettings: commissionsSettingsCreationDataSchema,
  compensationSettings: compensationSettingsCreationDataSchema.array()
}));

// src/partnership-configuration/products/per-currency-settings/withdrawal-per-currency-settings-creation-data.schema.ts
import { z as z78 } from "zod";
var withdrawalPerCurrencySettingsCreationDataSchema = z78.record(currenciesSchema, z78.strictObject({
  pricingSettings: withdrawalPricingSettingsCreationDataSchema,
  commissionSettings: commissionsSettingsCreationDataSchema,
  compensationSettings: compensationSettingsCreationDataSchema.array()
}));

// src/partnership-configuration/products/per-currency-settings/withdrawal-per-currency-settings-update-data.schema.ts
import { z as z79 } from "zod";
var withdrawalPerCurrencySettingsUpdateDataSchema = z79.record(currenciesSchema, z79.strictObject({
  pricingSettings: withdrawalPricingSettingsCreationDataSchema,
  commissionSettings: commissionsSettingsCreationDataSchema,
  compensationSettings: compensationSettingsCreationDataSchema.array()
}));

// src/partnership-configuration/products/create-product/create-baggage-disruption-product-req-body.schema.ts
var createBaggageDisruptionProductReqBodySchema = baseProductCreationDataSchema.extend({
  type: z80.literal(ProductTypeEnum3.BAGGAGE_DISRUPTION),
  coveredEventType: z80.literal(CoveredEventTypeEnum.TRIP),
  claimSettings: claimSettingsCreationDataSchema,
  perCurrencySettings: perCurrencySettingsCreationDataSchema,
  attachments: attachmentsCreationDataSchema
});

// src/partnership-configuration/products/create-product/create-cancellation-without-proof-product-req-body.schema.ts
import { z as z81 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum2, ProductTypeEnum as ProductTypeEnum4 } from "@repo/lib-koala-types";
var createCancellationWithoutProofProductReqBodySchema = baseProductCreationDataSchema.extend({
  type: z81.literal(ProductTypeEnum4.CANCELLATION_WITHOUT_PROOF),
  coveredEventType: z81.literal(CoveredEventTypeEnum2.TRIP),
  claimSettings: claimSettingsCreationDataSchema,
  perCurrencySettings: perCurrencySettingsCreationDataSchema,
  attachments: attachmentsCreationDataSchema
});

// src/partnership-configuration/products/create-product/create-change-for-any-reason-product-req-body.schema.ts
import { z as z82 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum3, ProductTypeEnum as ProductTypeEnum5 } from "@repo/lib-koala-types";
var createChangeForAnyReasonProductReqBodySchema = baseProductCreationDataSchema.extend({
  type: z82.literal(ProductTypeEnum5.CHANGE_FOR_ANY_REASON),
  coveredEventType: z82.literal(CoveredEventTypeEnum3.TRIP),
  claimSettings: claimSettingsCreationDataSchema,
  perCurrencySettings: perCurrencySettingsCreationDataSchema,
  attachments: attachmentsCreationDataSchema
});

// src/partnership-configuration/products/create-product/create-comprehensive-product-req-body.schema.ts
import { z as z83 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum4, ProductDistributionModeEnum as ProductDistributionModeEnum2, ProductTypeEnum as ProductTypeEnum6 } from "@repo/lib-koala-types";
var createComprehensiveProductReqBodySchema = baseProductCreationDataSchema.extend({
  type: z83.literal(ProductTypeEnum6.COMPREHENSIVE),
  coveredEventType: z83.literal(CoveredEventTypeEnum4.TRIP),
  perCurrencySettings: comprehensivePerCurrencySettingsCreationDataSchema,
  attachments: comprehensiveProductAttachmentsCreationDataSchema,
  distributionMode: z83.literal(ProductDistributionModeEnum2.BROKERAGE)
});

// src/partnership-configuration/products/create-product/create-fdc-product-req-body.schema.ts
import { z as z84 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum5, ProductTypeEnum as ProductTypeEnum7 } from "@repo/lib-koala-types";
var createFdcProductReqBodySchema = baseProductCreationDataSchema.extend({
  type: z84.literal(ProductTypeEnum7.FLIGHT_DISRUPTION),
  coveredEventType: z84.literal(CoveredEventTypeEnum5.FLIGHT),
  claimSettings: claimSettingsCreationDataSchema,
  perCurrencySettings: perCurrencySettingsCreationDataSchema,
  attachments: attachmentsCreationDataSchema
});

// src/partnership-configuration/products/create-product/create-product-req-body.schema.ts
import { z as z86 } from "zod";

// src/partnership-configuration/products/create-product/create-withdrawal-product-req-body.schema.ts
import { z as z85 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum6, ProductTypeEnum as ProductTypeEnum8 } from "@repo/lib-koala-types";
var createWithdrawalProductReqBodySchema = baseProductCreationDataSchema.extend({
  type: z85.literal(ProductTypeEnum8.WITHDRAWAL),
  coveredEventType: z85.union([
    z85.literal(CoveredEventTypeEnum6.TRIP),
    z85.literal(CoveredEventTypeEnum6.FLIGHT)
  ]),
  claimSettings: claimSettingsCreationDataSchema,
  perCurrencySettings: withdrawalPerCurrencySettingsCreationDataSchema,
  attachments: attachmentsCreationDataSchema
});

// src/partnership-configuration/products/create-product/create-product-req-body.schema.ts
var createProductReqBodySchema = z86.discriminatedUnion("type", [
  createComprehensiveProductReqBodySchema,
  createFdcProductReqBodySchema,
  createChangeForAnyReasonProductReqBodySchema,
  createWithdrawalProductReqBodySchema,
  createCancellationWithoutProofProductReqBodySchema,
  createBaggageDisruptionProductReqBodySchema
]);

// src/partnership-configuration/products/create-product/create-product-req-params.schema.ts
import { z as z87 } from "zod";
var createProductReqParamsSchema = z87.strictObject({
  partnerId: z87.string().uuid()
});

// src/partnership-configuration/products/create-product/create-product-res-body.schema.ts
var createProductResBodySchema = baseProductSchema.pick({
  id: true,
  name: true,
  type: true
});

// src/partnership-configuration/products/get-active-products/get-active-products-req-params.schema.ts
import { z as z88 } from "zod";
var getActiveProductsReqParamsSchema = z88.strictObject({
  partnerId: z88.string().uuid()
});

// src/partnership-configuration/products/get-active-products/get-active-products-res-body.schema.ts
import { z as z95 } from "zod";

// src/partnership-configuration/products/product-baggage-disruption.schema.ts
import { z as z89 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum7, ProductTypeEnum as ProductTypeEnum9 } from "@repo/lib-koala-types";
var baggageDisruptionProductSchema = baseProductSchema.extend({
  type: z89.literal(ProductTypeEnum9.BAGGAGE_DISRUPTION),
  coveredEventType: z89.literal(CoveredEventTypeEnum7.TRIP),
  claimSettings: claimSettingsSchema,
  perCurrencySettings: perCurrencySettingsSchema
});

// src/partnership-configuration/products/product-cancellation-without-proof.schema.ts
import { z as z90 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum8, ProductTypeEnum as ProductTypeEnum10 } from "@repo/lib-koala-types";
var cancellationWithoutProofProductSchema = baseProductSchema.extend({
  type: z90.literal(ProductTypeEnum10.CANCELLATION_WITHOUT_PROOF),
  coveredEventType: z90.enum([
    CoveredEventTypeEnum8.TRIP
  ]),
  claimSettings: claimSettingsSchema,
  perCurrencySettings: perCurrencySettingsSchema
});

// src/partnership-configuration/products/product-change-for-any-reason.schema.ts
import { z as z91 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum9, ProductTypeEnum as ProductTypeEnum11 } from "@repo/lib-koala-types";
var changeForAnyReasonProductSchema = baseProductSchema.extend({
  type: z91.literal(ProductTypeEnum11.CHANGE_FOR_ANY_REASON),
  coveredEventType: z91.literal(CoveredEventTypeEnum9.TRIP),
  claimSettings: claimSettingsSchema,
  perCurrencySettings: perCurrencySettingsSchema
});

// src/partnership-configuration/products/product-comprehensive.schema.ts
import { z as z92 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum10, ProductTypeEnum as ProductTypeEnum12 } from "@repo/lib-koala-types";
var comprehensiveProductSchema = baseProductSchema.extend({
  type: z92.literal(ProductTypeEnum12.COMPREHENSIVE),
  coveredEventType: z92.literal(CoveredEventTypeEnum10.TRIP),
  perCurrencySettings: comprehensivePerCurrencySettingsSchema
});

// src/partnership-configuration/products/product-fdc.schema.ts
import { z as z93 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum11, ProductTypeEnum as ProductTypeEnum13 } from "@repo/lib-koala-types";
var flightDisruptionProductSchema = baseProductSchema.extend({
  type: z93.literal(ProductTypeEnum13.FLIGHT_DISRUPTION),
  coveredEventType: z93.literal(CoveredEventTypeEnum11.FLIGHT),
  claimSettings: claimSettingsSchema,
  perCurrencySettings: perCurrencySettingsSchema
});

// src/partnership-configuration/products/product-withdrawal.schema.ts
import { z as z94 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum12, ProductTypeEnum as ProductTypeEnum14 } from "@repo/lib-koala-types";
var withdrawalProductSchema = baseProductSchema.extend({
  type: z94.literal(ProductTypeEnum14.WITHDRAWAL),
  coveredEventType: z94.enum([
    CoveredEventTypeEnum12.TRIP,
    CoveredEventTypeEnum12.FLIGHT
  ]),
  claimSettings: claimSettingsSchema,
  perCurrencySettings: perCurrencySettingsSchema
});

// src/partnership-configuration/products/get-active-products/get-active-products-res-body.schema.ts
var bundlesAndCampaignsSchema = z95.object({
  bundles: z95.strictObject({
    id: z95.string(),
    productBundleId: z95.string().uuid(),
    name: z95.string()
  }).array(),
  campaigns: z95.strictObject({
    id: z95.string(),
    name: z95.string()
  }).array()
});
var getActiveProductsResBodySchema = z95.array(bundlesAndCampaignsSchema.and(z95.discriminatedUnion("type", [
  comprehensiveProductSchema.strip(),
  changeForAnyReasonProductSchema.strip(),
  withdrawalProductSchema.strip(),
  flightDisruptionProductSchema.strip(),
  cancellationWithoutProofProductSchema.strip(),
  baggageDisruptionProductSchema.strip()
])));

// src/partnership-configuration/products/get-product/get-baggage-disruption-product-res-body.schema.ts
var getBaggageDisruptionProductResBodySchema = baggageDisruptionProductSchema.pick({
  id: true,
  partnerId: true,
  type: true,
  name: true,
  displayName: true,
  description: true,
  rootProductId: true,
  version: true,
  coveredEventType: true,
  distributionMode: true,
  claimSettings: true,
  retractionDelay: true,
  perCurrencySettings: true,
  attachments: true,
  pricingProvider: true,
  requiresAttachments: true,
  subscriptionSettings: true,
  communicationSettings: true,
  brokerageFeesRatio: true,
  provider: true,
  createdAt: true,
  updatedAt: true,
  productProviderExternalData: true,
  businessContributor: true,
  businessContributorCommissionRatio: true
});

// src/partnership-configuration/products/get-product/get-change-for-any-reason-product-res-body.schema.ts
var getChangeForAnyReasonProductResBodySchema = changeForAnyReasonProductSchema.pick({
  id: true,
  partnerId: true,
  type: true,
  name: true,
  displayName: true,
  description: true,
  rootProductId: true,
  version: true,
  requiresAttachments: true,
  attachments: true,
  pricingProvider: true,
  distributionMode: true,
  coveredEventType: true,
  subscriptionSettings: true,
  communicationSettings: true,
  retractionDelay: true,
  perCurrencySettings: true,
  claimSettings: true,
  brokerageFeesRatio: true,
  provider: true,
  createdAt: true,
  updatedAt: true,
  productProviderExternalData: true,
  businessContributor: true,
  businessContributorCommissionRatio: true
});

// src/partnership-configuration/products/get-product/get-comprehensive-product-res-body.schema.ts
var getComprehensiveProductResBodySchema = comprehensiveProductSchema.pick({
  id: true,
  partnerId: true,
  type: true,
  name: true,
  displayName: true,
  description: true,
  rootProductId: true,
  version: true,
  coveredEventType: true,
  distributionMode: true,
  subscriptionSettings: true,
  communicationSettings: true,
  perCurrencySettings: true,
  attachments: true,
  requiresAttachments: true,
  pricingProvider: true,
  retractionDelay: true,
  brokerageFeesRatio: true,
  provider: true,
  createdAt: true,
  updatedAt: true,
  productProviderExternalData: true,
  businessContributor: true,
  businessContributorCommissionRatio: true
});

// src/partnership-configuration/products/get-product/get-fdc-product-res-body.schema.ts
var getFdcProductResBodySchema = flightDisruptionProductSchema.pick({
  id: true,
  partnerId: true,
  type: true,
  name: true,
  displayName: true,
  description: true,
  rootProductId: true,
  version: true,
  coveredEventType: true,
  distributionMode: true,
  claimSettings: true,
  retractionDelay: true,
  perCurrencySettings: true,
  attachments: true,
  pricingProvider: true,
  requiresAttachments: true,
  subscriptionSettings: true,
  communicationSettings: true,
  brokerageFeesRatio: true,
  provider: true,
  createdAt: true,
  updatedAt: true,
  productProviderExternalData: true,
  businessContributor: true,
  businessContributorCommissionRatio: true
});

// src/partnership-configuration/products/get-product/get-product-req-params.schema.ts
import { z as z96 } from "zod";
var getProductReqParamsSchema = z96.strictObject({
  productId: z96.string().uuid(),
  partnerId: z96.string().uuid()
});

// src/partnership-configuration/products/get-product/get-product-res-body.schema.ts
import { z as z97 } from "zod";

// src/partnership-configuration/products/get-product/get-cancellation-without-proof-product-res-body.schema.ts
var getCancellationWithoutProofProductResBodySchema = cancellationWithoutProofProductSchema.pick({
  id: true,
  partnerId: true,
  type: true,
  name: true,
  displayName: true,
  description: true,
  rootProductId: true,
  version: true,
  coveredEventType: true,
  distributionMode: true,
  claimSettings: true,
  retractionDelay: true,
  perCurrencySettings: true,
  attachments: true,
  pricingProvider: true,
  requiresAttachments: true,
  subscriptionSettings: true,
  communicationSettings: true,
  brokerageFeesRatio: true,
  provider: true,
  createdAt: true,
  updatedAt: true,
  productProviderExternalData: true,
  businessContributor: true,
  businessContributorCommissionRatio: true
});

// src/partnership-configuration/products/get-product/get-withdrawal-product-res-body.schema.ts
var getWithdrawalProductResBodySchema = withdrawalProductSchema.pick({
  id: true,
  partnerId: true,
  type: true,
  name: true,
  displayName: true,
  description: true,
  rootProductId: true,
  version: true,
  coveredEventType: true,
  distributionMode: true,
  claimSettings: true,
  retractionDelay: true,
  perCurrencySettings: true,
  attachments: true,
  pricingProvider: true,
  requiresAttachments: true,
  subscriptionSettings: true,
  communicationSettings: true,
  brokerageFeesRatio: true,
  provider: true,
  createdAt: true,
  updatedAt: true,
  productProviderExternalData: true,
  businessContributor: true,
  businessContributorCommissionRatio: true
});

// src/partnership-configuration/products/get-product/get-product-res-body.schema.ts
var getProductResBodySchema = z97.discriminatedUnion("type", [
  getComprehensiveProductResBodySchema,
  getChangeForAnyReasonProductResBodySchema,
  getWithdrawalProductResBodySchema,
  getFdcProductResBodySchema,
  getCancellationWithoutProofProductResBodySchema,
  getBaggageDisruptionProductResBodySchema
]);

// src/partnership-configuration/products/get-products/get-products-req-params.schema.ts
import { z as z98 } from "zod";
var getProductsReqParamsSchema = z98.strictObject({
  partnerId: z98.string().uuid()
});

// src/partnership-configuration/products/get-products/get-products-res-body.schema.ts
import { z as z100 } from "zod";

// src/partnership-configuration/products/product-pricing-overview.schema.ts
import { z as z99 } from "zod";
import { ProductPricingProviderEnum as ProductPricingProviderEnum2 } from "@repo/lib-koala-types";
var productPricingOverviewSchema = z99.strictObject({
  currency: currenciesSchema,
  pricingBase: z99.enum([
    "TRIP_PRICE",
    "NON_REFUNDABLE_TRIP_PRICE"
  ]).default("TRIP_PRICE"),
  premium: z99.number().nullable(),
  retailPriceCoefficient: z99.number().nullable(),
  retailPriceFixed: z99.number().nullable(),
  retailPriceExternal: z99.enum([
    ProductPricingProviderEnum2.WAKAM,
    ProductPricingProviderEnum2.SWISS_RE,
    ProductPricingProviderEnum2.CARTRAWLER
  ]).nullable(),
  retailPriceProvidedByPartner: z99.boolean().default(false),
  partnerCommission: z99.number().nullable(),
  partnerCommissionCoefficient: z99.number().nullable(),
  partnerCommissionFixed: z99.number().nullable(),
  insurerCommission: z99.number().nullable(),
  koalaCommission: z99.number().nullable(),
  koalaCommissionCoefficient: z99.number().nullable(),
  koalaCommissionFixed: z99.number().nullable(),
  taxes: z99.number().nullable(),
  insuranceTaxes: z99.number().nullable(),
  serviceTaxes: z99.number().nullable(),
  businessContributorCommission: z99.number().nullable()
});

// src/partnership-configuration/products/get-products/get-products-res-body.schema.ts
var additionalFields = {
  pricingOverview: productPricingOverviewSchema.optional(),
  currencies: currenciesSchema.array().optional()
};
var getProductsResBodySchema = z100.array(z100.discriminatedUnion("type", [
  getComprehensiveProductResBodySchema.extend(additionalFields),
  getChangeForAnyReasonProductResBodySchema.extend(additionalFields),
  getWithdrawalProductResBodySchema.extend(additionalFields),
  getFdcProductResBodySchema.extend(additionalFields),
  getCancellationWithoutProofProductResBodySchema.extend(additionalFields),
  getBaggageDisruptionProductResBodySchema.extend(additionalFields)
]));

// src/partnership-configuration/products/get-products-overview/get-products-overview-req-params.schema.ts
import { z as z101 } from "zod";
var getProductsOverviewReqParamsSchema = z101.strictObject({
  partnerId: z101.string().uuid()
});

// src/partnership-configuration/products/get-products-overview/get-products-overview-res-body.schema.ts
var getProductsOverviewResBodySchema = baseProductSchema.pick({
  id: true,
  name: true,
  type: true,
  displayName: true,
  description: true,
  createdAt: true,
  updatedAt: true,
  version: true,
  partnerId: true,
  rootProductId: true,
  provider: true,
  pricingProvider: true
}).array();

// src/partnership-configuration/products/product.schema.ts
import { z as z102 } from "zod";
var productSchema = z102.discriminatedUnion("type", [
  comprehensiveProductSchema,
  changeForAnyReasonProductSchema,
  withdrawalProductSchema,
  flightDisruptionProductSchema,
  cancellationWithoutProofProductSchema,
  baggageDisruptionProductSchema
]);

// src/partnership-configuration/products/update-product/base-product-update-data.schema.ts
import { z as z103 } from "zod";
import { BusinessContributorEnum as BusinessContributorEnum2 } from "@repo/lib-koala-types";

// src/partnership-configuration/products/communication-settings/communication-settings-update-data.schema.ts
var communicationSettingsUpdateDataSchema = communicationSettingsSchema;

// src/partnership-configuration/products/update-product/base-product-update-data.schema.ts
var baseProductUpdateDataSchema = baseProductSchema.pick({
  requiresAttachments: true,
  displayName: true,
  description: true,
  distributionMode: true,
  pricingProvider: true,
  provider: true,
  retractionDelay: true,
  brokerageFeesRatio: true,
  rootProductId: true
}).extend({
  communicationSettings: communicationSettingsUpdateDataSchema,
  subscriptionSettings: subscriptionSettingsUpdateDataSchema,
  businessContributor: z103.nativeEnum(BusinessContributorEnum2).optional().nullable(),
  businessContributorCommissionRatio: z103.number().min(0).optional().nullable(),
  productProviderExternalData: productProviderExternalDataSchema.optional().nullable()
}).partial({
  requiresAttachments: true,
  distributionMode: true,
  pricingProvider: true,
  displayName: true,
  description: true,
  provider: true,
  retractionDelay: true,
  brokerageFeesRatio: true,
  communicationSettings: true,
  subscriptionSettings: true,
  rootProductId: true
});

// src/partnership-configuration/products/update-product/delete-product-req-params.schema.ts
import { z as z104 } from "zod";
var deleteProductReqParamsSchema = z104.strictObject({
  partnerId: z104.string().uuid(),
  productId: z104.string().uuid()
});

// src/partnership-configuration/products/update-product/delete-product-req-query.schema.ts
import { extendApi as extendApi22 } from "@anatine/zod-openapi";
var deleteProductReqQuerySchema = extendApi22(sideEffectsSchema, {
  hideDefinitions: [
    "sideEffects"
  ]
});

// src/partnership-configuration/products/update-product/update-baggage-disruption-product-req-body.schema.ts
import { z as z105 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum13, ProductTypeEnum as ProductTypeEnum15 } from "@repo/lib-koala-types";

// src/partnership-configuration/products/claim-settings/claim-settings-update-data.schema.ts
var claimSettingsUpdateDataSchema = claimSettingsSchema;

// src/partnership-configuration/products/update-product/update-baggage-disruption-product-req-body.schema.ts
var updateBaggageDisruptionProductReqBodySchema = baseProductUpdateDataSchema.extend({
  type: z105.literal(ProductTypeEnum15.BAGGAGE_DISRUPTION),
  coveredEventType: z105.literal(CoveredEventTypeEnum13.TRIP).optional(),
  claimSettings: claimSettingsUpdateDataSchema.optional(),
  perCurrencySettings: perCurrencySettingsUpdateDataSchema.optional(),
  attachments: attachmentsUpdateDataSchema.optional()
});

// src/partnership-configuration/products/update-product/update-cancellation-without-proof-product-req-body.schema.ts
import { z as z106 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum14, ProductTypeEnum as ProductTypeEnum16 } from "@repo/lib-koala-types";
var updateCancellationWithoutProofProductReqBodySchema = baseProductUpdateDataSchema.extend({
  type: z106.literal(ProductTypeEnum16.CANCELLATION_WITHOUT_PROOF),
  coveredEventType: z106.literal(CoveredEventTypeEnum14.TRIP).optional(),
  claimSettings: claimSettingsUpdateDataSchema.optional(),
  perCurrencySettings: perCurrencySettingsUpdateDataSchema.optional(),
  attachments: attachmentsUpdateDataSchema.optional()
});

// src/partnership-configuration/products/update-product/update-change-for-any-reason-product-req-body.schema.ts
import { z as z107 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum15, ProductTypeEnum as ProductTypeEnum17 } from "@repo/lib-koala-types";
var updateChangeForAnyReasonProductReqBodySchema = baseProductUpdateDataSchema.extend({
  type: z107.literal(ProductTypeEnum17.CHANGE_FOR_ANY_REASON),
  coveredEventType: z107.literal(CoveredEventTypeEnum15.TRIP).optional(),
  claimSettings: claimSettingsUpdateDataSchema.optional(),
  perCurrencySettings: perCurrencySettingsUpdateDataSchema.optional(),
  attachments: attachmentsUpdateDataSchema.optional()
});

// src/partnership-configuration/products/update-product/update-comprehensive-product-req-body.schema.ts
import { z as z108 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum16, ProductTypeEnum as ProductTypeEnum18 } from "@repo/lib-koala-types";
var updateComprehensiveProductReqBodySchema = baseProductUpdateDataSchema.extend({
  type: z108.literal(ProductTypeEnum18.COMPREHENSIVE),
  coveredEventType: z108.literal(CoveredEventTypeEnum16.TRIP).optional(),
  perCurrencySettings: comprehensivePerCurrencySettingsUpdateDataSchema.optional(),
  attachments: comprehensiveProductAttachmentsUpdateDataSchema.optional()
});

// src/partnership-configuration/products/update-product/update-fdc-product-req-body.schema.ts
import { z as z109 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum17, ProductTypeEnum as ProductTypeEnum19 } from "@repo/lib-koala-types";
var updateFdcProductReqBodySchema = baseProductUpdateDataSchema.extend({
  type: z109.literal(ProductTypeEnum19.FLIGHT_DISRUPTION),
  coveredEventType: z109.literal(CoveredEventTypeEnum17.FLIGHT).optional(),
  claimSettings: claimSettingsUpdateDataSchema.optional(),
  perCurrencySettings: perCurrencySettingsUpdateDataSchema.optional(),
  attachments: attachmentsUpdateDataSchema.optional()
});

// src/partnership-configuration/products/update-product/update-product-req-body.schema.ts
import { z as z111 } from "zod";

// src/partnership-configuration/products/update-product/update-withdrawal-product-req-body.schema.ts
import { z as z110 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum18, ProductTypeEnum as ProductTypeEnum20 } from "@repo/lib-koala-types";
var updateWithdrawalProductReqBodySchema = baseProductUpdateDataSchema.extend({
  type: z110.literal(ProductTypeEnum20.WITHDRAWAL),
  coveredEventType: z110.enum([
    CoveredEventTypeEnum18.TRIP,
    CoveredEventTypeEnum18.FLIGHT
  ]).optional(),
  claimSettings: claimSettingsUpdateDataSchema.optional(),
  perCurrencySettings: withdrawalPerCurrencySettingsUpdateDataSchema.optional(),
  attachments: attachmentsUpdateDataSchema.optional()
});

// src/partnership-configuration/products/update-product/update-product-req-body.schema.ts
var updateProductReqBodySchema = z111.union([
  updateChangeForAnyReasonProductReqBodySchema,
  updateComprehensiveProductReqBodySchema,
  updateFdcProductReqBodySchema,
  updateWithdrawalProductReqBodySchema,
  updateBaggageDisruptionProductReqBodySchema,
  updateCancellationWithoutProofProductReqBodySchema
]);

// src/partnership-configuration/products/update-product/update-product-req-params.schema.ts
import { z as z112 } from "zod";
var updateProductReqParamsSchema = z112.strictObject({
  partnerId: z112.string().uuid(),
  productId: z112.string().uuid()
});

// src/partnership-configuration/products/update-product/update-product-res-body.schema.ts
var updateProductResBodySchema = productSchema;

// src/partnership-configuration/bundles/get-bundle-overview/get-bundle-overview-res-body.schema.ts
var getBundleOverviewResBodySchema = z113.strictObject({
  partner: z113.strictObject({
    id: z113.string().uuid(),
    name: z113.string()
  }),
  bundle: bundleSchema.pick({
    id: true,
    name: true,
    version: true,
    rootBundleId: true,
    createdAt: true,
    updatedAt: true
  }).extend({
    products: z113.array(baseProductSchema.pick({
      id: true,
      name: true,
      displayName: true,
      description: true,
      type: true,
      version: true,
      rootProductId: true,
      pricingProvider: true,
      productProviderExternalData: true,
      createdAt: true
    }).extend({
      productBundleId: z113.string().uuid(),
      rootBundleId: z113.string().uuid(),
      languages: z113.string().array(),
      currencies: z113.string().array(),
      pricingOverview: productPricingOverviewSchema,
      preConditions: z113.string()
    }))
  })
});

// src/partnership-configuration/bundles/get-bundles/get-bundles-req-params.schema.ts
import { z as z114 } from "zod";
var getBundlesReqParamsSchema = z114.strictObject({
  partnerId: z114.string().uuid()
});

// src/partnership-configuration/bundles/get-bundles/get-bundles-res-body.schema.ts
import { z as z115 } from "zod";
var getBundlesResBodySchema = z115.array(bundleSchema.pick({
  id: true,
  name: true,
  version: true,
  rootBundleId: true,
  createdAt: true,
  updatedAt: true,
  productBundles: true
}));

// src/partnership-configuration/bundles/get-bundles-overview/get-bundles-overview-req-params.schema.ts
import { z as z116 } from "zod";
var getBundlesOverviewReqParamsSchema = z116.strictObject({
  partnerId: z116.string().uuid()
});

// src/partnership-configuration/bundles/get-bundles-overview/get-bundles-overview-res-body.schema.ts
import { z as z117 } from "zod";
var getBundlesOverviewResBodySchema = bundleSchema.pick({
  id: true,
  name: true,
  version: true,
  rootBundleId: true,
  createdAt: true,
  updatedAt: true
}).extend({
  products: z117.strictObject({
    id: z117.string().uuid(),
    productBundleId: z117.string().uuid()
  }).array()
}).array();

// src/partnership-configuration/bundles/update-bundle/update-bundle-req-body.schema.ts
import { z as z118 } from "zod";
var updateBundleReqBodySchema = z118.strictObject({
  productBundles: z118.array(z118.strictObject({
    productId: z118.string().uuid(),
    productBundleId: z118.string().uuid()
  })).min(1, "Bundle must include at least one product bundle.")
});

// src/partnership-configuration/bundles/update-bundle/update-bundle-req-params.schema.ts
import { z as z119 } from "zod";
var updateBundleReqParamsSchema = z119.strictObject({
  partnerId: z119.string().uuid(),
  bundleId: z119.string().uuid()
});

// src/partnership-configuration/bundles/update-bundle/update-bundle-res-body.schema.ts
var updateBundleResBodySchema = bundleSchema.pick({
  id: true,
  name: true,
  version: true,
  rootBundleId: true,
  createdAt: true,
  updatedAt: true,
  productBundles: true
});

// src/partnership-configuration/campaigns/campaign.schema.ts
import { z as z121 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema7 } from "@repo/schema-common";

// src/partnership-configuration/campaigns/campaign-bundle.schema.ts
import { z as z120 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema6 } from "@repo/schema-common";
var productBundleSchema = z120.discriminatedUnion("type", [
  comprehensiveProductSchema.extend({
    productBundleId: z120.string().uuid()
  }),
  changeForAnyReasonProductSchema.extend({
    productBundleId: z120.string().uuid()
  }),
  withdrawalProductSchema.extend({
    productBundleId: z120.string().uuid()
  }),
  flightDisruptionProductSchema.extend({
    productBundleId: z120.string().uuid()
  }),
  cancellationWithoutProofProductSchema.extend({
    productBundleId: z120.string().uuid()
  }),
  baggageDisruptionProductSchema.extend({
    productBundleId: z120.string().uuid()
  })
]);
var campaignBundleSchema = z120.strictObject({
  id: z120.string().uuid(),
  name: z120.string().min(1),
  version: z120.number(),
  rootBundleId: z120.string().uuid(),
  weight: z120.number().positive().max(1),
  campaignBundleId: z120.string().uuid(),
  campaignBundleName: z120.string().min(1),
  productBundles: z120.array(productBundleSchema),
  createdAt: dateTimeWithUtcOffsetSchema6,
  updatedAt: dateTimeWithUtcOffsetSchema6
});

// src/partnership-configuration/campaigns/campaign.schema.ts
var campaignSchema = z121.strictObject({
  id: z121.string().uuid(),
  name: z121.string().min(1),
  partnerId: z121.string().uuid(),
  rootBundleId: z121.string().uuid(),
  isActive: z121.boolean(),
  bundles: z121.array(campaignBundleSchema).min(1),
  startDate: dateTimeWithUtcOffsetSchema7.optional(),
  endDate: dateTimeWithUtcOffsetSchema7.optional(),
  createdAt: dateTimeWithUtcOffsetSchema7,
  updatedAt: dateTimeWithUtcOffsetSchema7
});

// src/partnership-configuration/campaigns/campaign-bundle-creation-data.schema.ts
var campaignBundleCreationDataSchema = campaignBundleSchema.pick({
  id: true,
  weight: true,
  campaignBundleName: true,
  campaignBundleId: true
});

// src/partnership-configuration/campaigns/create-campaign/create-campaign-req-body.schema.ts
import { z as z124 } from "zod";

// src/partnership-configuration/campaigns/create-campaign/create-default-campaign-req-body.schema.ts
import { z as z122 } from "zod";
var createDefaultCampaignReqBodySchema = z122.strictObject({
  id: z122.string().uuid(),
  bundles: z122.array(campaignBundleCreationDataSchema).min(1, "Campaign must include at least one bundle.")
});

// src/partnership-configuration/campaigns/create-campaign/create-non-default-campaign-req-body.schema.ts
import { z as z123 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema8 } from "@repo/schema-common";
var createNonDefaultCampaignReqBodySchema = z123.strictObject({
  id: z123.string().uuid(),
  name: z123.string().min(1),
  startDate: dateTimeWithUtcOffsetSchema8,
  endDate: dateTimeWithUtcOffsetSchema8,
  bundles: z123.array(campaignBundleCreationDataSchema).min(1, "Campaign must include at least one bundle.")
});

// src/partnership-configuration/campaigns/create-campaign/create-campaign-req-body.schema.ts
var CampaignType = /* @__PURE__ */ function(CampaignType2) {
  CampaignType2["Default"] = "DEFAULT";
  CampaignType2["NonDefault"] = "NON_DEFAULT";
  return CampaignType2;
}({});
var createCampaignReqBodySchema = z124.discriminatedUnion("type", [
  createDefaultCampaignReqBodySchema.extend({
    type: z124.literal("DEFAULT")
  }),
  createNonDefaultCampaignReqBodySchema.extend({
    type: z124.literal("NON_DEFAULT")
  })
]);

// src/partnership-configuration/campaigns/create-campaign/create-campaign-req-params.schema.ts
import { z as z125 } from "zod";
var createCampaignReqParamsSchema = z125.strictObject({
  partnerId: z125.string().uuid()
});

// src/partnership-configuration/campaigns/create-campaign/create-campaign-res-body.schema.ts
var createCampaignResBodySchema = campaignSchema.pick({
  id: true,
  name: true,
  partnerId: true,
  rootBundleId: true,
  isActive: true,
  startDate: true,
  endDate: true,
  bundles: true,
  createdAt: true,
  updatedAt: true
});

// src/partnership-configuration/campaigns/get-campaign/get-campaign-req-params.schema.ts
import { z as z126 } from "zod";
var getCampaignReqParamsSchema = z126.strictObject({
  partnerId: z126.string().uuid(),
  campaignId: z126.string().uuid()
});

// src/partnership-configuration/campaigns/get-campaign/get-campaign-res-body.schema.ts
var getCampaignResBodySchema = campaignSchema.pick({
  id: true,
  name: true,
  startDate: true,
  endDate: true,
  partnerId: true,
  rootBundleId: true,
  isActive: true,
  createdAt: true,
  updatedAt: true
}).extend({
  bundles: campaignBundleSchema.omit({
    productBundles: true
  }).array()
});

// src/partnership-configuration/campaigns/get-campaign-publication-status/get-campaign-publication-status-req-params.schema.ts
import { z as z127 } from "zod";
var getCampaignPublicationStatusReqParamsSchema = z127.strictObject({
  partnerId: z127.string().uuid(),
  campaignId: z127.string().uuid()
});

// src/partnership-configuration/campaigns/get-campaign-publication-status/get-campaign-publication-status-res-body.schema.ts
import { z as z128 } from "zod";
import { PublicationStatusEnum } from "@repo/lib-koala-types";
var getCampaignPublicationStatusResBodySchema = campaignSchema.pick({
  id: true,
  name: true,
  startDate: true,
  endDate: true,
  partnerId: true,
  rootBundleId: true,
  isActive: true,
  bundles: true,
  createdAt: true,
  updatedAt: true
}).extend({
  publicationStatus: z128.nativeEnum(PublicationStatusEnum),
  isActiveInTargetStage: z128.boolean()
});

// src/partnership-configuration/campaigns/get-campaigns/get-campaigns-req-params.schema.ts
import { z as z129 } from "zod";
var getCampaignsReqParamsSchema = z129.strictObject({
  partnerId: z129.string().uuid()
});

// src/partnership-configuration/campaigns/get-campaigns/get-campaigns-res-body.schema.ts
var getCampaignsResBodySchema = getCampaignResBodySchema.array();

// src/partnership-configuration/campaigns/get-campaigns-overview/get-campaigns-overview-req-params.schema.ts
import { z as z130 } from "zod";
var getCampaignsOverviewReqParamsSchema = z130.strictObject({
  partnerId: z130.string().uuid()
});

// src/partnership-configuration/campaigns/get-campaigns-overview/get-campaigns-overview-res-body.schema.ts
import { z as z131 } from "zod";
import { PublicationStatusEnum as PublicationStatusEnum2 } from "@repo/lib-koala-types";
var getCampaignsOverviewResBodySchema = campaignSchema.pick({
  id: true,
  partnerId: true,
  name: true,
  startDate: true,
  endDate: true,
  createdAt: true,
  updatedAt: true
}).extend({
  publicationStatus: z131.nativeEnum(PublicationStatusEnum2),
  bundles: z131.strictObject({
    id: z131.string().uuid(),
    campaignBundleId: z131.string().uuid()
  }).array()
}).array();

// src/partnership-configuration/campaigns/get-campaigns-publication-status/get-campaigns-publication-status-req-params.schema.ts
import { z as z132 } from "zod";
var getCampaignsPublicationStatusReqParamsSchema = z132.strictObject({
  partnerId: z132.string().uuid()
});

// src/partnership-configuration/campaigns/get-campaigns-publication-status/get-campaigns-publication-status-res-body.schema.ts
import { z as z133 } from "zod";
import { PublicationStatusEnum as PublicationStatusEnum3 } from "@repo/lib-koala-types";
var getCampaignsPublicationStatusResBodySchema = z133.array(campaignSchema.pick({
  id: true,
  name: true,
  startDate: true,
  endDate: true,
  partnerId: true,
  rootBundleId: true,
  isActive: true,
  bundles: true,
  createdAt: true,
  updatedAt: true
}).extend({
  publicationStatus: z133.nativeEnum(PublicationStatusEnum3),
  isActiveInTargetStage: z133.boolean()
}));

// src/partnership-configuration/campaigns/publish-campaign/publish-campaign-req-params.schema.ts
import { z as z134 } from "zod";
var publishCampaignReqParamsSchema = z134.strictObject({
  partnerId: z134.string().uuid(),
  campaignId: z134.string().uuid()
});

// src/partnership-configuration/campaigns/publish-campaign/publish-campaign-res-body.schema.ts
var publishCampaignResBodySchema = campaignSchema.pick({
  id: true,
  name: true,
  partnerId: true,
  rootBundleId: true,
  isActive: true,
  startDate: true,
  endDate: true,
  bundles: true,
  createdAt: true,
  updatedAt: true
});

// src/partnership-configuration/campaigns/update-campaign/update-campaign-req-body.schema.ts
import { z as z135 } from "zod";
var updateCampaignReqBodySchema = campaignSchema.pick({
  startDate: true,
  endDate: true
}).extend({
  bundles: z135.array(z135.strictObject({
    id: z135.string().uuid(),
    weight: z135.number().positive().max(1),
    campaignBundleId: z135.string().uuid(),
    campaignBundleName: z135.string()
  })).min(1)
}).partial();

// src/partnership-configuration/campaigns/update-campaign/update-campaign-req-params.schema.ts
import { z as z136 } from "zod";
var updateCampaignReqParamsSchema = z136.strictObject({
  partnerId: z136.string().uuid(),
  campaignId: z136.string().uuid()
});

// src/partnership-configuration/campaigns/update-campaign/update-campaign-res-body.schema.ts
var updateCampaignResBodySchema = campaignSchema.pick({
  id: true,
  name: true,
  partnerId: true,
  rootBundleId: true,
  isActive: true,
  startDate: true,
  endDate: true,
  bundles: true,
  createdAt: true,
  updatedAt: true
});

// src/partnership-configuration/errors/base-configuration-api-error.ts
var BaseConfigurationApiError = class extends Error {
  static {
    __name(this, "BaseConfigurationApiError");
  }
  constructor(message) {
    super(message);
  }
};

// src/partnership-configuration/errors/partnership-configuration-error.types.ts
var PartnershipConfigurationErrorTypes = /* @__PURE__ */ function(PartnershipConfigurationErrorTypes2) {
  PartnershipConfigurationErrorTypes2["PARTNER_NOT_FOUND"] = "PARTNER_NOT_FOUND";
  PartnershipConfigurationErrorTypes2["PARTNER_ALREADY_EXISTS"] = "PARTNER_ALREADY_EXISTS";
  PartnershipConfigurationErrorTypes2["PARTNER_COUNTRY_MISSING_TAXES"] = "PARTNER_COUNTRY_MISSING_TAXES";
  PartnershipConfigurationErrorTypes2["PARTNER_COUNTRY_NOT_FOUND"] = "PARTNER_COUNTRY_NOT_FOUND";
  PartnershipConfigurationErrorTypes2["PARTNER_WEBHOOK_SECRET_NOT_FOUND"] = "PARTNER_WEBHOOK_SECRET_NOT_FOUND";
  PartnershipConfigurationErrorTypes2["PARTNER_DUPLICATE_CLASSIFICATION_PRODUCT_TYPE"] = "PARTNER_DUPLICATE_CLASSIFICATION_PRODUCT_TYPE";
  PartnershipConfigurationErrorTypes2["PRODUCT_NOT_FOUND"] = "PRODUCT_NOT_FOUND";
  PartnershipConfigurationErrorTypes2["PRODUCT_INVALID_CONFIGURATION"] = "PRODUCT_INVALID_CONFIGURATION";
  PartnershipConfigurationErrorTypes2["PRODUCT_ALREADY_EXISTS"] = "PRODUCT_ALREADY_EXISTS";
  PartnershipConfigurationErrorTypes2["PRODUCT_EXISTS_IN_TARGET_STAGE"] = "PRODUCT_EXISTS_IN_TARGET_STAGE";
  PartnershipConfigurationErrorTypes2["PRODUCT_PROVIDER_EXTERNAL_DATA_NOT_VALIDATED"] = "PRODUCT_PROVIDER_EXTERNAL_DATA_NOT_VALIDATED";
  PartnershipConfigurationErrorTypes2["UNEXPECTED_COMPENSATION_SETTINGS_COUNT"] = "UNEXPECTED_COMPENSATION_SETTINGS_COUNT";
  PartnershipConfigurationErrorTypes2["MISSING_DELAY_COMPENSATION_CONDITION"] = "MISSING_DELAY_COMPENSATION_CONDITION";
  PartnershipConfigurationErrorTypes2["MISSING_LOSS_COMPENSATION_CONDITION"] = "MISSING_LOSS_COMPENSATION_CONDITION";
  PartnershipConfigurationErrorTypes2["BUNDLE_NOT_FOUND"] = "BUNDLE_NOT_FOUND";
  PartnershipConfigurationErrorTypes2["BUNDLE_ALREADY_EXISTS"] = "BUNDLE_ALREADY_EXISTS";
  PartnershipConfigurationErrorTypes2["BUNDLE_IS_NOT_A_ROOT_BUNDLE"] = "BUNDLE_IS_NOT_A_ROOT_BUNDLE";
  PartnershipConfigurationErrorTypes2["BUNDLE_EXISTS_IN_TARGET_STAGE"] = "BUNDLE_EXISTS_IN_TARGET_STAGE";
  PartnershipConfigurationErrorTypes2["BUNDLE_PRODUCTS_CURRENCIES_DISCREPANCIES"] = "BUNDLE_PRODUCTS_CURRENCIES_DISCREPANCIES";
  PartnershipConfigurationErrorTypes2["CAMPAIGN_NOT_FOUND"] = "CAMPAIGN_NOT_FOUND";
  PartnershipConfigurationErrorTypes2["CAMPAIGN_ALREADY_EXISTS"] = "CAMPAIGN_ALREADY_EXISTS";
  PartnershipConfigurationErrorTypes2["CAMPAIGN_EXISTS_IN_TARGET_STAGE"] = "CAMPAIGN_EXISTS_IN_TARGET_STAGE";
  PartnershipConfigurationErrorTypes2["DEFAULT_CAMPAIGN_ALREADY_EXISTS"] = "DEFAULT_CAMPAIGN_ALREADY_EXISTS";
  PartnershipConfigurationErrorTypes2["BUNDLES_HAVE_DIFFERENT_ROOT_BUNDLES"] = "BUNDLES_HAVE_DIFFERENT_ROOT_BUNDLES";
  PartnershipConfigurationErrorTypes2["CAMPAIGN_AND_BUNDLE_MISMATCH_ON_ROOT_BUNDLE"] = "CAMPAIGN_AND_BUNDLE_MISMATCH_ON_ROOT_BUNDLE";
  PartnershipConfigurationErrorTypes2["CAMPAIGN_INVALID_BUNDLE_WEIGHTS"] = "CAMPAIGN_INVALID_BUNDLE_WEIGHTS";
  PartnershipConfigurationErrorTypes2["CAMPAIGN_OVERLAPS_OTHER_CAMPAIGNS"] = "CAMPAIGN_OVERLAPS_OTHER_CAMPAIGNS";
  PartnershipConfigurationErrorTypes2["ROOT_BUNDLE_NOT_FOUND"] = "ROOT_BUNDLE_NOT_FOUND";
  PartnershipConfigurationErrorTypes2["WISE_PAYOUT_METHOD_NOT_FOUND"] = "WISE_PAYOUT_METHOD_NOT_FOUND";
  PartnershipConfigurationErrorTypes2["WISE_PAYOUT_METHOD_ALREADY_EXISTS"] = "WISE_PAYOUT_METHOD_ALREADY_EXISTS";
  PartnershipConfigurationErrorTypes2["WISE_FORBIDDEN_COUNTRY_NOT_FOUND"] = "WISE_FORBIDDEN_COUNTRY_NOT_FOUND";
  PartnershipConfigurationErrorTypes2["WISE_FORBIDDEN_COUNTRY_ALREADY_EXISTS"] = "WISE_FORBIDDEN_COUNTRY_ALREADY_EXISTS";
  PartnershipConfigurationErrorTypes2["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
  PartnershipConfigurationErrorTypes2["PARTNER_EXISTS_IN_TARGET_STAGE"] = "PARTNER_EXISTS_IN_TARGET_STAGE";
  PartnershipConfigurationErrorTypes2["OPERATION_NOT_ALLOWED_ON_TARGET_STAGE"] = "OPERATION_NOT_ALLOWED_ON_TARGET_STAGE";
  return PartnershipConfigurationErrorTypes2;
}({});

// src/partnership-configuration/errors/bundle-already-exists.error.ts
var BundleAlreadyExistsError = class extends BaseConfigurationApiError {
  static {
    __name(this, "BundleAlreadyExistsError");
  }
  type = PartnershipConfigurationErrorTypes.BUNDLE_ALREADY_EXISTS;
  constructor(message = "The bundle already exists.") {
    super(message);
  }
};

// src/partnership-configuration/errors/bundle-exists-in-target-stage.error.ts
var BundleExistsInTargetStageError = class extends BaseConfigurationApiError {
  static {
    __name(this, "BundleExistsInTargetStageError");
  }
  type = PartnershipConfigurationErrorTypes.BUNDLE_EXISTS_IN_TARGET_STAGE;
  constructor(message = "The bundle exists in the target stage.") {
    super(message);
  }
};

// src/partnership-configuration/errors/bundle-is-not-a-root-bundle.error.ts
var BundleIsNotARootBundleError = class extends BaseConfigurationApiError {
  static {
    __name(this, "BundleIsNotARootBundleError");
  }
  type = PartnershipConfigurationErrorTypes.BUNDLE_IS_NOT_A_ROOT_BUNDLE;
  constructor(message = "The bundle is not a root bundle.") {
    super(message);
  }
};

// src/partnership-configuration/errors/bundle-not-found.error.ts
var BundleNotFoundError = class extends BaseConfigurationApiError {
  static {
    __name(this, "BundleNotFoundError");
  }
  type = PartnershipConfigurationErrorTypes.BUNDLE_NOT_FOUND;
  constructor(message = "The bundle was not found.") {
    super(message);
  }
};

// src/partnership-configuration/errors/bundle-products-currencies-discrepancies.error.ts
var BundleProductsCurrenciesDiscrepanciesError = class extends BaseConfigurationApiError {
  static {
    __name(this, "BundleProductsCurrenciesDiscrepanciesError");
  }
  type = PartnershipConfigurationErrorTypes.BUNDLE_PRODUCTS_CURRENCIES_DISCREPANCIES;
  constructor(message = "The products of the bundle do not support the exact same currencies which can lead to invalid quotes.") {
    super(message);
  }
};

// src/partnership-configuration/errors/bundles-have-different-root-bundles.error.ts
var BundlesHaveDifferentRootBundlesError = class extends BaseConfigurationApiError {
  static {
    __name(this, "BundlesHaveDifferentRootBundlesError");
  }
  type = PartnershipConfigurationErrorTypes.BUNDLES_HAVE_DIFFERENT_ROOT_BUNDLES;
  constructor(message = "Bundles have different root bundles.") {
    super(message);
  }
};

// src/partnership-configuration/errors/campaign-already-exists.error.ts
var CampaignAlreadyExistsError = class extends BaseConfigurationApiError {
  static {
    __name(this, "CampaignAlreadyExistsError");
  }
  type = PartnershipConfigurationErrorTypes.CAMPAIGN_ALREADY_EXISTS;
  constructor(message = "The campaign already exists.") {
    super(message);
  }
};

// src/partnership-configuration/errors/campaign-and-bundle-mismatch-on-root-bundle.error.ts
var CampaignAndBundleMismatchOnRootBundleError = class extends BaseConfigurationApiError {
  static {
    __name(this, "CampaignAndBundleMismatchOnRootBundleError");
  }
  type = PartnershipConfigurationErrorTypes.CAMPAIGN_AND_BUNDLE_MISMATCH_ON_ROOT_BUNDLE;
  constructor(message = "Campaign and bundles of the campaign do not have the same root bundle.") {
    super(message);
  }
};

// src/partnership-configuration/errors/campaign-invalid-bundle-weights.error.ts
var CampaignInvalidBundleWeightsError = class extends BaseConfigurationApiError {
  static {
    __name(this, "CampaignInvalidBundleWeightsError");
  }
  type = PartnershipConfigurationErrorTypes.CAMPAIGN_INVALID_BUNDLE_WEIGHTS;
  constructor(message = "The bundles of the campaign have invalid weights.") {
    super(message);
  }
};

// src/partnership-configuration/errors/campaign-not-found.error.ts
var CampaignNotFoundError = class extends BaseConfigurationApiError {
  static {
    __name(this, "CampaignNotFoundError");
  }
  type = PartnershipConfigurationErrorTypes.CAMPAIGN_NOT_FOUND;
  constructor(message = "The campaign was not found.") {
    super(message);
  }
};

// src/partnership-configuration/errors/campaign-overlaps-other-campaigns.error.ts
var CampaignOverlapsOtherCampaignsError = class extends BaseConfigurationApiError {
  static {
    __name(this, "CampaignOverlapsOtherCampaignsError");
  }
  type = PartnershipConfigurationErrorTypes.CAMPAIGN_OVERLAPS_OTHER_CAMPAIGNS;
  constructor(message = "The campaign overlaps other campaigns.") {
    super(message);
  }
};

// src/partnership-configuration/errors/default-campaign-already-exists.error.ts
var DefaultCampaignAlreadyExistsError = class extends BaseConfigurationApiError {
  static {
    __name(this, "DefaultCampaignAlreadyExistsError");
  }
  type = PartnershipConfigurationErrorTypes.DEFAULT_CAMPAIGN_ALREADY_EXISTS;
  constructor(message = "A default campaign already exists for that bundle.") {
    super(message);
  }
};

// src/partnership-configuration/errors/operation-not-allowed-on-target-stage.error.ts
var OperationNotAllowedOnTargetStageError = class extends BaseConfigurationApiError {
  static {
    __name(this, "OperationNotAllowedOnTargetStageError");
  }
  type = PartnershipConfigurationErrorTypes.OPERATION_NOT_ALLOWED_ON_TARGET_STAGE;
  constructor(message = "Operation is not allowed on the target stage.") {
    super(message);
  }
};

// src/partnership-configuration/errors/partner-already-exists.error.ts
var PartnerAlreadyExistsError = class extends BaseConfigurationApiError {
  static {
    __name(this, "PartnerAlreadyExistsError");
  }
  type = PartnershipConfigurationErrorTypes.PARTNER_ALREADY_EXISTS;
  constructor(message = "The partner already exists.") {
    super(message);
  }
};

// src/partnership-configuration/errors/partner-country-missing-taxes.error.ts
var PartnerCountryMissingTaxesError = class extends BaseConfigurationApiError {
  static {
    __name(this, "PartnerCountryMissingTaxesError");
  }
  type = PartnershipConfigurationErrorTypes.PARTNER_COUNTRY_MISSING_TAXES;
  constructor(message = "The country of the partner misses some taxes.") {
    super(message);
  }
};

// src/partnership-configuration/errors/partner-country-not-found.error.ts
var PartnerCountryNotFoundError = class extends BaseConfigurationApiError {
  static {
    __name(this, "PartnerCountryNotFoundError");
  }
  type = PartnershipConfigurationErrorTypes.PARTNER_COUNTRY_NOT_FOUND;
  constructor(message = "The country of the partner was not found.") {
    super(message);
  }
};

// src/partnership-configuration/errors/partner-not-found.error.ts
var PartnerNotFoundError = class extends BaseConfigurationApiError {
  static {
    __name(this, "PartnerNotFoundError");
  }
  type = PartnershipConfigurationErrorTypes.PARTNER_NOT_FOUND;
  constructor(message = "The partner was not found.") {
    super(message);
  }
};

// src/partnership-configuration/errors/partner-webhook-secret-not-found.error.ts
var PartnerWebhookSecretNotFoundError = class extends BaseConfigurationApiError {
  static {
    __name(this, "PartnerWebhookSecretNotFoundError");
  }
  type = PartnershipConfigurationErrorTypes.PARTNER_WEBHOOK_SECRET_NOT_FOUND;
  constructor(message = "The partner webhook secret was not found.") {
    super(message);
  }
};

// src/partnership-configuration/errors/product-already-exists.error.ts
var ProductAlreadyExistsError = class extends BaseConfigurationApiError {
  static {
    __name(this, "ProductAlreadyExistsError");
  }
  type = PartnershipConfigurationErrorTypes.PRODUCT_ALREADY_EXISTS;
  constructor(message = "The product already exists.") {
    super(message);
  }
};

// src/partnership-configuration/errors/product-exists-in-target-stage.error.ts
var ProductExistsInTargetStageError = class extends BaseConfigurationApiError {
  static {
    __name(this, "ProductExistsInTargetStageError");
  }
  type = PartnershipConfigurationErrorTypes.PRODUCT_EXISTS_IN_TARGET_STAGE;
  constructor(message = "The product exists in the target stage.") {
    super(message);
  }
};

// src/partnership-configuration/errors/product-invalid-configuration.error.ts
var ProductInvalidConfigurationError = class extends BaseConfigurationApiError {
  static {
    __name(this, "ProductInvalidConfigurationError");
  }
  type = PartnershipConfigurationErrorTypes.PRODUCT_INVALID_CONFIGURATION;
  constructor(message = "The product configuration is invalid.") {
    super(message);
  }
};

// src/partnership-configuration/errors/product-not-found.error.ts
var ProductNotFoundError = class extends BaseConfigurationApiError {
  static {
    __name(this, "ProductNotFoundError");
  }
  type = PartnershipConfigurationErrorTypes.PRODUCT_NOT_FOUND;
  constructor(message = "The product was not found.") {
    super(message);
  }
};

// src/partnership-configuration/errors/root-bundle-not-found.error.ts
var RootBundleNotFoundError = class extends BaseConfigurationApiError {
  static {
    __name(this, "RootBundleNotFoundError");
  }
  type = PartnershipConfigurationErrorTypes.ROOT_BUNDLE_NOT_FOUND;
  constructor(message = "The root bundle was not found.") {
    super(message);
  }
};

// src/partnership-configuration/errors/unknown.error.ts
var UnknownError = class extends BaseConfigurationApiError {
  static {
    __name(this, "UnknownError");
  }
  type = PartnershipConfigurationErrorTypes.UNKNOWN_ERROR;
  constructor(message = "Unknown error.") {
    super(message);
  }
};

// src/partnership-configuration/errors/wise-forbidden-country-already-exists.error.ts
var WiseForbiddenCountryAlreadyExistsError = class extends BaseConfigurationApiError {
  static {
    __name(this, "WiseForbiddenCountryAlreadyExistsError");
  }
  type = PartnershipConfigurationErrorTypes.WISE_FORBIDDEN_COUNTRY_ALREADY_EXISTS;
  constructor(message = "The Wise forbidden country already exists.") {
    super(message);
  }
};

// src/partnership-configuration/errors/wise-forbidden-country-not-found.error.ts
var WiseForbiddenCountryNotFoundError = class extends BaseConfigurationApiError {
  static {
    __name(this, "WiseForbiddenCountryNotFoundError");
  }
  type = PartnershipConfigurationErrorTypes.WISE_FORBIDDEN_COUNTRY_NOT_FOUND;
  constructor(message = "The Wise forbidden country was not found.") {
    super(message);
  }
};

// src/partnership-configuration/errors/wise-payout-method-already-exists.error.ts
var WisePayoutMethodAlreadyExistsError = class extends BaseConfigurationApiError {
  static {
    __name(this, "WisePayoutMethodAlreadyExistsError");
  }
  type = PartnershipConfigurationErrorTypes.WISE_PAYOUT_METHOD_ALREADY_EXISTS;
  constructor(message = "The Wise payout method already exists.") {
    super(message);
  }
};

// src/partnership-configuration/errors/wise-payout-method-not-found.error.ts
var WisePayoutMethodNotFoundError = class extends BaseConfigurationApiError {
  static {
    __name(this, "WisePayoutMethodNotFoundError");
  }
  type = PartnershipConfigurationErrorTypes.WISE_PAYOUT_METHOD_NOT_FOUND;
  constructor(message = "The Wise payout method was not found.") {
    super(message);
  }
};

// src/partnership-configuration/errors/index.ts
var PARTNERSHIP_CONFIGURATION_API_ERRORS = [
  UnknownError,
  PartnerAlreadyExistsError,
  PartnerCountryMissingTaxesError,
  PartnerCountryNotFoundError,
  PartnerNotFoundError,
  PartnerWebhookSecretNotFoundError,
  ProductAlreadyExistsError,
  ProductNotFoundError,
  ProductExistsInTargetStageError,
  ProductInvalidConfigurationError,
  BundleAlreadyExistsError,
  BundleExistsInTargetStageError,
  BundleIsNotARootBundleError,
  BundleNotFoundError,
  BundlesHaveDifferentRootBundlesError,
  BundleProductsCurrenciesDiscrepanciesError,
  CampaignAlreadyExistsError,
  CampaignAndBundleMismatchOnRootBundleError,
  CampaignInvalidBundleWeightsError,
  CampaignNotFoundError,
  CampaignOverlapsOtherCampaignsError,
  DefaultCampaignAlreadyExistsError,
  OperationNotAllowedOnTargetStageError,
  RootBundleNotFoundError,
  WiseForbiddenCountryAlreadyExistsError,
  WiseForbiddenCountryNotFoundError,
  WisePayoutMethodAlreadyExistsError,
  WisePayoutMethodNotFoundError
];

// src/partnership-configuration/partners/account.schema.ts
import { z as z137 } from "zod";
import { PartnerAccountGroupEnum } from "@repo/lib-koala-types";
var accountSchema = z137.strictObject({
  id: z137.string().uuid(),
  name: z137.string(),
  group: z137.literal(PartnerAccountGroupEnum.PARTNER)
});

// src/partnership-configuration/partners/bank-details/partner-bank-details.schema.ts
import { z as z141 } from "zod";

// src/partnership-configuration/partners/bank-details/partner-bank-details-account-number.schema.ts
import { z as z139 } from "zod";

// src/partnership-configuration/partners/bank-details/partner-bank-details-base.schema.ts
import { z as z138 } from "zod";
var partnerBankDetailsBaseSchema = z138.strictObject({
  id: z138.string().uuid(),
  beneficiaryName: z138.string().trim().regex(/^[0-9A-Za-zÀ-ÖØ-öø-ÿ-_()'*,.\s]*$/).min(1).max(255),
  bankName: z138.string().trim().min(1).max(255).optional(),
  branchCity: z138.string().trim().min(1).max(255).optional(),
  branchName: z138.string().trim().min(1).max(255).optional(),
  address: beneficiaryAddressSchema.extend({
    id: z138.string().uuid()
  }).optional()
});

// src/partnership-configuration/partners/bank-details/partner-bank-details-account-number.schema.ts
var partnerBankDetailsAccountNumberSchema = partnerBankDetailsBaseSchema.extend({
  type: z139.literal("ACCOUNT_NUMBER"),
  bic: bicSchema,
  accountNumber: accountNumberSchema
});

// src/partnership-configuration/partners/bank-details/partner-bank-details-iban.schema.ts
import { z as z140 } from "zod";
var partnerBankDetailsIbanSchema = partnerBankDetailsBaseSchema.extend({
  type: z140.literal("IBAN"),
  iban: ibanSchema,
  bic: bicSchema
});

// src/partnership-configuration/partners/bank-details/partner-bank-details.schema.ts
var partnerBankDetailsSchema = z141.discriminatedUnion("type", [
  partnerBankDetailsIbanSchema,
  partnerBankDetailsAccountNumberSchema
]);

// src/partnership-configuration/partners/create-partner/create-partner-req-body.schema.ts
import { z as z147 } from "zod";

// src/partnership-configuration/partners/partner.schema.ts
import { z as z144 } from "zod";
import { PartnerAccountGroupEnum as PartnerAccountGroupEnum2, PartnerBusinessCategoryEnum, PartnerTierEnum } from "@repo/lib-koala-types";
import { commaSeparatedEmailsSchema, dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema9 } from "@repo/schema-common";

// src/partnership-configuration/partners/per-product-classification-data.schema.ts
import { z as z142 } from "zod";
import { PartnerBrandingModeEnum, PartnerIntegrationModeEnum, ProductTypeEnum as ProductTypeEnum21 } from "@repo/lib-koala-types";
var perProductClassificationDataSchema = z142.strictObject({
  id: z142.string().uuid(),
  partnerId: z142.string().uuid(),
  dealOwner: z142.string().min(1),
  integrationMode: z142.nativeEnum(PartnerIntegrationModeEnum).optional(),
  brandingMode: z142.nativeEnum(PartnerBrandingModeEnum).optional(),
  productType: z142.nativeEnum(ProductTypeEnum21).optional()
});

// src/partnership-configuration/partners/synchronization-settings.schema.ts
import { z as z143 } from "zod";
import { PartnerSyncWebhookVersionEnum } from "@repo/lib-koala";
import { ClaimStatusEnum as ClaimStatusEnum2 } from "@repo/lib-koala-types";
var synchronizationSettingsSchema = z143.strictObject({
  id: z143.string().uuid(),
  partnerId: z143.string().uuid(),
  apiSecretKey: z143.string(),
  apiUrl: z143.string().url(),
  claimStatuses: z143.array(z143.nativeEnum(ClaimStatusEnum2)),
  syncContracts: z143.boolean(),
  webhookVersion: z143.nativeEnum(PartnerSyncWebhookVersionEnum)
});

// src/partnership-configuration/partners/partner.schema.ts
var accountSchema2 = z144.strictObject({
  id: z144.string().uuid(),
  partnerId: z144.string().uuid(),
  name: z144.string(),
  group: z144.literal(PartnerAccountGroupEnum2.PARTNER),
  createdAt: dateTimeWithUtcOffsetSchema9
});
var partnerSchema = z144.strictObject({
  id: z144.string().uuid(),
  name: z144.string().min(1).regex(/^[^,]*$/, {
    message: "Commas are not allowed."
  }).regex(/^[^\n\r]*$/, {
    message: "Line breaks are not allowed."
  }),
  account: accountSchema2,
  businessCategory: z144.nativeEnum(PartnerBusinessCategoryEnum).optional(),
  countryId: z144.string().length(2),
  quoteExpirationDurationInDays: z144.number().int().positive(),
  bccEmailAddress: commaSeparatedEmailsSchema.optional(),
  developersEmailAddress: commaSeparatedEmailsSchema.optional(),
  synchronizationSettings: synchronizationSettingsSchema.optional(),
  perProductClassificationData: z144.array(perProductClassificationDataSchema).optional(),
  tier: z144.nativeEnum(PartnerTierEnum).optional(),
  isKoalaMerchantOfRecord: z144.boolean(),
  bankDetails: partnerBankDetailsSchema.optional(),
  createdAt: dateTimeWithUtcOffsetSchema9,
  updatedAt: dateTimeWithUtcOffsetSchema9
});

// src/partnership-configuration/partners/quote-expiration-duration-in-days-input.schema.ts
import { z as z145 } from "zod";
import { MAX_QUOTE_EXPIRATION_DURATION_IN_DAYS } from "@repo/lib-koala";
var quoteExpirationDurationInDaysInputSchema = z145.number().int().positive().max(MAX_QUOTE_EXPIRATION_DURATION_IN_DAYS, {
  message: `A quote cannot remain valid for more than ${MAX_QUOTE_EXPIRATION_DURATION_IN_DAYS} days.`
});

// src/partnership-configuration/partners/create-partner/per-product-classification-data-creation-data.schema.ts
var perProductClassificationDataCreationDataSchema = perProductClassificationDataSchema.pick({
  id: true,
  brandingMode: true,
  dealOwner: true,
  integrationMode: true,
  productType: true
});

// src/partnership-configuration/partners/create-partner/synchronization-settings-creation-data.schema.ts
import { z as z146 } from "zod";
var synchronizationSettingsCreationDataSchema = synchronizationSettingsSchema.pick({
  id: true,
  apiUrl: true,
  claimStatuses: true,
  syncContracts: true,
  webhookVersion: true
}).extend({
  secretValue: z146.string().min(1)
});

// src/partnership-configuration/partners/create-partner/create-partner-req-body.schema.ts
var createPartnerReqBodySchema = partnerSchema.pick({
  id: true,
  name: true,
  businessCategory: true,
  countryId: true,
  bccEmailAddress: true,
  developersEmailAddress: true,
  tier: true,
  isKoalaMerchantOfRecord: true
}).extend({
  quoteExpirationDurationInDays: quoteExpirationDurationInDaysInputSchema,
  perProductClassificationData: z147.array(perProductClassificationDataCreationDataSchema).optional(),
  synchronizationSettings: synchronizationSettingsCreationDataSchema.optional(),
  bankDetails: partnerBankDetailsSchema.optional(),
  accountId: z147.string().uuid()
});

// src/partnership-configuration/partners/create-partner/create-partner-res-body.schema.ts
import { z as z148 } from "zod";
var createPartnerResBodySchema = partnerSchema.pick({
  id: true,
  name: true
}).extend({
  token: z148.string()
});

// src/partnership-configuration/partners/get-partner/get-partner-req-params.schema.ts
import { z as z149 } from "zod";
var getPartnerReqParamsSchema = z149.strictObject({
  id: z149.string().uuid()
});

// src/partnership-configuration/partners/get-partner/get-partner-res-body.schema.ts
var getPartnerResBodySchema = partnerSchema.pick({
  id: true,
  name: true,
  account: true,
  businessCategory: true,
  countryId: true,
  quoteExpirationDurationInDays: true,
  developersEmailAddress: true,
  bccEmailAddress: true,
  synchronizationSettings: true,
  perProductClassificationData: true,
  tier: true,
  isKoalaMerchantOfRecord: true,
  bankDetails: true,
  createdAt: true,
  updatedAt: true
});

// src/partnership-configuration/partners/get-partner-overview/get-partner-overview-req-params.schema.ts
import { z as z150 } from "zod";
var getPartnerOverviewReqParamsSchema = z150.strictObject({
  id: z150.string().uuid()
});

// src/partnership-configuration/partners/get-partner-overview/get-partner-overview-res-body.schema.ts
import { z as z151 } from "zod";
import { PublicationStatusEnum as PublicationStatusEnum4 } from "@repo/lib-koala-types";
var getPartnerOverviewResBodySchema = z151.strictObject({
  partner: partnerSchema.pick({
    id: true,
    name: true,
    countryId: true,
    quoteExpirationDurationInDays: true,
    developersEmailAddress: true,
    bccEmailAddress: true,
    businessCategory: true,
    perProductClassificationData: true,
    synchronizationSettings: true,
    tier: true,
    isKoalaMerchantOfRecord: true,
    bankDetails: true,
    createdAt: true,
    updatedAt: true
  }).extend({
    publicationStatus: z151.nativeEnum(PublicationStatusEnum4),
    accountId: z151.string()
  }),
  activeProducts: baseProductSchema.pick({
    id: true,
    name: true,
    type: true,
    displayName: true,
    version: true,
    pricingProvider: true,
    productProviderExternalData: true
  }).extend({
    preConditions: z151.string(),
    isActive: z151.boolean(),
    isActiveInTargetStage: z151.boolean(),
    pricingOverview: productPricingOverviewSchema,
    campaigns: z151.strictObject({
      id: z151.string().uuid(),
      name: z151.string()
    }).array(),
    bundles: z151.strictObject({
      id: z151.string().uuid(),
      productBundleId: z151.string().uuid(),
      name: z151.string()
    }).array(),
    currencies: currenciesSchema.array()
  }).array()
});

// src/partnership-configuration/partners/get-partner-publication-status/get-partner-publication-status-req-params.schema.ts
import { z as z152 } from "zod";
var getPartnerPublicationStatusReqParamsSchema = z152.strictObject({
  id: z152.string().uuid()
});

// src/partnership-configuration/partners/get-partner-publication-status/get-partner-publication-status-res-body.schema.ts
import { z as z153 } from "zod";
import { PublicationStatusEnum as PublicationStatusEnum5 } from "@repo/lib-koala-types";
var getPartnerPublicationStatusResBodySchema = partnerSchema.pick({
  id: true,
  name: true,
  account: true,
  businessCategory: true,
  countryId: true,
  quoteExpirationDurationInDays: true,
  developersEmailAddress: true,
  bccEmailAddress: true,
  tier: true,
  perProductClassificationData: true,
  isKoalaMerchantOfRecord: true,
  synchronizationSettings: true,
  bankDetails: true,
  createdAt: true,
  updatedAt: true
}).extend({
  publicationStatus: z153.nativeEnum(PublicationStatusEnum5)
});

// src/partnership-configuration/partners/list-partners/list-partners-res-body.schema.ts
import { z as z154 } from "zod";
var listPartnersResBodySchema = z154.array(partnerSchema.pick({
  id: true,
  name: true,
  account: true,
  countryId: true,
  quoteExpirationDurationInDays: true,
  developersEmailAddress: true,
  businessCategory: true,
  perProductClassificationData: true,
  tier: true,
  bccEmailAddress: true,
  synchronizationSettings: true,
  isKoalaMerchantOfRecord: true,
  bankDetails: true,
  createdAt: true,
  updatedAt: true
}));

// src/partnership-configuration/partners/list-partners-publication-status/list-partners-publication-status-res-body.schema.ts
import { z as z155 } from "zod";
import { PublicationStatusEnum as PublicationStatusEnum6 } from "@repo/lib-koala-types";
var listPartnersPublicationStatusResBodySchema = z155.array(partnerSchema.pick({
  id: true,
  name: true,
  account: true,
  businessCategory: true,
  countryId: true,
  quoteExpirationDurationInDays: true,
  tier: true,
  developersEmailAddress: true,
  bccEmailAddress: true,
  synchronizationSettings: true,
  perProductClassificationData: true,
  isKoalaMerchantOfRecord: true,
  bankDetails: true,
  createdAt: true,
  updatedAt: true
}).extend({
  publicationStatus: z155.nativeEnum(PublicationStatusEnum6)
}));

// src/partnership-configuration/partners/publish-partner/publish-partner-req-body.schema.ts
import { z as z156 } from "zod";
var publishPartnerReqBodySchema = synchronizationSettingsSchema.pick({
  apiUrl: true
}).extend({
  secretValue: z156.string().min(1)
}).partial({
  apiUrl: true,
  secretValue: true
});

// src/partnership-configuration/partners/publish-partner/publish-partner-req-params.schema.ts
var publishPartnerReqParamsSchema = partnerSchema.pick({
  id: true
});

// src/partnership-configuration/partners/publish-partner/publish-partner-res-body.schema.ts
import { z as z157 } from "zod";
var publishPartnerResBodySchema = partnerSchema.pick({
  id: true,
  name: true,
  account: true,
  businessCategory: true,
  countryId: true,
  quoteExpirationDurationInDays: true,
  developersEmailAddress: true,
  bccEmailAddress: true,
  perProductClassificationData: true,
  synchronizationSettings: true,
  tier: true,
  isKoalaMerchantOfRecord: true,
  bankDetails: true,
  createdAt: true,
  updatedAt: true
}).extend({
  token: z157.string().optional()
});

// src/partnership-configuration/partners/update-partner/per-product-classification-data-update-data.schema.ts
var perProductClassificationDataUpdateDataSchema = perProductClassificationDataSchema.pick({
  id: true,
  brandingMode: true,
  dealOwner: true,
  integrationMode: true,
  productType: true
});

// src/partnership-configuration/partners/update-partner/synchronization-settings-update-data.schema.ts
import { z as z158 } from "zod";
var synchronizationSettingsUpdateDataSchema = synchronizationSettingsSchema.pick({
  id: true,
  apiUrl: true,
  claimStatuses: true,
  syncContracts: true,
  webhookVersion: true
}).extend({
  secretValue: z158.string().min(1)
}).partial({
  apiUrl: true,
  secretValue: true
});

// src/partnership-configuration/partners/update-partner/update-partner-req-body.schema.ts
import { z as z159 } from "zod";
var updatePartnerReqBodySchema = partnerSchema.pick({
  countryId: true,
  businessCategory: true,
  bccEmailAddress: true,
  tier: true,
  developersEmailAddress: true,
  isKoalaMerchantOfRecord: true
}).merge(z159.strictObject({
  quoteExpirationDurationInDays: quoteExpirationDurationInDaysInputSchema
})).merge(z159.strictObject({
  perProductClassificationData: z159.array(perProductClassificationDataUpdateDataSchema)
})).merge(z159.strictObject({
  synchronizationSettings: synchronizationSettingsUpdateDataSchema
})).merge(z159.strictObject({
  bankDetails: partnerBankDetailsSchema.optional().nullable()
})).partial().strict();

// src/partnership-configuration/partners/update-partner/update-partner-req-params.schema.ts
import { z as z160 } from "zod";
var updatePartnerReqParamsSchema = z160.strictObject({
  id: z160.string().uuid()
});

// src/partnership-configuration/partners/update-partner/update-partner-res-body.schema.ts
var updatePartnerResBodySchema = partnerSchema.pick({
  id: true,
  name: true,
  quoteExpirationDurationInDays: true,
  developersEmailAddress: true,
  perProductClassificationData: true,
  synchronizationSettings: true,
  bccEmailAddress: true,
  businessCategory: true,
  countryId: true,
  account: true,
  tier: true,
  isKoalaMerchantOfRecord: true,
  bankDetails: true,
  updatedAt: true,
  createdAt: true
});

// src/partnership-configuration/subscriptions/get-subscription/contract-financial-info.schema.ts
import { extendApi as extendApi23 } from "@anatine/zod-openapi";
import { z as z161 } from "zod";
import { isoCurrencySchema, priceSchema as priceSchema8 } from "@repo/schema-common";
var contractFinancialInfoSchema = extendApi23(z161.strictObject({
  retailPremiumTaxesIncluded: extendApi23(priceSchema8, {
    description: "The full price paid by the customer, including Koala commission, partner commission, insurer premium and taxes."
  }),
  maximumExpectedCompensation: extendApi23(priceSchema8, {
    description: "Maximum amount travelers are entitled to for this contract. Varies by product settings and context."
  }),
  initialPremium: extendApi23(priceSchema8, {
    description: "Premium due to the insurer as set at subscription time (Gross Net Written Premium). May differ from the actual premium after financial-backer selection."
  }),
  distributionPartnerCommission: extendApi23(priceSchema8, {
    description: "Commission paid to the distribution partner."
  }),
  initialKoalaCommission: extendApi23(priceSchema8, {
    description: "Koala commission as computed at subscription time: retail \u2212 taxes \u2212 initial premium \u2212 brokerage fees \u2212 business contributor commission \u2212 partner commission."
  }),
  businessContributorCommission: extendApi23(priceSchema8, {
    description: "Commission paid to the business contributor. Zero when none applies."
  }),
  brokerageFees: extendApi23(priceSchema8.optional(), {
    description: "Brokerage fees on the contract, when set."
  }),
  serviceTaxAmount: extendApi23(priceSchema8, {
    description: "Service tax portion of the retail price."
  }),
  insuranceTaxAmount: extendApi23(priceSchema8, {
    description: "Insurance tax portion of the retail price."
  }),
  additionalTaxAmount: extendApi23(priceSchema8, {
    description: "Additional tax portion of the retail price."
  }),
  financialBackerCurrency: extendApi23(isoCurrencySchema.optional(), {
    description: "Currency in which the financial-backer amounts below are denominated."
  }),
  financialBackerExchangeRateUsed: extendApi23(z161.number().positive().optional(), {
    description: "Exchange rate from contract currency to financial-backer currency at attribution time."
  }),
  financialBackerPremium: extendApi23(z161.number().positive().optional(), {
    description: "Premium charged by the assigned financial backer, in financial-backer currency."
  }),
  actualKoalaCommission: extendApi23(z161.number().optional(), {
    description: "Koala commission after financial-backer selection, in financial-backer currency."
  }),
  actualServiceTaxes: extendApi23(z161.number().nonnegative().optional(), {
    description: "Service taxes after financial-backer selection, in financial-backer currency."
  }),
  actualInsuranceTaxes: extendApi23(z161.number().nonnegative().optional(), {
    description: "Insurance taxes after financial-backer selection, in financial-backer currency."
  })
}), {
  title: "ContractFinancialInfoOutputDto"
});

// src/partnership-configuration/subscriptions/get-subscription/get-subscription-req-params.schema.ts
import { extendApi as extendApi24 } from "@anatine/zod-openapi";
import { z as z162 } from "zod";
var getSubscriptionReqParamsSchema = z162.strictObject({
  partnerId: extendApi24(z162.string().uuid(), {
    description: "Universally unique identifier (UUID) of the partner that owns the subscription.",
    example: "f7a0f30d-6a6f-4b39-90c5-9d0ee6db6d51"
  }),
  bookingNumber: extendApi24(z162.string().max(255), {
    description: "Booking number of the subscription.",
    example: "5K7SXD"
  })
});

// src/partnership-configuration/subscriptions/get-subscription/get-subscription-res-body.schema.ts
import { extendApi as extendApi36 } from "@anatine/zod-openapi";
import { z as z171 } from "zod";

// src/partnership-configuration/subscriptions/get-subscription/get-subscription-res-body-customer.schema.ts
import { extendApi as extendApi25 } from "@anatine/zod-openapi";
import { z as z163 } from "zod";
import { emailSchema as emailSchema2, languageSchema, phoneNumberSchema } from "@repo/schema-common";
var getSubscriptionResBodyCustomerSchema = extendApi25(z163.strictObject({
  id: extendApi25(z163.string().uuid(), {
    description: "Universally unique identifier (UUID) of the customer.",
    example: "a1b2c3d4-e5f6-7890-abcd-ef1234567890"
  }),
  firstName: extendApi25(z163.string().min(1).max(255), {
    description: "First name of the customer.",
    example: "Mary"
  }),
  lastName: extendApi25(z163.string().min(1).max(255), {
    description: "Last name of the customer.",
    example: "Dancer"
  }),
  email: extendApi25(emailSchema2, {
    description: "Email address of the customer.",
    example: "mary.dancer@gmail.com"
  }),
  language: extendApi25(languageSchema, {
    description: "Language of the customer.",
    example: "en-GB"
  }),
  phoneNumber: extendApi25(phoneNumberSchema.optional(), {
    description: "Phone number of the customer.",
    example: "+1 213 373 4253"
  }),
  birthday: extendApi25(birthdaySchema.optional(), {
    description: "Birthday of the customer.",
    example: "1990-05-12"
  }),
  address: extendApi25(addressSchema.optional(), {
    description: "Address of the customer."
  })
}), {
  title: "GetSubscriptionCustomerOutputDto"
});

// src/partnership-configuration/subscriptions/get-subscription/get-subscription-res-body-trip.schema.ts
import { extendApi as extendApi26 } from "@anatine/zod-openapi";
import { z as z164 } from "zod";
import { priceSchema as priceSchema9 } from "@repo/schema-common";
var getSubscriptionResBodyTripSchema = extendApi26(z164.strictObject({
  id: z164.string().uuid(),
  bundle: z164.strictObject({
    id: z164.string().uuid(),
    name: z164.string()
  }),
  contractIds: extendApi26(z164.array(z164.string().uuid()).min(1), {
    description: "Active contracts of the trip, all coming from the same quoted bundle."
  }),
  price: extendApi26(priceSchema9, {
    description: "Sum of the retail prices of the trip active contracts."
  }),
  tripPrice: extendApi26(priceSchema9.optional(), {
    description: "Price of the trip itself, when known."
  }),
  startDate: z164.string().datetime({
    offset: true
  })
}), {
  title: "SubscriptionTripOutputDto"
});

// src/partnership-configuration/subscriptions/get-subscription/rich-contract.schema.ts
import { extendApi as extendApi35 } from "@anatine/zod-openapi";
import { z as z170 } from "zod";

// src/resources/subscriptions/contracts/flight-contract.schema.ts
import { extendApi as extendApi30 } from "@anatine/zod-openapi";
import { DateTime as DateTime7 } from "luxon";
import { z as z167 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum19, ProductTypeEnum as ProductTypeEnum22 } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema12, priceSchema as priceSchema11 } from "@repo/schema-common";

// src/resources/subscriptions/contracts/contract-claim.schema.ts
import { extendApi as extendApi27 } from "@anatine/zod-openapi";
import { DateTime as DateTime6 } from "luxon";
import { z as z165 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum3 } from "@repo/lib-koala";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema10, priceSchema as priceSchema10 } from "@repo/schema-common";
var contractClaimSchema = z165.strictObject({
  id: extendApi27(z165.string().uuid().optional(), {
    description: "Identifier of the claim."
  }),
  maximumExpectedCompensation: extendApi27(priceSchema10, {
    description: "Maximum compensation to which travelers are entitled. It may vary depending on the product settings and the context (e.g. actual delay of a flight)."
  }),
  deadline: extendApi27(dateTimeWithUtcOffsetSchema10.optional(), {
    description: "The date until which travelers can make a claim.",
    example: DateTime6.utc().plus({
      months: 1
    }).startOf("hour").plus({
      hours: 8,
      minutes: 21
    }).toString(),
    format: "date-time",
    type: "string"
  }),
  deadlineOriginalTimeZone: z165.string().optional(),
  status: extendApi27(z165.nativeEnum(ClaimStatusEnum3).optional(), {
    description: `The claim status are the following:
  - <code>${ClaimStatusEnum3.ACCEPTED}</code>: The travelers are eligible for compensation; we are waiting to receive their bank details.
  - <code>${ClaimStatusEnum3.REFUNDABLE}</code>: The bank details have been received; the bank transfer has not been processed yet.
  - <code>${ClaimStatusEnum3.REFUNDED}</code>: The bank transfer has been processed; the travelers have received their compensation or will receive it shortly.
  - <code>${ClaimStatusEnum3.MORE_INFO_NEEDED}</code>: Additional information is needed to determine the eligibility of the travelers.
  - <code>${ClaimStatusEnum3.MANUAL_VERIFICATION_NEEDED}</code>: Claim eligibility requires manual verification or there is potential fraud; the claim requires manual verification.
  - <code>${ClaimStatusEnum3.REJECTED}</code>: Based on the information provided, the travelers are not eligible for compensation.
  - <code>${ClaimStatusEnum3.AUTOMATICALLY_REJECTED}</code>: The claim was automatically rejected following a flight status update.
  - <code>${ClaimStatusEnum3.REFUND_ERROR}</code>: The refund could not be processed because information is missing.
  - <code>${ClaimStatusEnum3.CLAIM_ERROR}</code>: The claim process failed unexpectedly; an investigation is needed on our side.`,
    example: ClaimStatusEnum3.REFUNDED
  }),
  compensation: extendApi27(priceSchema10.optional(), {
    description: "Amount of the compensation."
  }),
  claimDate: extendApi27(dateTimeWithUtcOffsetSchema10.optional(), {
    description: "The date of the event the claim was made for.",
    example: DateTime6.utc().minus({
      days: 3
    }).startOf("day").toString(),
    format: "date-time",
    type: "string"
  })
});

// src/resources/subscriptions/contracts/contract-flight.schema.ts
import { extendApi as extendApi28 } from "@anatine/zod-openapi";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema11 } from "@repo/schema-common";
var contractFlightSchema = baseFlightSchema.extend({
  departureDate: extendApi28(dateTimeWithUtcOffsetSchema11, {
    description: "The <strong>local</strong> departure <strong>date time</strong> following the ISO 8601 format."
  }),
  arrivalDate: extendApi28(dateTimeWithUtcOffsetSchema11, {
    description: "The <strong>local</strong> arrival <strong>date time</strong> following the ISO 8601 format."
  })
});

// src/resources/subscriptions/contracts/contract-traveler.schema.ts
import { extendApi as extendApi29 } from "@anatine/zod-openapi";
import { z as z166 } from "zod";
import { AgeRangeEnum as AgeRangeEnum3 } from "@repo/lib-koala-types";
import { countryCodeSchema as countryCodeSchema6 } from "@repo/schema-common";
var contractTravelerSchema = extendApi29(z166.strictObject({
  ageRange: extendApi29(z166.nativeEnum(AgeRangeEnum3), {
    default: AgeRangeEnum3.ADULT,
    description: `We need one age range per traveler.
    The ageRange are the following:
  - <code>${AgeRangeEnum3.INFANT}</code> (0-23 months old)
  - <code>${AgeRangeEnum3.CHILD}</code> (2-11 years old)
  - <code>${AgeRangeEnum3.ADULT}</code> (12 - 64 years old)
  - <code>${AgeRangeEnum3.SENIOR}</code> (65+ years old)

<strong>\u26A0\uFE0F If not provided, <code>${AgeRangeEnum3.ADULT}</code> is assumed by default.</strong>`,
    example: AgeRangeEnum3.ADULT
  }),
  firstName: extendApi29(z166.string().max(255), {
    description: "First name of the traveler.",
    example: "Mary"
  }),
  lastName: extendApi29(z166.string().max(255), {
    description: "Last name of the traveler.",
    example: "Dancer"
  }),
  birthday: extendApi29(birthdaySchema.optional(), {
    description: "Birthday date of the traveler.",
    example: "1990-02-10"
  }),
  address: richAddressSchema.optional(),
  residenceCountryCode: extendApi29(countryCodeSchema6.optional(), {
    description: "The country of residence of the traveler following the ISO 3166-2 format.",
    example: "FR"
  }),
  numberOfCheckedBags: extendApi29(z166.number().int().positive().optional(), {
    description: "Number of checked bags for this traveler (baggage-disruption context).",
    example: 2
  })
}), {
  title: "ContractTravelerOutputDto",
  hideDefinitions: [
    "address"
  ]
});

// src/resources/subscriptions/contracts/flight-contract.schema.ts
var flightContractSchema = extendApi30(z167.strictObject({
  id: extendApi30(z167.string().uuid(), {
    description: "Universally unique identifier (UUID) of the contract.",
    example: "94e70133-8108-4ed8-a929-8ded4113311c"
  }),
  product: extendApi30(z167.strictObject({
    type: extendApi30(z167.nativeEnum(ProductTypeEnum22), {
      description: "Type of the product linked to the contract.",
      example: "FLIGHT_DISRUPTION"
    }),
    name: extendApi30(z167.string().max(255), {
      description: "Name of the product linked to the contract.",
      example: "Flight Disruption Compensation"
    }),
    coveredEventType: extendApi30(z167.nativeEnum(CoveredEventTypeEnum19), {
      description: "Type of event covered by the product linked to the contract.",
      example: CoveredEventTypeEnum19.FLIGHT
    })
  }), {
    description: "Product details related to this contract."
  }),
  coveredEvent: extendApi30(contractFlightSchema, {
    description: "The covered flight by the contract."
  }),
  travelers: extendApi30(z167.array(contractTravelerSchema).min(1), {
    description: "Travelers covered by the contract."
  }),
  price: extendApi30(priceSchema11, {
    description: "Price paid for this contract."
  }),
  expirationDate: extendApi30(dateTimeWithUtcOffsetSchema12.optional(), {
    description: "The date until which travelers can make a claim.",
    example: DateTime7.utc().plus({
      months: 1
    }).startOf("hour").plus({
      hours: 8,
      minutes: 21
    }).toString(),
    format: "date-time",
    type: "string"
  }),
  claim: extendApi30(contractClaimSchema.optional(), {
    description: "Claim details for this contract."
  })
}), {
  title: "FlightContractOutputDto"
});

// src/resources/subscriptions/contracts/trip-contract.schema.ts
import { extendApi as extendApi31 } from "@anatine/zod-openapi";
import { z as z168 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum20, ProductTypeEnum as ProductTypeEnum23 } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema13, priceSchema as priceSchema12 } from "@repo/schema-common";
var tripContractSchema = extendApi31(z168.strictObject({
  id: extendApi31(z168.string().uuid(), {
    description: "Universally unique identifier (UUID) of the contract.",
    example: "94e70133-8108-4ed8-a929-8ded4113311c"
  }),
  product: extendApi31(z168.strictObject({
    type: extendApi31(z168.nativeEnum(ProductTypeEnum23), {
      description: "Type of the product linked to the contract.",
      example: "FLIGHT_DISRUPTION"
    }),
    name: extendApi31(z168.string().max(255), {
      description: "Name of the product linked to the contract.",
      example: "Flight Disruption Compensation"
    }),
    coveredEventType: extendApi31(z168.nativeEnum(CoveredEventTypeEnum20), {
      description: "Type of event covered by the product linked to the contract.",
      example: CoveredEventTypeEnum20.TRIP
    })
  }), {
    description: "The covered event by the contract."
  }),
  coveredEvent: extendApi31(z168.strictObject({
    price: extendApi31(priceSchema12.optional(), {
      description: "Price of the trip"
    }),
    journeys: z168.array(extendApi31(z168.strictObject({
      flights: z168.array(contractFlightSchema),
      segments: extendApi31(z168.array(z168.union([
        flightSegmentSchema,
        genericSegmentSchema
      ])), {
        description: "Segments of the journey. It also contains the flights even if you provided flights instead of segments."
      }),
      price: extendApi31(priceSchema12.optional(), {
        description: "Price of the journey."
      })
    }), {
      title: "JourneyOutputDto"
    })),
    stays: z168.array(extendApi31(staySchema, {
      title: "StayOutputDto"
    }))
  }), {
    description: "The covered trip by the contract."
  }),
  travelers: extendApi31(z168.array(contractTravelerSchema).min(1), {
    description: "Travelers covered by the contract."
  }),
  price: extendApi31(priceSchema12, {
    description: "Price paid for this contract."
  }),
  expirationDate: extendApi31(dateTimeWithUtcOffsetSchema13.optional(), {
    description: "The date until which travelers can make a claim."
  }),
  claim: extendApi31(contractClaimSchema.optional(), {
    description: "Claim details for this contract."
  })
}), {
  title: "TripContractOutputDto"
});

// src/partnership-configuration/subscriptions/get-subscription/rich-contract-flight.schema.ts
import { extendApi as extendApi32 } from "@anatine/zod-openapi";
import { timezoneSchema as timezoneSchema2 } from "@repo/schema-common";
var richContractFlightSchema = extendApi32(contractFlightSchema.extend({
  arrivalAirportTimezone: extendApi32(timezoneSchema2, {
    description: "IANA time zone name of the arrival airport.",
    example: "America/New_York",
    externalDocs: {
      url: "https://www.iana.org/time-zones"
    }
  }),
  departureAirportTimezone: extendApi32(timezoneSchema2, {
    description: "IANA time zone name of the departure airport.",
    example: "Europe/Paris",
    externalDocs: {
      url: "https://www.iana.org/time-zones"
    }
  })
}), {
  title: "RichContractFlightOutputDto"
});

// src/partnership-configuration/subscriptions/get-subscription/rich-trip-covered-event.schema.ts
import { extendApi as extendApi34 } from "@anatine/zod-openapi";
import { z as z169 } from "zod";
import { priceSchema as priceSchema13 } from "@repo/schema-common";

// src/partnership-configuration/subscriptions/get-subscription/rich-flight-segment.schema.ts
import { extendApi as extendApi33 } from "@anatine/zod-openapi";
import { timezoneSchema as timezoneSchema3 } from "@repo/schema-common";
var richFlightSegmentSchema = extendApi33(flightSegmentSchema.extend({
  arrivalAirportTimezone: extendApi33(timezoneSchema3, {
    description: "IANA time zone name of the arrival airport.",
    example: "America/New_York",
    externalDocs: {
      url: "https://www.iana.org/time-zones"
    }
  }),
  departureAirportTimezone: extendApi33(timezoneSchema3, {
    description: "IANA time zone name of the departure airport.",
    example: "Europe/Paris",
    externalDocs: {
      url: "https://www.iana.org/time-zones"
    }
  })
}), {
  title: "RichFlightSegmentOutputDto"
});

// src/partnership-configuration/subscriptions/get-subscription/rich-trip-covered-event.schema.ts
var richTripCoveredEventSchema = extendApi34(z169.strictObject({
  price: extendApi34(priceSchema13.optional(), {
    description: "Price of the trip."
  }),
  journeys: z169.array(extendApi34(z169.strictObject({
    flights: z169.array(richContractFlightSchema),
    segments: extendApi34(z169.array(z169.union([
      richFlightSegmentSchema,
      genericSegmentSchema
    ])), {
      description: "Segments of the journey with rich-only airport time zone info."
    }),
    price: extendApi34(priceSchema13.optional(), {
      description: "Price of the journey."
    })
  }), {
    title: "RichJourneyOutputDto"
  })),
  stays: z169.array(extendApi34(staySchema, {
    title: "StayOutputDto"
  }))
}), {
  description: "The covered trip by the contract, with rich-only airport time zone info."
});

// src/partnership-configuration/subscriptions/get-subscription/rich-contract.schema.ts
var richFlightContractSchema = extendApi35(flightContractSchema.extend({
  tripId: extendApi35(z170.string().uuid(), {
    description: "Trip of the subscription the contract belongs to."
  }),
  coveredEvent: extendApi35(richContractFlightSchema, {
    description: "The covered flight by the contract, with rich-only airport time zone info."
  }),
  expirationDateOriginalTimeZone: z170.string().optional(),
  financialData: contractFinancialInfoSchema
}), {
  title: "RichFlightContractOutputDto"
});
var richTripContractSchema = extendApi35(tripContractSchema.extend({
  tripId: extendApi35(z170.string().uuid(), {
    description: "Trip of the subscription the contract belongs to."
  }),
  coveredEvent: richTripCoveredEventSchema,
  expirationDateOriginalTimeZone: z170.string().optional(),
  financialData: contractFinancialInfoSchema
}), {
  title: "RichTripContractOutputDto"
});
var richContractSchema = z170.union([
  richFlightContractSchema,
  richTripContractSchema
]);

// src/partnership-configuration/subscriptions/get-subscription/get-subscription-res-body.schema.ts
var getSubscriptionResBodySchema = z171.strictObject({
  data: z171.strictObject({
    contracts: extendApi36(z171.array(z171.union([
      richFlightContractSchema,
      richTripContractSchema
    ])).min(1), {
      description: "List of contracts linked to this subscription, including financial data."
    }),
    customer: getSubscriptionResBodyCustomerSchema,
    trips: extendApi36(z171.array(getSubscriptionResBodyTripSchema), {
      description: "Trips of the subscription holding at least one active contract. One trip holds one bundle."
    })
  })
});

// src/partnership-configuration/wise-forbidden-countries/wise-forbidden-country.schema.ts
import { z as z172 } from "zod";
import { countryCodeSchema as countryCodeSchema7, dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema14 } from "@repo/schema-common";
var wiseForbiddenCountrySchema = z172.strictObject({
  id: countryCodeSchema7,
  notes: z172.string().trim().min(1).max(500).nullable(),
  createdAt: dateTimeWithUtcOffsetSchema14,
  updatedAt: dateTimeWithUtcOffsetSchema14
});

// src/partnership-configuration/wise-forbidden-countries/create-wise-forbidden-country-req-body.schema.ts
var createWiseForbiddenCountryReqBodySchema = wiseForbiddenCountrySchema.pick({
  id: true,
  notes: true
}).partial({
  notes: true
});

// src/partnership-configuration/wise-forbidden-countries/create-wise-forbidden-country-res-body.schema.ts
var createWiseForbiddenCountryResBodySchema = wiseForbiddenCountrySchema.pick({
  id: true,
  notes: true,
  createdAt: true,
  updatedAt: true
});

// src/partnership-configuration/wise-forbidden-countries/delete-wise-forbidden-country-req-params.schema.ts
import { z as z173 } from "zod";
import { countryCodeSchema as countryCodeSchema8 } from "@repo/schema-common";
var deleteWiseForbiddenCountryReqParamsSchema = z173.strictObject({
  id: countryCodeSchema8
});

// src/partnership-configuration/wise-forbidden-countries/get-wise-forbidden-countries-res-body.schema.ts
var getWiseForbiddenCountriesResBodySchema = wiseForbiddenCountrySchema.pick({
  id: true,
  notes: true,
  createdAt: true,
  updatedAt: true
}).array();

// src/partnership-configuration/wise-forbidden-countries/update-wise-forbidden-country-req-body.schema.ts
var updateWiseForbiddenCountryReqBodySchema = wiseForbiddenCountrySchema.pick({
  notes: true
});

// src/partnership-configuration/wise-forbidden-countries/update-wise-forbidden-country-req-params.schema.ts
var updateWiseForbiddenCountryReqParamsSchema = wiseForbiddenCountrySchema.pick({
  id: true
});

// src/partnership-configuration/wise-forbidden-countries/update-wise-forbidden-country-res-body.schema.ts
var updateWiseForbiddenCountryResBodySchema = wiseForbiddenCountrySchema.pick({
  id: true,
  notes: true,
  createdAt: true,
  updatedAt: true
});

// src/partnership-configuration/wise-payout-methods/wise-payout-method.schema.ts
import { z as z174 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema15 } from "@repo/schema-common";
var wisePayoutMethodSchema = z174.strictObject({
  id: z174.string().uuid(),
  name: z174.string().trim().min(1).max(50),
  isAuthorized: z174.boolean(),
  notes: z174.string().trim().min(1).max(500).nullable(),
  createdAt: dateTimeWithUtcOffsetSchema15,
  updatedAt: dateTimeWithUtcOffsetSchema15
});

// src/partnership-configuration/wise-payout-methods/create-wise-payout-method/create-wise-payout-method-req-body.schema.ts
var createWisePayoutMethodReqBodySchema = wisePayoutMethodSchema.pick({
  id: true,
  name: true,
  isAuthorized: true,
  notes: true
}).partial({
  notes: true
});

// src/partnership-configuration/wise-payout-methods/create-wise-payout-method/create-wise-payout-method-res-body.schema.ts
var createWisePayoutMethodResBodySchema = wisePayoutMethodSchema.pick({
  id: true,
  name: true,
  isAuthorized: true,
  notes: true,
  createdAt: true,
  updatedAt: true
});

// src/partnership-configuration/wise-payout-methods/get-wise-payout-methods/get-wise-payout-methods-res-body.schema.ts
var getWisePayoutMethodsResBodySchema = wisePayoutMethodSchema.pick({
  id: true,
  name: true,
  isAuthorized: true,
  notes: true,
  createdAt: true,
  updatedAt: true
}).array();

// src/partnership-configuration/wise-payout-methods/get-wise-payout-methods-rich/get-wise-payout-methods-rich-res-body.schema.ts
import { z as z175 } from "zod";
import { PublicationStatusEnum as PublicationStatusEnum7 } from "@repo/lib-koala-types";
var getWisePayoutMethodsRichResBodySchema = wisePayoutMethodSchema.pick({
  id: true,
  name: true,
  isAuthorized: true,
  notes: true,
  createdAt: true,
  updatedAt: true
}).extend({
  publicationStatus: z175.nativeEnum(PublicationStatusEnum7),
  isAuthorizedInTargetStage: z175.boolean()
}).array();

// src/partnership-configuration/wise-payout-methods/publish-wise-payout-method/publish-wise-payout-method-req-params.schema.ts
import { z as z176 } from "zod";
var publishWisePayoutMethodReqParamsSchema = z176.strictObject({
  id: z176.string().uuid()
});

// src/partnership-configuration/wise-payout-methods/publish-wise-payout-method/publish-wise-payout-method-res-body.schema.ts
var publishWisePayoutMethodResBodySchema = wisePayoutMethodSchema.pick({
  id: true,
  name: true,
  isAuthorized: true,
  notes: true,
  createdAt: true,
  updatedAt: true
});

// src/partnership-configuration/wise-payout-methods/update-wise-payout-method/update-wise-payout-method-req-body.schema.ts
var updateWisePayoutMethodReqBodySchema = wisePayoutMethodSchema.pick({
  name: true,
  isAuthorized: true,
  notes: true
}).partial();

// src/partnership-configuration/wise-payout-methods/update-wise-payout-method/update-wise-payout-method-req-params.schema.ts
import { z as z177 } from "zod";
var updateWisePayoutMethodReqParamsSchema = z177.strictObject({
  id: z177.string().uuid()
});

// src/partnership-configuration/wise-payout-methods/update-wise-payout-method/update-wise-payout-method-res-body.schema.ts
var updateWisePayoutMethodResBodySchema = wisePayoutMethodSchema.pick({
  id: true,
  name: true,
  isAuthorized: true,
  notes: true,
  createdAt: true,
  updatedAt: true
});

// src/resources/bank-details/update-subscription-bank-details/update-subscription-bank-details-req-body.schema.ts
import { z as z178 } from "zod";
var updateSubscriptionBankDetailsReqBodySchema = z178.strictObject({
  data: z178.strictObject({
    iban: ibanSchema.optional().nullable(),
    accountNumber: accountNumberSchema.optional().nullable(),
    bic: bicSchema.optional().nullable(),
    beneficiaryName: beneficiarySchema.shape.beneficiaryName.optional(),
    beneficiaryAddress: richAddressSchema.optional()
  })
});

// src/resources/bank-details/update-subscription-bank-details/update-subscription-bank-details-req-params.schema.ts
import { z as z179 } from "zod";
var updateSubscriptionBankDetailsReqParamsSchema = z179.strictObject({
  id: z179.string().uuid()
});

// src/resources/bank-details/update-subscription-bank-details/update-subscription-bank-details-req-query.schema.ts
import { flexibleBooleanSchema as flexibleBooleanSchema2 } from "@repo/schema-common";
var updateSubscriptionBankDetailsReqQuerySchema = sideEffectsSchema.extend({
  silent: flexibleBooleanSchema2.default(false)
});

// src/resources/bookings/confirm-booking-cancellation/confirm-booking-cancellation-contract.schema.ts
import { extendApi as extendApi37 } from "@anatine/zod-openapi";
import { z as z180 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum4 } from "@repo/lib-koala-types";
var confirmBookingCancellationContractSchema = z180.strictObject({
  id: extendApi37(z180.string().uuid(), {
    description: "Universally unique identifier (UUID) of the contract.",
    example: "94e70133-8108-4ed8-a929-8ded4113311c"
  }),
  claim: z180.strictObject({
    status: extendApi37(z180.nativeEnum(ClaimStatusEnum4), {
      description: `The status of the claim once the cancellation has been confirmed:
  - <code>${ClaimStatusEnum4.ACCEPTED}</code>: The travelers are eligible for compensation; we are waiting to receive their bank details.
  - <code>${ClaimStatusEnum4.REFUNDABLE}</code>: The bank details have been received; the bank transfer has not been processed yet.
  - <code>${ClaimStatusEnum4.REFUNDED}</code>: The bank transfer has been processed; the travelers have received their compensation or will receive it shortly.
  - <code>${ClaimStatusEnum4.MORE_INFO_NEEDED}</code>: Additional information is needed to determine the eligibility of the travelers.
  - <code>${ClaimStatusEnum4.MANUAL_VERIFICATION_NEEDED}</code>: Claim eligibility requires manual verification or there is potential fraud; the claim requires manual verification.
  - <code>${ClaimStatusEnum4.REJECTED}</code>: Based on the information provided, the travelers are not eligible for compensation.
  - <code>${ClaimStatusEnum4.AUTOMATICALLY_REJECTED}</code>: The claim was automatically rejected following a flight status update.
  - <code>${ClaimStatusEnum4.REFUND_ERROR}</code>: The refund could not be processed because information is missing.
  - <code>${ClaimStatusEnum4.CLAIM_ERROR}</code>: The claim process failed unexpectedly; an investigation is needed on our side.`,
      example: ClaimStatusEnum4.ACCEPTED
    })
  })
});

// src/resources/bookings/confirm-booking-cancellation/confirm-booking-cancellation-req-params.schema.ts
import { extendApi as extendApi38 } from "@anatine/zod-openapi";
import { z as z181 } from "zod";
import { bookingNumberSchema } from "@repo/schema-common";
var confirmBookingCancellationReqParamsSchema = z181.strictObject({
  bookingNumber: extendApi38(bookingNumberSchema, {
    description: "The booking reference number associated with the subscription.",
    example: "5K7SXD"
  })
});

// src/resources/bookings/confirm-booking-cancellation/confirm-booking-cancellation-res-body.schema.ts
import { extendApi as extendApi39 } from "@anatine/zod-openapi";
import { z as z182 } from "zod";
import { bookingNumberSchema as bookingNumberSchema2 } from "@repo/schema-common";
var confirmBookingCancellationResBodySchema = z182.strictObject({
  data: z182.strictObject({
    bookingNumber: extendApi39(bookingNumberSchema2, {
      description: "The booking reference number associated with the subscription.",
      example: "5K7SXD"
    }),
    contracts: extendApi39(confirmBookingCancellationContractSchema.array(), {
      description: "The contracts of the subscription whose claim has been confirmed."
    })
  })
});

// src/resources/claim/create-claim/claim-distribution-context.schema.ts
import { extendApi as extendApi41 } from "@anatine/zod-openapi";
import { z as z184 } from "zod";

// src/resources/claim/create-claim/omio-claim-reconciliation.schema.ts
import { extendApi as extendApi40 } from "@anatine/zod-openapi";
import { z as z183 } from "zod";
import { priceSchema as priceSchema14 } from "@repo/schema-common";
var OMIO_CLAIM_RECONCILIATION_SOURCES = [
  "CANCELLATION_ACTUALS",
  "QUOTE_TIME_FALLBACK"
];
var OMIO_CLAIM_RECONCILIATION_FALLBACK_REASONS = [
  "NO_TICKET_REFUND_DATA",
  "OFFER_STORE_IDS_UNRECONCILED",
  "TICKET_MODIFIED",
  "PARTIAL_TICKET_DATA",
  "CURRENCY_AMBIGUOUS"
];
var omioClaimReconciliationSchema = z183.strictObject({
  source: extendApi40(z183.enum(OMIO_CLAIM_RECONCILIATION_SOURCES), {
    description: "How the refundable amount was determined: reconciled against the actual carrier refund, or fell back to the quote-time estimate."
  }),
  fallbackReason: extendApi40(z183.enum(OMIO_CLAIM_RECONCILIATION_FALLBACK_REASONS).optional(), {
    description: "Why the actual carrier refund could not be used."
  }),
  ticketPrice: extendApi40(priceSchema14.optional(), {
    description: "The price of the ticket."
  }),
  providerRefund: extendApi40(priceSchema14.optional(), {
    description: "The amount the provider refunded (actual or estimated depending on the source)."
  }),
  nonRefundableBase: extendApi40(priceSchema14.optional(), {
    description: "The portion of the ticket price the coverage applies to."
  }),
  coveredSegmentIds: extendApi40(z183.array(z183.string().min(1).max(255)).min(1).optional(), {
    description: "The segments covered by the claimed ticket, matching the <code>externalId</code> of the quote-time segments."
  })
});

// src/resources/claim/create-claim/claim-distribution-context.schema.ts
var claimDistributionContextSchema = extendApi41(z184.strictObject({
  omioClaimReconciliation: extendApi41(omioClaimReconciliationSchema.optional(), {
    description: "Omio-specific reconciliation context behind the refundable amount of the claim."
  })
}), {
  type: "object",
  hideDefinitions: [
    "omioClaimReconciliation"
  ]
});

// src/resources/claim/create-claim/claim-reasons.schema.ts
import { extendApi as extendApi42 } from "@anatine/zod-openapi";
import { z as z185 } from "zod";
import { ClaimEligibilityConditionNames as ClaimEligibilityConditionNames2, ClaimStatusEnum as ClaimStatusEnum5, FraudProductConditionNameEnum as FraudProductConditionNameEnum2, OtherProductConditionNameEnum as OtherProductConditionNameEnum2 } from "@repo/lib-koala-types";
var claimReasonsSchema = extendApi42(z185.strictObject({
  code: extendApi42(z185.nativeEnum({
    ...ClaimEligibilityConditionNames2,
    ...FraudProductConditionNameEnum2,
    ...OtherProductConditionNameEnum2
  }), {
    description: `The code of the reason.</br>
        The list of codes for which a claim is in <code>${ClaimStatusEnum5.MANUAL_VERIFICATION_NEEDED}</code> status is: [<code>${Object.values(FraudProductConditionNameEnum2).join("</code>, <code>")}</code>].`
  }),
  message: extendApi42(z185.string(), {
    description: "The details of the reason."
  })
}).array(), {
  description: "Reasons why the claim is in this status."
});

// src/resources/claim/create-claim/create-change-for-any-reason-claim.schema.ts
import { extendApi as extendApi43 } from "@anatine/zod-openapi";
import { z as z187 } from "zod";
import { ProductTypeEnum as ProductTypeEnum24 } from "@repo/lib-koala-types";
import { priceSchema as priceSchema15 } from "@repo/schema-common";

// src/resources/claim/create-claim/create-claim-base.schema.ts
import { z as z186 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema16 } from "@repo/schema-common";
var createClaimBaseSchema = z186.strictObject({
  bankDetail: bankDetailsSchema.optional(),
  claimDate: dateTimeWithUtcOffsetSchema16.optional(),
  distributionContext: claimDistributionContextSchema.optional()
});

// src/resources/claim/create-claim/create-change-for-any-reason-claim.schema.ts
var createChangeForAnyReasonClaimSchema = extendApi43(createClaimBaseSchema.extend({
  productType: extendApi43(z187.literal(ProductTypeEnum24.CHANGE_FOR_ANY_REASON), {
    description: "The type of the product.",
    example: ProductTypeEnum24.CHANGE_FOR_ANY_REASON,
    type: "string"
  }),
  modificationCost: extendApi43(priceSchema15, {
    description: "The cost of the booking modification.",
    type: "object"
  })
}), {
  title: "CreateChangeForAnyReasonClaimDataInputDto",
  hideDefinitions: [
    "claimDate"
  ],
  type: "object"
});

// src/resources/claim/create-claim/create-claim-req-body.schema.ts
import { extendApi as extendApi46 } from "@anatine/zod-openapi";
import { z as z190 } from "zod";

// src/resources/claim/create-claim/create-fdc-claim.schema.ts
import { extendApi as extendApi44 } from "@anatine/zod-openapi";
import { z as z188 } from "zod";
import { ProductTypeEnum as ProductTypeEnum25 } from "@repo/lib-koala-types";
var createFdcClaimSchema = extendApi44(createClaimBaseSchema.extend({
  productType: extendApi44(z188.literal(ProductTypeEnum25.FLIGHT_DISRUPTION).optional(), {
    description: "The type of the product.",
    example: ProductTypeEnum25.FLIGHT_DISRUPTION,
    type: "string"
  })
}), {
  title: "CreateFdcClaimDataInputDto",
  hideDefinitions: [
    "claimDate"
  ],
  type: "object"
});

// src/resources/claim/create-claim/create-withdrawal-claim.schema.ts
import { extendApi as extendApi45 } from "@anatine/zod-openapi";
import { z as z189 } from "zod";
import { ProductTypeEnum as ProductTypeEnum26 } from "@repo/lib-koala-types";
import { priceSchema as priceSchema16 } from "@repo/schema-common";
var createWithdrawalClaimSchema = extendApi45(createClaimBaseSchema.extend({
  productType: extendApi45(z189.literal(ProductTypeEnum26.WITHDRAWAL).optional(), {
    description: "The type of the product.",
    example: ProductTypeEnum26.WITHDRAWAL,
    type: "string"
  }),
  refundableAmount: extendApi45(priceSchema16.optional(), {
    description: "The refundable amount of the booking.",
    type: "object"
  }),
  cancellationFees: extendApi45(priceSchema16.extend({
    amount: z189.number().positive()
  }).optional(), {
    description: "The cancellation fees of the booking.",
    type: "object",
    example: 90
  })
}).refine((data) => !(data.refundableAmount && data.cancellationFees), {
  message: "Either refundableAmount or cancellationFees must be provided but not both."
}), {
  title: "CreateWithdrawalClaimDataInputDto",
  type: "object",
  hideDefinitions: [
    "claimDate"
  ]
});

// src/resources/claim/create-claim/create-claim-req-body.schema.ts
var createClaimReqBodySchema = extendApi46(z190.strictObject({
  data: z190.union([
    extendApi46(createChangeForAnyReasonClaimSchema, {
      type: "object"
    }),
    extendApi46(createFdcClaimSchema, {
      type: "object"
    }),
    extendApi46(createWithdrawalClaimSchema, {
      type: "object"
    })
  ])
}), {
  type: "object"
});

// src/resources/claim/common/claim-req-params.schema.ts
import { extendApi as extendApi47 } from "@anatine/zod-openapi";
import { z as z191 } from "zod";
var claimReqParamsSchema = z191.strictObject({
  id: extendApi47(z191.string().uuid(), {
    description: "Universally unique identifier (UUID) of the contract.",
    example: "94e70133-8108-4ed8-a929-8ded4113311c"
  })
});

// src/resources/claim/create-claim/create-claim-req-params.schema.ts
var createClaimReqParamsSchema = claimReqParamsSchema;

// src/resources/claim/create-claim/create-claim-req-query.schema.ts
import { extendApi as extendApi49 } from "@anatine/zod-openapi";
import { flexibleBooleanSchema as flexibleBooleanSchema4 } from "@repo/schema-common";

// src/resources/claim/common/claim-req-query.schema.ts
import { extendApi as extendApi48 } from "@anatine/zod-openapi";
import { flexibleBooleanSchema as flexibleBooleanSchema3 } from "@repo/schema-common";
var claimReqQuerySchema = extendApi48(sideEffectsSchema.extend({
  silent: flexibleBooleanSchema3.default(false)
}), {
  hideDefinitions: [
    "silent",
    "sideEffects"
  ]
});

// src/resources/claim/create-claim/create-claim-req-query.schema.ts
var createClaimReqQuerySchema = extendApi49(claimReqQuerySchema.extend({
  /*
   * `oneOf: undefined` discards the combinator generated by `flexibleBooleanSchema`, which is a Zod union.
   * @nestjs/swagger keeps combinators at the root of the parameter object instead of nesting them under
   * `schema`, which is invalid for an OpenAPI 3.0 parameter object.
   */
  dryRun: extendApi49(flexibleBooleanSchema4.default(false), {
    description: "Evaluate eligibility and compensation without creating the claim or triggering any side effect.",
    example: true,
    oneOf: void 0,
    type: "boolean"
  }),
  bypassAdditionalChecks: flexibleBooleanSchema4.default(false)
}), {
  hideDefinitions: [
    "silent",
    "sideEffects",
    "bypassAdditionalChecks"
  ]
});

// src/resources/claim/create-claim/create-claim-res-body.schema.ts
import { extendApi as extendApi51 } from "@anatine/zod-openapi";
import { DateTime as DateTime8 } from "luxon";
import { z as z193 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema17, priceSchema as priceSchema17 } from "@repo/schema-common";

// src/resources/claim/create-claim/create-claim-status.schema.ts
import { extendApi as extendApi50 } from "@anatine/zod-openapi";
import { z as z192 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum6 } from "@repo/lib-koala-types";
var createClaimStatusSchema = extendApi50(z192.nativeEnum(ClaimStatusEnum6), {
  description: `The claim status are the following:
- <code>${ClaimStatusEnum6.MORE_INFO_NEEDED}</code> Information is missing to determine the eligibility of the travelers
- <code>${ClaimStatusEnum6.MANUAL_VERIFICATION_NEEDED}</code> Fraud suspicion has been detected for this claim
- <code>${ClaimStatusEnum6.AUTOMATICALLY_REJECTED}</code> The claim was automatically rejected when a flight status was updated
- <code>${ClaimStatusEnum6.ACCEPTED}</code> Travelers eligible for compensation, we are waiting to receive their bank details
- <code>${ClaimStatusEnum6.REFUNDABLE}</code> Bank details received, the bank transfer has not been processed yet
- <code>${ClaimStatusEnum6.REFUNDED}</code> Bank transfer has been processed, the travelers have either received their compensation or will receive it shortly
- <code>${ClaimStatusEnum6.REJECTED}</code> Based on the information given, the traveler is not eligible to compensation`,
  example: `${ClaimStatusEnum6.REFUNDED}`
});

// src/resources/claim/create-claim/create-claim-res-body.schema.ts
var createClaimResBodySchema = z193.strictObject({
  data: extendApi51(z193.strictObject({
    compensation: extendApi51(priceSchema17.optional(), {
      description: "The compensation amount.",
      example: {
        amount: 100,
        currency: "EUR"
      }
    }),
    reasons: claimReasonsSchema.optional(),
    status: createClaimStatusSchema,
    lastStatusChangeDate: extendApi51(dateTimeWithUtcOffsetSchema17.optional(), {
      description: "The date when the claim status last changed.",
      example: DateTime8.utc().plus({
        months: 1
      }).startOf("hour").plus({
        hours: 8,
        minutes: 21
      }).toString()
    }),
    refundDate: extendApi51(dateTimeWithUtcOffsetSchema17.optional(), {
      description: "The date when the claim is refunded.",
      example: DateTime8.utc().plus({
        months: 1
      }).startOf("hour").plus({
        hours: 8,
        minutes: 21
      }).toString()
    })
  }), {
    title: "CreateClaimDataOutputDto"
  })
});

// src/resources/claim/create-claim-goodwill-gesture/create-claim-goodwill-gesture-req-body.schema.ts
var createClaimGoodwillGestureReqBodySchema = createClaimReqBodySchema;

// src/resources/claim/create-claim-goodwill-gesture/create-claim-goodwill-gesture-req-params.schema.ts
var createClaimGoodwillGestureReqParamsSchema = claimReqParamsSchema;

// src/resources/claim/create-claim-goodwill-gesture/create-claim-goodwill-gesture-req-query.schema.ts
var createClaimGoodwillGestureReqQuerySchema = claimReqQuerySchema;

// src/resources/claim/create-claim-goodwill-gesture/create-claim-goodwill-gesture-res-body.schema.ts
import { z as z194 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum7 } from "@repo/lib-koala-types";
var createClaimGoodwillGestureResBodySchema = z194.strictObject({
  data: z194.strictObject({
    status: z194.nativeEnum(ClaimStatusEnum7)
  })
});

// src/resources/claim/create-claim-refund/create-claim-refund-req-params.schema.ts
var createClaimRefundReqParamsSchema = claimReqParamsSchema;

// src/resources/claim/create-claim-refund/create-claim-refund-req-query.schema.ts
var createClaimRefundReqQuerySchema = claimReqQuerySchema;

// src/resources/claim/create-claim-refund/create-claim-refund-res-body.schema.ts
import { z as z195 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum8 } from "@repo/lib-koala-types";
var createClaimRefundResBodySchema = z195.strictObject({
  data: z195.strictObject({
    status: z195.nativeEnum(ClaimStatusEnum8)
  })
});

// src/resources/claim/create-claim-rejection/create-claim-rejection-req-body.schema.ts
import * as z196 from "zod";
var createClaimRejectionReqBodySchema = z196.strictObject({
  data: z196.strictObject({
    note: z196.string().optional(),
    translatedNote: z196.string().optional()
  })
});

// src/resources/claim/create-claim-rejection/create-claim-rejection-req-params.schema.ts
var createClaimRejectionReqParamsSchema = claimReqParamsSchema;

// src/resources/claim/create-claim-rejection/create-claim-rejection-req-query.schema.ts
var createClaimRejectionReqQuerySchema = claimReqQuerySchema;

// src/resources/claim/create-claim-rejection/create-claim-rejection-res-body.schema.ts
import { z as z197 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum9 } from "@repo/lib-koala-types";
var createClaimRejectionResBodySchema = z197.strictObject({
  data: z197.strictObject({
    status: z197.nativeEnum(ClaimStatusEnum9)
  })
});

// src/resources/claim/create-claim-verification/create-claim-verification-req-body.schema.ts
import * as z198 from "zod";
import { priceSchema as priceSchema18 } from "@repo/schema-common";
var createClaimVerificationReqBodySchema = z198.strictObject({
  data: z198.strictObject({
    cancellationFees: priceSchema18.extend({
      amount: z198.number().positive()
    }).optional()
  }).optional()
});

// src/resources/claim/create-claim-verification/create-claim-verification-req-params.schema.ts
var createClaimVerificationReqParamsSchema = claimReqParamsSchema;

// src/resources/claim/create-claim-verification/create-claim-verification-req-query.schema.ts
var createClaimVerificationReqQuerySchema = claimReqQuerySchema;

// src/resources/claim/create-claim-verification/create-claim-verification-res-body.schema.ts
import { z as z199 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum10 } from "@repo/lib-koala-types";
var createClaimVerificationResBodySchema = z199.strictObject({
  data: z199.strictObject({
    status: z199.nativeEnum(ClaimStatusEnum10)
  })
});

// src/resources/claim/delete-claim/delete-claim-req-params.schema.ts
import { z as z200 } from "zod";
var deleteClaimReqParamsSchema = z200.strictObject({
  id: z200.string().uuid()
});

// src/resources/claim/delete-claim/delete-claim-res-body.schema.ts
import { z as z201 } from "zod";
var deleteClaimResBodySchema = z201.strictObject({});

// src/resources/contract/delete-contract/delete-contract-params-body.schema.ts
import { extendApi as extendApi52 } from "@anatine/zod-openapi";
import { z as z202 } from "zod";
var deleteContractReqParamsSchema = z202.strictObject({
  id: extendApi52(z202.string().uuid(), {
    description: "Universally unique identifier (UUID) of the contract.",
    example: "94e70133-8108-4ed8-a929-8ded4113311c"
  })
});

// src/resources/contract/delete-contract/delete-contract-req-body.schema.ts
import { z as z203 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema18 } from "@repo/schema-common";
var deleteContractReqBodySchema = z203.strictObject({
  data: z203.strictObject({
    renouncedAt: dateTimeWithUtcOffsetSchema18.optional()
  }).optional()
});

// src/resources/contract/delete-documents/delete-stale-contract-documents-res-body.schema.ts
import { z as z204 } from "zod";
var deleteStaleContractDocumentsResBodySchema = z204.strictObject({
  data: z204.strictObject({
    deletedCount: z204.number().int().min(0)
  })
});

// src/resources/contract/find-one-contract/find-one-contract-req-params.schema.ts
import { extendApi as extendApi53 } from "@anatine/zod-openapi";
import { z as z205 } from "zod";
var findOneContractReqParamsSchema = z205.strictObject({
  id: extendApi53(z205.string().uuid(), {
    description: "Universally unique identifier (UUID) of the contract.",
    example: "94e70133-8108-4ed8-a929-8ded4113311c"
  })
});

// src/resources/contract/find-one-contract/find-one-contract-res-body.schema.ts
import { extendApi as extendApi54 } from "@anatine/zod-openapi";
import { z as z206 } from "zod";
var findOneContractResBodySchema = z206.strictObject({
  data: z206.strictObject({
    id: extendApi54(z206.string().uuid().min(1), {
      description: "Universally unique identifier (UUID) of the contract.",
      example: "94e70133-8108-4ed8-a929-8ded4113311c"
    }),
    claim: claimDataSchema.optional(),
    customer: extendApi54(z206.strictObject({
      firstName: extendApi54(z206.string(), {
        description: "First name of the customer."
      }),
      lastName: extendApi54(z206.string(), {
        description: "Last name of the customer."
      })
    })),
    documents: extendApi54(z206.array(productAttachmentSchema), {
      description: "List of the documents related to the contract."
    })
  })
});

// src/resources/create-flight-monitoring/create-flight-monitoring-req-query.schema.ts
import { z as z207 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema19 } from "@repo/schema-common";
var createFlightMonitoringReqQuerySchema = z207.strictObject({
  ignoreFlightUpdatedAfter: dateTimeWithUtcOffsetSchema19.optional()
});

// src/resources/create-flight-monitoring/create-flight-monitoring-res-body.schema.ts
import { z as z208 } from "zod";
var createFlightMonitoringResBodySchema = z208.strictObject({
  monitoredFlightsCount: z208.number().min(0),
  updatedFlightsCount: z208.number().min(0),
  backlogCount: z208.number().min(0)
});

// src/resources/create-token/create-account-token-req-body.schema.ts
import { z as z209 } from "zod";
var createAccountTokenReqBodySchema = z209.strictObject({
  data: z209.strictObject({
    accountId: z209.string()
  })
});

// src/resources/create-token/create-account-token-res-body.schema.ts
import { z as z210 } from "zod";
var createAccountTokenResBodySchema = z210.strictObject({
  data: z210.strictObject({
    token: z210.string()
  })
});

// src/resources/create-token/create-subscribed-customer-token-req-body.schema.ts
import { z as z211 } from "zod";
import { emailSchema as emailSchema3 } from "@repo/schema-common";
var subscribedCustomerTokenBaseSchema = z211.strictObject({
  bookingNumber: z211.string().min(1),
  challengeToken: z211.string().optional(),
  validityHours: z211.number().int().min(1).max(336).optional()
});
var subscribedCustomerTokenByEmailSchema = subscribedCustomerTokenBaseSchema.extend({
  email: emailSchema3
});
var subscribedCustomerTokenByLastNameSchema = subscribedCustomerTokenBaseSchema.extend({
  lastName: z211.string().trim().min(1)
});
var createSubscribedCustomerTokenReqBodySchema = z211.strictObject({
  data: z211.union([
    subscribedCustomerTokenByEmailSchema,
    subscribedCustomerTokenByLastNameSchema
  ])
});

// src/resources/create-token/create-subscribed-customer-token-req-body-dto.mapper.ts
import { emailSchema as emailSchema4 } from "@repo/schema-common";
function isEmail(value) {
  return emailSchema4.safeParse(value).success;
}
__name(isEmail, "isEmail");
(function(CreateSubscribedCustomerTokenReqBodyDtoMapper2) {
  function fromLoginFormData(data) {
    const identifier = data.identifier?.trim() ?? "";
    const bookingNumber = data.bookingNumber ?? "";
    const challengeTokenPayload = data.challengeToken ? {
      challengeToken: data.challengeToken
    } : {};
    if (isEmail(identifier)) {
      return {
        bookingNumber,
        email: identifier,
        ...challengeTokenPayload
      };
    }
    return {
      bookingNumber,
      lastName: identifier,
      ...challengeTokenPayload
    };
  }
  __name(fromLoginFormData, "fromLoginFormData");
  CreateSubscribedCustomerTokenReqBodyDtoMapper2.fromLoginFormData = fromLoginFormData;
})(CreateSubscribedCustomerTokenReqBodyDtoMapper || (CreateSubscribedCustomerTokenReqBodyDtoMapper = {}));
var CreateSubscribedCustomerTokenReqBodyDtoMapper;

// src/resources/create-token/create-subscribed-customer-token-res-body.schema.ts
import { z as z212 } from "zod";
var createSubscribedCustomerTokenResBodySchema = z212.strictObject({
  data: z212.strictObject({
    token: z212.string()
  })
});

// src/resources/customer/create-customer-contract-claim/baggage-delay-disruption.schema.ts
import { z as z213 } from "zod";
import { BaggageDisruptionTypes } from "@repo/lib-koala-types";
import { pirReferenceSchema } from "@repo/schema-common";
var baggageDelayDisruptionSchema = z213.strictObject({
  id: z213.string().uuid(),
  travelerId: z213.string().uuid(),
  documentId: z213.string().uuid(),
  type: z213.literal(BaggageDisruptionTypes.BAGGAGE_DELAY),
  pirReference: pirReferenceSchema,
  numberOfAffectedBags: z213.number().int().positive()
});

// src/resources/customer/create-customer-contract-claim/baggage-loss-disruption.schema.ts
import { z as z214 } from "zod";
import { BaggageDisruptionTypes as BaggageDisruptionTypes2 } from "@repo/lib-koala-types";
import { pirReferenceSchema as pirReferenceSchema2 } from "@repo/schema-common";
var baggageLossDisruptionSchema = z214.strictObject({
  id: z214.string().uuid(),
  travelerId: z214.string().uuid(),
  documentId: z214.string().uuid(),
  type: z214.literal(BaggageDisruptionTypes2.BAGGAGE_LOSS),
  pirReference: pirReferenceSchema2,
  numberOfAffectedBags: z214.number().int().positive()
});

// src/resources/customer/create-customer-contract-claim/bdc-disruption.schema.ts
import { z as z215 } from "zod";
var bdcDisruptionSchema = z215.discriminatedUnion("type", [
  baggageLossDisruptionSchema,
  baggageDelayDisruptionSchema
]);

// src/resources/customer/create-customer-contract-claim/create-customer-contract-claim-bdc-req-body-data.schema.ts
import { z as z216 } from "zod";
import { ProductTypeEnum as ProductTypeEnum27 } from "@repo/lib-koala-types";
var createCustomerContractClaimBdcReqBodyDataSchema = z216.strictObject({
  productType: z216.literal(ProductTypeEnum27.BAGGAGE_DISRUPTION),
  bankDetail: bankDetailsSchema.optional(),
  disruptions: z216.array(bdcDisruptionSchema).min(1)
});

// src/resources/customer/create-customer-contract-claim/create-customer-contract-claim-cwp-req-body-data.schema.ts
import { z as z217 } from "zod";
import { CwpCancellationReasons, ProductTypeEnum as ProductTypeEnum28 } from "@repo/lib-koala-types";
var createCustomerContractClaimCwpReqBodyDataSchema = z217.strictObject({
  productType: z217.literal(ProductTypeEnum28.CANCELLATION_WITHOUT_PROOF),
  bankDetail: bankDetailsSchema.optional(),
  cancellationReason: z217.nativeEnum(CwpCancellationReasons),
  cancellationReasonMessage: z217.string().min(1).max(255).optional()
});

// src/resources/customer/create-customer-contract-claim/create-customer-contract-claim-req-body.schema.ts
import { z as z219 } from "zod";
import { CwpCancellationReasons as CwpCancellationReasons2, ProductTypeEnum as ProductTypeEnum29 } from "@repo/lib-koala-types";

// src/resources/customer/create-customer-contract-claim/create-customer-contract-claim-generic-req-body-data.schema.ts
import { z as z218 } from "zod";
var createCustomerContractClaimGenericReqBodyDataSchema = z218.strictObject({
  productType: z218.literal("UNKNOWN").optional().default("UNKNOWN"),
  bankDetail: bankDetailsSchema.optional()
});

// src/resources/customer/create-customer-contract-claim/create-customer-contract-claim-req-body.schema.ts
var createCustomerContractClaimReqBodySchema = z219.strictObject({
  data: z219.discriminatedUnion("productType", [
    createCustomerContractClaimGenericReqBodyDataSchema,
    createCustomerContractClaimBdcReqBodyDataSchema,
    createCustomerContractClaimCwpReqBodyDataSchema
  ]).optional()
}).refine((body) => {
  if (body.data?.productType !== ProductTypeEnum29.CANCELLATION_WITHOUT_PROOF) {
    return true;
  }
  if (body.data.cancellationReason !== CwpCancellationReasons2.OTHER) {
    return true;
  }
  return body.data.cancellationReasonMessage !== void 0;
}, {
  message: "cancellationReasonMessage is required when cancellationReason is OTHER",
  path: [
    "data",
    "cancellationReasonMessage"
  ]
});

// src/resources/customer/create-customer-contract-claim/create-customer-contract-claim-req-params.schema.ts
import { extendApi as extendApi55 } from "@anatine/zod-openapi";
import { z as z220 } from "zod";
var createCustomerContractClaimReqParamsSchema = z220.strictObject({
  id: extendApi55(z220.string().uuid(), {
    description: "Universally unique identifier (UUID) of the contract.",
    example: "94e70133-8108-4ed8-a929-8ded4113311c"
  })
});

// src/resources/customer/create-customer-contract-claim/create-customer-contract-claim-res-body.schema.ts
import { z as z221 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum11 } from "@repo/lib-koala-types";
var createCustomerContractClaimResBodySchema = z221.object({
  data: z221.object({
    status: z221.nativeEnum(ClaimStatusEnum11)
  })
});

// src/resources/customer/create-customer-contract-document/create-customer-contract-document-req-body.schema.ts
import { z as z222 } from "zod";
var createCustomerContractDocumentReqBodySchema = z222.strictObject({
  data: contractDocumentSchema.pick({
    type: true,
    originalDocumentName: true
  })
});

// src/resources/customer/create-customer-contract-document/create-customer-contract-document-req-params.schema.ts
import { z as z223 } from "zod";
var createCustomerContractDocumentReqParamsSchema = z223.strictObject({
  id: z223.string().uuid().describe("The ID of the contract")
});

// src/resources/customer/create-customer-contract-document/create-customer-contract-document-res-body.schema.ts
import { z as z224 } from "zod";
var createCustomerContractDocumentResBodySchema = z224.strictObject({
  data: z224.object({
    uploadUrl: z224.string().url(),
    document: contractDocumentSchema
  })
});

// src/resources/customer/create-customer-subscription-bank-details/create-customer-subscription-bank-details-req-body.schema.ts
import { z as z226 } from "zod";

// src/resources/wise-recipients/wise-recipient-account-data.schema.ts
import { z as z225 } from "zod";
var wiseRecipientAccountDataSchema = z225.record(z225.unknown());

// src/resources/customer/create-customer-subscription-bank-details/create-customer-subscription-bank-details-req-body.schema.ts
var createCustomerSubscriptionBankDetailsReqBodySchema = z226.discriminatedUnion("type", [
  z226.strictObject({
    type: z226.literal("wise"),
    accountData: wiseRecipientAccountDataSchema
  }),
  z226.strictObject({
    type: z226.literal("legacy"),
    bankDetails: bankDetailsSchema
  })
]);

// src/resources/customer/create-customer-subscription-bank-details/create-customer-subscription-bank-details-req-params.schema.ts
import { z as z227 } from "zod";
var createCustomerSubscriptionBankDetailsReqParamsSchema = z227.strictObject({
  id: z227.string().uuid()
});

// src/resources/customer/create-customer-subscription-bank-details/create-customer-subscription-bank-details-res-body.schema.ts
import { z as z228 } from "zod";
var createCustomerSubscriptionBankDetailsResBodySchema = z228.strictObject({
  data: z228.strictObject({
    bankDetailId: z228.string().uuid(),
    wiseRecipientAccountId: z228.number().int().optional()
  })
});

// src/resources/customer/delete-customer-contract-document/delete-customer-contract-document-req-params.schema.ts
import { z as z229 } from "zod";
var deleteCustomerContractDocumentReqParamsSchema = z229.strictObject({
  id: z229.string().uuid(),
  documentId: z229.string().uuid()
});

// src/resources/customer/get-customer-contract-document/get-customer-contract-document-req-params.schema.ts
import { z as z230 } from "zod";
var getCustomerContractDocumentReqParamsSchema = z230.strictObject({
  id: z230.string().uuid().describe("The ID of the contract"),
  documentId: z230.string().uuid().describe("The ID of the document associated with the contract")
});

// src/resources/customer/get-customer-contract-document/get-customer-contract-document-res-body.schema.ts
import { z as z231 } from "zod";
var getCustomerContractDocumentResBodySchema = z231.strictObject({
  data: contractDocumentSchema
});

// src/resources/customer/get-customer-contract-latest-claim/get-customer-contract-latest-claim-req-params.schema.ts
import { z as z232 } from "zod";
var getCustomerContractLatestClaimReqParamsSchema = z232.strictObject({
  id: z232.string().uuid()
});

// src/resources/customer/get-customer-contract-latest-claim/get-customer-contract-latest-claim-res-body.schema.ts
import { z as z233 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum12 } from "@repo/lib-koala-types";
import { claimRejectedReasonsSchema } from "@repo/schema-koala";
var rejectedClaimSchema = z233.object({
  status: z233.literal(ClaimStatusEnum12.REJECTED),
  reasons: claimRejectedReasonsSchema
});
var otherClaimStatuses = Object.values(ClaimStatusEnum12).filter((status) => status !== ClaimStatusEnum12.REJECTED);
var otherClaimStatusSchemas = otherClaimStatuses.map((status) => z233.object({
  status: z233.literal(status)
}));
var getCustomerContractLatestClaimResBodySchema = z233.object({
  data: z233.discriminatedUnion("status", [
    rejectedClaimSchema,
    ...otherClaimStatusSchemas
  ])
});

// src/resources/customer/get-customer-contract-wise-account-requirements/get-customer-contract-wise-account-requirements-req-params.schema.ts
import { z as z234 } from "zod";
var getCustomerContractWiseAccountRequirementsReqParamsSchema = z234.strictObject({
  id: z234.string().uuid()
});

// src/resources/customer/get-customer-contract-wise-account-requirements/get-customer-contract-wise-account-requirements-res-body.schema.ts
import { z as z235 } from "zod";
var getCustomerContractWiseAccountRequirementsResBodySchema = z235.object({
  data: z235.object({
    quoteId: z235.string().uuid(),
    accountRequirements: z235.array(wiseAccountRequirementSchema),
    forbiddenCountryIds: z235.array(z235.string().length(2))
  })
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body.schema.ts
import { z as z254 } from "zod";

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-contract.schema.ts
import { z as z249 } from "zod";
import { BankDetailsTypeEnum as BankDetailsTypeEnum3, ProductTypeEnum as ProductTypeEnum30 } from "@repo/lib-koala-types";

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-contract-claim.schema.ts
import { z as z236 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum13 } from "@repo/lib-koala";
import { priceSchema as priceSchema19 } from "@repo/schema-common";
var getCustomerSubscriptionResBodyContractClaimSchema = z236.object({
  deadline: z236.string().optional(),
  maximumExpectedCompensation: priceSchema19.optional(),
  status: z236.nativeEnum(ClaimStatusEnum13).optional(),
  lastStatusChangeDate: z236.string().optional(),
  refundDate: z236.string().optional(),
  compensation: priceSchema19.optional()
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-covered-event.schema.ts
import { z as z248 } from "zod";

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-flight-covered-event.schema.ts
import { z as z238 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum21 } from "@repo/lib-koala-types";

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-flight.schema.ts
import { z as z237 } from "zod";
import { airlineIataSchema as airlineIataSchema2, flightNumberSchema as flightNumberSchema2, iataSchema as iataSchema2, priceSchema as priceSchema20 } from "@repo/schema-common";
var getCustomerSubscriptionResBodyFlightSchema = z237.object({
  airlineIata: airlineIataSchema2,
  arrivalAirportIata: iataSchema2,
  arrivalAirportName: z237.string(),
  departureAirportIata: iataSchema2,
  departureAirportName: z237.string(),
  number: flightNumberSchema2,
  price: priceSchema20.optional(),
  arrivalDate: z237.string(),
  departureDate: z237.string()
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-flight-covered-event.schema.ts
var getCustomerSubscriptionResBodyFlightCoveredEventSchema = getCustomerSubscriptionResBodyFlightSchema.extend({
  coveredEventType: z238.literal(CoveredEventTypeEnum21.FLIGHT)
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-trip-covered-event.schema.ts
import { z as z247 } from "zod";
import { CoveredEventTypeEnum as CoveredEventTypeEnum22 } from "@repo/lib-koala-types";

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-trip.schema.ts
import { z as z246 } from "zod";
import { priceSchema as priceSchema24 } from "@repo/schema-common";

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-journey.schema.ts
import { z as z243 } from "zod";
import { priceSchema as priceSchema22 } from "@repo/schema-common";

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-segment.schema.ts
import { z as z242 } from "zod";

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-flight-segment.schema.ts
import { z as z239 } from "zod";
var flightModeSchema = z239.literal("FLIGHT");
var getCustomerSubscriptionResBodyFlightSegmentSchema = getCustomerSubscriptionResBodyFlightSchema.extend({
  arrivalDate: z239.object({
    local: z239.string()
  }),
  departureDate: z239.object({
    local: z239.string()
  }),
  mode: flightModeSchema
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-generic-segment.schema.ts
import { z as z241 } from "zod";
import { RouteModeEnum as RouteModeEnum3 } from "@repo/lib-koala-types";
import { priceSchema as priceSchema21 } from "@repo/schema-common";

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-generic-segment-stop.schema.ts
import { IANAZone } from "luxon";
import { z as z240 } from "zod";
import { countryCodeSchema as countryCodeSchema9, latitudeSchema as latitudeSchema2, longitudeSchema as longitudeSchema2, uicSchema as uicSchema2 } from "@repo/schema-common";
var getCustomerSubscriptionResBodyGenericSegmentStopSchema = z240.object({
  countryCode: countryCodeSchema9,
  externalId: z240.string().nonempty(),
  latitude: latitudeSchema2.optional(),
  longitude: longitudeSchema2.optional(),
  name: z240.string().min(1),
  timezone: z240.string().min(1).refine((timezone) => IANAZone.isValidZone(timezone)),
  uic: uicSchema2.optional()
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-generic-segment.schema.ts
var genericModeSchema = z241.enum([
  RouteModeEnum3.BUS,
  RouteModeEnum3.FERRY,
  RouteModeEnum3.TRAIN
]);
var getCustomerSubscriptionResBodyGenericSegmentSchema = z241.object({
  arrivalDate: z241.object({
    local: z241.string()
  }),
  arrivalStop: getCustomerSubscriptionResBodyGenericSegmentStopSchema,
  departureDate: z241.object({
    local: z241.string()
  }),
  departureStop: getCustomerSubscriptionResBodyGenericSegmentStopSchema,
  externalId: z241.string().min(1),
  mode: genericModeSchema,
  price: priceSchema21.optional(),
  transportIdentifier: z241.string().max(255)
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-segment.schema.ts
var getCustomerSubscriptionResBodySegmentSchema = z242.discriminatedUnion("mode", [
  getCustomerSubscriptionResBodyGenericSegmentSchema,
  getCustomerSubscriptionResBodyFlightSegmentSchema
]);

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-journey.schema.ts
var getCustomerSubscriptionResBodyJourneySchema = z243.object({
  flights: z243.array(getCustomerSubscriptionResBodyFlightSchema),
  segments: z243.array(getCustomerSubscriptionResBodySegmentSchema),
  price: priceSchema22.optional()
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-stay.schema.ts
import { z as z245 } from "zod";
import { priceSchema as priceSchema23 } from "@repo/schema-common";

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-address.schema.ts
import { z as z244 } from "zod";
import { countryCodeSchema as countryCodeSchema10 } from "@repo/schema-common";
var countryOnlyAddressSchema2 = z244.object({
  countryCode: countryCodeSchema10
});
var richAddressSchema2 = countryOnlyAddressSchema2.extend({
  line1: z244.string().min(1),
  line2: z244.string().min(1).optional(),
  line3: z244.string().min(1).optional(),
  line4: z244.string().min(1).optional(),
  locality: z244.string().min(1),
  postcode: z244.string().min(1),
  region: z244.string().min(1).optional()
});
var getCustomerSubscriptionResBodyAddressSchema = z244.union([
  countryOnlyAddressSchema2,
  richAddressSchema2
]);

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-stay.schema.ts
var getCustomerSubscriptionResBodyStaySchema = z245.object({
  address: getCustomerSubscriptionResBodyAddressSchema,
  endDate: z245.string(),
  name: z245.string().nonempty().max(255),
  price: priceSchema23.optional(),
  startDate: z245.string()
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-trip.schema.ts
var getCustomerSubscriptionResBodyTripSchema = z246.object({
  journeys: z246.array(getCustomerSubscriptionResBodyJourneySchema),
  stays: z246.array(getCustomerSubscriptionResBodyStaySchema),
  price: priceSchema24.optional()
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-trip-covered-event.schema.ts
var getCustomerSubscriptionResBodyTripCoveredEventSchema = getCustomerSubscriptionResBodyTripSchema.extend({
  coveredEventType: z247.literal(CoveredEventTypeEnum22.TRIP)
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-covered-event.schema.ts
var getCustomerSubscriptionResBodyCoveredEventSchema = z248.discriminatedUnion("coveredEventType", [
  getCustomerSubscriptionResBodyTripCoveredEventSchema,
  getCustomerSubscriptionResBodyFlightCoveredEventSchema
]);

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-contract.schema.ts
var productSchema2 = z249.discriminatedUnion("type", [
  z249.object({
    type: z249.literal(ProductTypeEnum30.COMPREHENSIVE)
  }),
  z249.object({
    type: z249.literal(ProductTypeEnum30.WITHDRAWAL),
    bankDetailsType: z249.nativeEnum(BankDetailsTypeEnum3),
    requireCancellationProof: z249.boolean()
  }),
  z249.object({
    type: z249.literal(ProductTypeEnum30.FLIGHT_DISRUPTION),
    bankDetailsType: z249.nativeEnum(BankDetailsTypeEnum3)
  }),
  z249.object({
    type: z249.literal(ProductTypeEnum30.CHANGE_FOR_ANY_REASON),
    bankDetailsType: z249.nativeEnum(BankDetailsTypeEnum3)
  }),
  z249.object({
    type: z249.literal(ProductTypeEnum30.BAGGAGE_DISRUPTION),
    bankDetailsType: z249.nativeEnum(BankDetailsTypeEnum3),
    compensationSettings: z249.array(compensationSettingSchema)
  }),
  z249.object({
    type: z249.literal(ProductTypeEnum30.CANCELLATION_WITHOUT_PROOF),
    bankDetailsType: z249.nativeEnum(BankDetailsTypeEnum3)
  })
]);
var getCustomerSubscriptionResBodyContractSchema = z249.object({
  id: z249.string().uuid(),
  product: productSchema2,
  coveredEvent: getCustomerSubscriptionResBodyCoveredEventSchema,
  hasClaim: z249.boolean(),
  claim: getCustomerSubscriptionResBodyContractClaimSchema,
  documentCount: z249.number().int().min(0)
});

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body-travelers.schema.ts
import { z as z250 } from "zod";
import { AgeRangeEnum as AgeRangeEnum4 } from "@repo/lib-koala-types";
import { countryCodeSchema as countryCodeSchema11 } from "@repo/schema-common";
var getCustomerSubscriptionResBodyTravelersSchema = z250.object({
  id: z250.string().uuid(),
  firstName: z250.string().min(1).max(255),
  lastName: z250.string().min(1).max(255),
  ageRange: z250.nativeEnum(AgeRangeEnum4).default(AgeRangeEnum4.ADULT),
  residenceCountryCode: countryCodeSchema11.optional(),
  birthday: birthdaySchema.optional(),
  address: richAddressSchema.optional(),
  numberOfCheckedBags: z250.number().int().positive().optional()
});

// src/resources/customer/get-customer-subscription/payment-info.schema.ts
import { z as z253 } from "zod";

// src/resources/customer/get-customer-subscription/payment-info-merchant-of-record.schema.ts
import { z as z251 } from "zod";
import { SubscriptionPaymentStatuses } from "@repo/lib-koala-types";
var paymentInfoMerchantOfRecordSchema = z251.object({
  isKoalaMerchantOfRecord: z251.literal(true),
  status: z251.nativeEnum(SubscriptionPaymentStatuses).optional(),
  lastUpdatedAt: z251.string().optional(),
  price: z251.object({
    amount: z251.number(),
    currency: z251.string()
  })
});

// src/resources/customer/get-customer-subscription/payment-info-not-merchant-of-record.schema.ts
import { z as z252 } from "zod";
var paymentInfoNotMerchantOfRecordSchema = z252.object({
  isKoalaMerchantOfRecord: z252.literal(false)
});

// src/resources/customer/get-customer-subscription/payment-info.schema.ts
var paymentInfoSchema = z253.discriminatedUnion("isKoalaMerchantOfRecord", [
  paymentInfoNotMerchantOfRecordSchema,
  paymentInfoMerchantOfRecordSchema
]);

// src/resources/customer/get-customer-subscription/get-customer-subscription-res-body.schema.ts
var getCustomerSubscriptionResBodySchema = z254.object({
  data: z254.object({
    id: z254.string().uuid(),
    bookingNumber: z254.string(),
    contracts: z254.array(getCustomerSubscriptionResBodyContractSchema),
    travelers: z254.array(getCustomerSubscriptionResBodyTravelersSchema),
    customer: z254.object({
      firstName: z254.string(),
      lastName: z254.string()
    }),
    partner: partnerSchema.pick({
      name: true,
      isKoalaMerchantOfRecord: true
    }),
    paymentInfo: paymentInfoSchema,
    maxDocumentsPerContract: z254.number().int().min(1)
  })
});

// src/resources/dashboard/eur-amount.schema.ts
import { extendApi as extendApi56 } from "@anatine/zod-openapi";
import { z as z255 } from "zod";
var eurAmountSchema = extendApi56(z255.number().finite(), {
  description: "Amount in EUR, rounded to 2 decimals.",
  example: 1234.56
});

// src/resources/dashboard/get-dashboard-reference-res-body.schema.ts
import { extendApi as extendApi57 } from "@anatine/zod-openapi";
import { z as z256 } from "zod";
var DASHBOARD_COMMISSION_CURVE_STEP_MINUTES = 15;
var DASHBOARD_COMMISSION_CURVE_POINTS_COUNT = 24 * 60 / DASHBOARD_COMMISSION_CURVE_STEP_MINUTES;
var getDashboardReferenceResBodySchema = z256.strictObject({
  data: extendApi57(z256.strictObject({
    bestCommissionDay: extendApi57(z256.strictObject({
      date: extendApi57(z256.string().date(), {
        description: "The day (Europe/Paris, YYYY-MM-DD) with the highest Koala commission sum ever.",
        example: "2025-07-14"
      }),
      koalaCommissionSum: extendApi57(eurAmountSchema, {
        description: "Koala commission sum of that day, in EUR. Renounced and replaced contracts are excluded."
      })
    }), {
      title: "DashboardBestCommissionDayOutputDto"
    }),
    yesterday: extendApi57(z256.strictObject({
      date: extendApi57(z256.string().date(), {
        description: "Yesterday, in Europe/Paris time (YYYY-MM-DD).",
        example: "2026-08-25"
      }),
      koalaCommissionSum: extendApi57(eurAmountSchema, {
        description: "Yesterday's total Koala commission, in EUR."
      }),
      koalaCommissionCurve: extendApi57(z256.array(eurAmountSchema).length(DASHBOARD_COMMISSION_CURVE_POINTS_COUNT), {
        description: `Yesterday's cumulative Koala commission in EUR, sampled every ${DASHBOARD_COMMISSION_CURVE_STEP_MINUTES} minutes in Europe/Paris time. Index <code>i</code> is the cumulative commission of contracts created before the end of the <code>i</code>-th ${DASHBOARD_COMMISSION_CURVE_STEP_MINUTES}-minute bucket (index 0 covers 00:00\u201300:15, index ${DASHBOARD_COMMISSION_CURVE_POINTS_COUNT - 1} covers 23:45\u201324:00). The last point equals the total. Renounced and replaced contracts are excluded. On DST-change days the wall clock drives the buckets: spring-forward leaves the 02:00\u201303:00 buckets empty, fall-back folds the repeated 02:00\u201303:00 hour into the same four buckets; the total stays correct.`
      })
    }), {
      title: "DashboardYesterdayOutputDto"
    })
  }), {
    title: "DashboardReferenceOutputDto"
  })
});

// src/resources/dashboard/get-dashboard-snapshot-res-body.schema.ts
import { extendApi as extendApi58 } from "@anatine/zod-openapi";
import { z as z257 } from "zod";
import { ProductTypeEnum as ProductTypeEnum31 } from "@repo/lib-koala-types";
var productContractsCountSchema = z257.number().int().nonnegative();
var contractsCountByProductTypeShape = {
  [ProductTypeEnum31.BAGGAGE_DISRUPTION]: productContractsCountSchema,
  [ProductTypeEnum31.CANCELLATION_WITHOUT_PROOF]: productContractsCountSchema,
  [ProductTypeEnum31.CHANGE_FOR_ANY_REASON]: productContractsCountSchema,
  [ProductTypeEnum31.COMPREHENSIVE]: productContractsCountSchema,
  [ProductTypeEnum31.FLIGHT_DISRUPTION]: productContractsCountSchema,
  [ProductTypeEnum31.WITHDRAWAL]: productContractsCountSchema
};
var activitySubscriptionsCountSchema = extendApi58(z257.number().int().nonnegative(), {
  description: "Number of subscriptions created today with at least one active contract of this activity.",
  example: 12
});
var activityKoalaCommissionSumSchema = extendApi58(eurAmountSchema, {
  description: "Sum of Koala commissions of the active contracts of this activity created today, in EUR."
});
var partnerActivitySchema = z257.strictObject({
  partnerName: extendApi58(z257.string(), {
    example: "Volotea"
  }),
  subscriptionsCount: activitySubscriptionsCountSchema,
  koalaCommissionSum: activityKoalaCommissionSumSchema
});
var productTypeActivitySchema = z257.strictObject({
  productType: z257.nativeEnum(ProductTypeEnum31),
  subscriptionsCount: activitySubscriptionsCountSchema,
  koalaCommissionSum: activityKoalaCommissionSumSchema
});
var getDashboardSnapshotResBodySchema = z257.strictObject({
  data: extendApi58(z257.strictObject({
    date: extendApi58(z257.string().date(), {
      description: "The day the figures are aggregated over, in Europe/Paris time (YYYY-MM-DD).",
      example: "2026-08-26"
    }),
    subscriptionsCount: extendApi58(z257.number().int().nonnegative(), {
      description: "Number of subscriptions with an active contract created today. A multi-contract subscription counts once. Renounced and replaced contracts are excluded from every figure of this snapshot; non-EUR amounts are converted with the exchange rate of the contract creation day.",
      example: 42
    }),
    claimsCount: extendApi58(z257.number().int().nonnegative(), {
      description: "Number of claims created today whose current status is not REJECTED, AUTOMATICALLY_REJECTED or CLAIM_ERROR.",
      example: 3
    }),
    retailSum: extendApi58(eurAmountSchema, {
      description: "Sum of retail prices (taxes included) of contracts created today, in EUR."
    }),
    grossNetWrittenPremiumSum: extendApi58(eurAmountSchema, {
      description: "Sum of gross net written premiums of contracts created today, in EUR."
    }),
    koalaCommissionSum: extendApi58(eurAmountSchema, {
      description: "Sum of Koala commissions of contracts created today, in EUR."
    }),
    biggestSubscriptionRetail: extendApi58(eurAmountSchema, {
      description: "Retail price (taxes included) of the biggest subscription created today, in EUR. Contracts of a same subscription are summed. 0 when there is no subscription yet."
    }),
    biggestSubscriptionCommission: extendApi58(eurAmountSchema, {
      description: "Koala commission of the subscription with the biggest commission created today, in EUR. Contracts of a same subscription are summed. 0 when there is no subscription yet."
    }),
    contractsCountByProductType: extendApi58(z257.strictObject(contractsCountByProductTypeShape), {
      description: "Number of active contracts created today, per product type. Every contract of a subscription counts, so the counts can add up to more than subscriptionsCount."
    }),
    partnersActivity: extendApi58(z257.array(partnerActivitySchema), {
      description: "Koala commission and subscriptions count of today, for every partner with at least one active contract created today, sorted by descending commission."
    }),
    productTypesActivity: extendApi58(z257.array(productTypeActivitySchema), {
      description: "Koala commission and subscriptions count of today, for every product type with at least one active contract created today, sorted by descending commission. A subscription counts once per product type it holds, so the counts can add up to more than subscriptionsCount."
    })
  }), {
    title: "DashboardSnapshotOutputDto"
  })
});

// src/resources/documents/get-document/get-document-req-params.schema.ts
import { z as z258 } from "zod";
var getDocumentReqParamsSchema = z258.strictObject({
  documentId: z258.string().uuid()
});

// src/resources/documents/get-document/get-document-res-body.schema.ts
import { z as z259 } from "zod";
import { ContractDocumentUploadStatuses as ContractDocumentUploadStatuses2 } from "@repo/lib-koala";
var contractDocumentWithDownloadUrlSchema = contractDocumentSchema.extend({
  uploadStatus: z259.literal(ContractDocumentUploadStatuses2.COMPLETED),
  downloadUrl: z259.string().url()
});
var contractDocumentWithoutDownloadUrlSchema = contractDocumentSchema.extend({
  uploadStatus: z259.enum([
    ContractDocumentUploadStatuses2.REQUESTED,
    ContractDocumentUploadStatuses2.FAILED
  ])
});
var getDocumentResBodySchema = z259.discriminatedUnion("uploadStatus", [
  contractDocumentWithDownloadUrlSchema,
  contractDocumentWithoutDownloadUrlSchema
]);

// src/resources/documents/replace-document/replace-document-req-body.schema.ts
import { z as z260 } from "zod";
var replaceDocumentReqBodySchema = z260.strictObject({
  data: contractDocumentSchema.pick({
    originalDocumentName: true
  })
});

// src/resources/documents/replace-document/replace-document-req-params.schema.ts
import { z as z261 } from "zod";
var replaceDocumentReqParamsSchema = z261.strictObject({
  documentId: z261.string().uuid()
});

// src/resources/documents/replace-document/replace-document-res-body.schema.ts
import { z as z262 } from "zod";
var replaceDocumentResBodySchema = z262.strictObject({
  data: contractDocumentSchema,
  uploadUrl: z262.string().url()
});

// src/resources/documents/update-document/update-document-req-body.schema.ts
import { z as z263 } from "zod";
var updateDocumentReqBodySchema = z263.strictObject({
  data: contractDocumentSchema.pick({
    status: true,
    uploadStatus: true,
    fileSizeInBytes: true,
    mimeType: true,
    rejectionReason: true,
    uploader: true
  }).partial()
});

// src/resources/documents/update-document/update-document-req-params.schema.ts
import { z as z264 } from "zod";
var updateDocumentReqParamsSchema = z264.strictObject({
  documentId: z264.string().uuid()
});

// src/resources/documents/update-document/update-document-res-body.schema.ts
var updateDocumentResBodySchema = contractDocumentSchema;

// src/resources/exchange-rate-requests/create-exchange-rate-requests-req-query.schema.ts
import { DateTime as DateTime9 } from "luxon";
import { z as z265 } from "zod";
var createExchangeRateRequestsReqQuerySchema = z265.strictObject({
  date: z265.string().date().transform((value) => DateTime9.fromFormat(value, "yyyy-MM-dd", {
    zone: "utc"
  })).optional()
});

// src/resources/external-claims/upsert-external-claim/upsert-external-claim-req-body.schema.ts
import { z as z266 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum14, ExternalClaimProviderEnum } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema20, priceSchema as priceSchema25 } from "@repo/schema-common";
var upsertExternalClaimReqBodySchema = z266.strictObject({
  data: z266.strictObject({
    provider: z266.nativeEnum(ExternalClaimProviderEnum),
    status: z266.enum([
      ClaimStatusEnum14.MANUAL_VERIFICATION_NEEDED,
      ClaimStatusEnum14.ACCEPTED,
      ClaimStatusEnum14.REJECTED,
      ClaimStatusEnum14.REFUNDED
    ]),
    compensation: priceSchema25.optional(),
    rejectionNote: z266.string().optional(),
    claimedAt: dateTimeWithUtcOffsetSchema20
  })
});

// src/resources/external-claims/upsert-external-claim/upsert-external-claim-req-params.schema.ts
import { z as z267 } from "zod";
var upsertExternalClaimReqParamsSchema = z267.strictObject({
  id: z267.string().uuid(),
  providerClaimId: z267.string().min(1)
});

// src/resources/external-claims/upsert-external-claim/upsert-external-claim-res-body.schema.ts
import { z as z268 } from "zod";
import { ClaimStatusEnum as ClaimStatusEnum15 } from "@repo/lib-koala-types";
var upsertExternalClaimResBodySchema = z268.strictObject({
  data: z268.strictObject({
    id: z268.string().uuid(),
    status: z268.nativeEnum(ClaimStatusEnum15),
    updated: z268.boolean()
  })
});

// src/resources/flight-statuses/create-flight-status/create-flight-status-req-body.schema.ts
import { extendApi as extendApi59 } from "@anatine/zod-openapi";
import { z as z269 } from "zod";
import { FlightStatusEnum } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema21 } from "@repo/schema-common";
var createFlightStatusReqBodySchema = z269.strictObject({
  data: z269.strictObject({
    providedAt: extendApi59(dateTimeWithUtcOffsetSchema21, {
      description: "The <b>date time</b> at which this change was applied, following the ISO 8601 format."
    }),
    status: extendApi59(z269.nativeEnum(FlightStatusEnum), {
      description: "The flight status."
    }),
    arrivalGateDelay: extendApi59(z269.number().optional(), {
      description: "The arrival gate delay in minutes."
    })
  })
});

// src/resources/flight-statuses/create-flight-status/create-flight-status-req-params.schema.ts
import { DateTime as DateTime10 } from "luxon";
import { z as z270 } from "zod";
import { airlineIataSchema as airlineIataSchema3, flightNumberSchema as flightNumberSchema3, iataSchema as iataSchema3 } from "@repo/schema-common";
var createFlightStatusReqParamsSchema = z270.strictObject({
  airlineIata: airlineIataSchema3,
  departureAirportIata: iataSchema3,
  number: flightNumberSchema3,
  localDepartureDate: z270.string().date().transform((value) => DateTime10.fromFormat(value, "yyyy-MM-dd", {
    zone: "utc"
  }))
});

// src/resources/flight-statuses/create-flight-status/create-flight-status-res-body.schema.ts
import { extendApi as extendApi60 } from "@anatine/zod-openapi";
import { z as z271 } from "zod";
import { FlightStatusEnum as FlightStatusEnum2 } from "@repo/lib-koala-types";
import { airlineIataSchema as airlineIataSchema4, dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema22, flightNumberSchema as flightNumberSchema4, iataSchema as iataSchema4, priceSchema as priceSchema26 } from "@repo/schema-common";
var createFlightStatusResBodySchema = z271.strictObject({
  data: z271.strictObject({
    flight: z271.strictObject({
      departureDate: extendApi60(dateTimeWithUtcOffsetSchema22, {
        description: "The <b>local</b> departure <b>date time</b>, following the ISO 8601 format."
      }),
      arrivalDate: extendApi60(dateTimeWithUtcOffsetSchema22, {
        description: "The <b>local</b> arrival <b>date time</b>, following the ISO 8601 format."
      }),
      airlineIata: extendApi60(airlineIataSchema4, {
        description: "IATA code of the carrier."
      }),
      arrivalAirportIata: extendApi60(iataSchema4, {
        description: "IATA code of the arrival airport."
      }),
      departureAirportIata: extendApi60(iataSchema4, {
        description: "IATA code of the departure airport."
      }),
      number: extendApi60(flightNumberSchema4, {
        description: "Flight number."
      }),
      price: extendApi60(priceSchema26.optional(), {
        description: "Price of the flight."
      })
    }),
    status: extendApi60(z271.nativeEnum(FlightStatusEnum2), {
      description: "The flight status."
    }),
    providedAt: extendApi60(dateTimeWithUtcOffsetSchema22, {
      description: "The <b>date time</b> at which this change was applied, following the ISO 8601 format."
    }),
    arrivalGateDelay: extendApi60(z271.number().optional(), {
      description: "The arrival gate delay in minutes."
    })
  })
});

// src/resources/flights-tracker/create-flight-tracker/create-flight-tracker-req-body.schema.ts
import { z as z272 } from "zod";
import { FlightStatusProviderEnum } from "@repo/lib-koala-types";
var createFlightTrackerReqBodySchema = z272.strictObject({
  data: z272.strictObject({
    provider: z272.literal(FlightStatusProviderEnum.FLIGHT_STATS)
  })
});

// src/resources/flights-tracker/create-flight-tracker/create-flight-tracker-req-params.schema.ts
import { extendApi as extendApi61 } from "@anatine/zod-openapi";
import { DateTime as DateTime11 } from "luxon";
import { z as z273 } from "zod";
import { airlineIataSchema as airlineIataSchema5, flightNumberSchema as flightNumberSchema5, iataSchema as iataSchema5 } from "@repo/schema-common";
var createFlightTrackerReqParamsSchema = z273.strictObject({
  airlineIata: extendApi61(airlineIataSchema5, {
    description: "IATA code of the carrier.",
    example: "DL"
  }),
  departureAirportIata: extendApi61(iataSchema5, {
    description: "IATA code of the departure airport.",
    example: "CDG"
  }),
  number: extendApi61(flightNumberSchema5, {
    description: "Flight number.",
    example: "263"
  }),
  localDepartureDate: extendApi61(z273.string().date().transform((date) => DateTime11.fromFormat(date, "yyyy-LL-dd", {
    setZone: true,
    zone: "utc"
  })), {
    description: "Local departure date in AAAA-MM-JJ format.",
    example: "2022-04-18"
  })
});

// src/resources/flights-tracker/create-flight-tracker/create-flight-tracker-res-body.schema.ts
import { extendApi as extendApi62 } from "@anatine/zod-openapi";
import { DateTime as DateTime12 } from "luxon";
import { z as z274 } from "zod";
import { FlightStatusEnum as FlightStatusEnum3 } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema23 } from "@repo/schema-common";
var createFlightTrackerResBodySchema = z274.strictObject({
  data: z274.strictObject({
    flight: baseFlightSchema.extend({
      departureDate: extendApi62(dateTimeWithUtcOffsetSchema23, {
        description: "The <strong>local</strong> departure <strong>date time</strong> following the ISO 8601 format.",
        example: DateTime12.utc().setZone("Europe/Paris").plus({
          months: 1
        }).startOf("hour").toISO(),
        type: "string"
      }),
      arrivalDate: extendApi62(dateTimeWithUtcOffsetSchema23, {
        description: "The <strong>local</strong> arrival <strong>date time</strong> following the ISO 8601 format.",
        example: DateTime12.utc().setZone("America/New_York").plus({
          months: 1
        }).startOf("hour").plus({
          hours: 8,
          minutes: 21
        }).toISO(),
        type: "string"
      })
    }),
    status: extendApi62(z274.nativeEnum(FlightStatusEnum3), {
      description: "The flight status.",
      example: FlightStatusEnum3.LANDED
    }),
    providedAt: extendApi62(z274.string(), {
      description: "The <strong>date time</strong> at which this change was applied, following the ISO 8601 format.",
      example: DateTime12.utc().plus({
        months: 1
      }).startOf("hour").plus({
        hours: 8,
        minutes: 21
      }).toISO(),
      type: "string"
    }),
    arrivalGateDelay: extendApi62(z274.number().optional(), {
      description: "The arrival gate delay in minutes.",
      example: 45
    })
  })
});

// src/resources/flights-tracker/create-flight-tracker/no-status-available.schema.ts
import { z as z275 } from "zod";
var noStatusAvailableSchema = z275.strictObject({
  data: z275.strictObject({
    message: z275.string()
  })
});

// src/resources/login/login-req-body.schema.ts
import { z as z276 } from "zod";
var loginReqBodySchema = z276.strictObject({
  data: z276.strictObject({
    name: z276.string(),
    password: z276.string(),
    challengeToken: z276.string().optional()
  })
});

// src/resources/login/login-res-body.schema.ts
import { z as z277 } from "zod";
var loginResBodySchema = z277.strictObject({
  data: z277.strictObject({
    token: z277.string()
  })
});

// src/resources/public/create-quote/public-partner-create-quote-req-body.schema.ts
import { z as z285 } from "zod";
import { isoCurrencySchema as isoCurrencySchema2 } from "@repo/schema-common";

// src/resources/public/create-quote/public-partner-create-quote-req-body-trip.schema.ts
import { z as z284 } from "zod";
import { priceSchema as priceSchema31 } from "@repo/schema-common";

// src/resources/public/create-quote/public-partner-create-quote-req-body-journey.schema.ts
import { z as z281 } from "zod";
import { priceSchema as priceSchema29 } from "@repo/schema-common";

// src/resources/public/create-quote/public-partner-create-quote-req-body-segment.schema.ts
import { z as z280 } from "zod";

// src/resources/public/create-quote/public-partner-create-quote-req-body-flight-segment.schema.ts
import { z as z278 } from "zod";
import { RouteModeEnum as RouteModeEnum4 } from "@repo/lib-koala-types";
import { airlineIataSchema as airlineIataSchema6, dateTimeWithoutUtcOffsetSchema as dateTimeWithoutUtcOffsetSchema4, flightNumberSchema as flightNumberSchema6, iataSchema as iataSchema6, priceSchema as priceSchema27 } from "@repo/schema-common";
var publicPartnerCreateQuoteReqBodyFlightSegmentSchema = z278.strictObject({
  mode: z278.literal(RouteModeEnum4.FLIGHT),
  airlineIata: airlineIataSchema6,
  departureAirportIata: iataSchema6,
  arrivalAirportIata: iataSchema6,
  number: flightNumberSchema6,
  departureDate: dateTimeWithoutUtcOffsetSchema4,
  arrivalDate: dateTimeWithoutUtcOffsetSchema4,
  price: priceSchema27.optional()
});

// src/resources/public/create-quote/public-partner-create-quote-req-body-generic-segment.schema.ts
import { z as z279 } from "zod";
import { RouteModeEnum as RouteModeEnum5 } from "@repo/lib-koala-types";
import { dateTimeWithoutUtcOffsetSchema as dateTimeWithoutUtcOffsetSchema5, priceSchema as priceSchema28 } from "@repo/schema-common";
var publicPartnerCreateQuoteReqBodyGenericSegmentSchema = z279.strictObject({
  mode: z279.enum([
    RouteModeEnum5.BUS,
    RouteModeEnum5.TRAIN,
    RouteModeEnum5.FERRY
  ]),
  externalId: z279.string().min(1).max(255),
  transportIdentifier: z279.string().min(1).max(255),
  departureStop: segmentStopSchema,
  arrivalStop: segmentStopSchema,
  departureDate: dateTimeWithoutUtcOffsetSchema5,
  arrivalDate: dateTimeWithoutUtcOffsetSchema5,
  price: priceSchema28.optional()
});

// src/resources/public/create-quote/public-partner-create-quote-req-body-segment.schema.ts
var publicPartnerCreateQuoteReqBodySegmentSchema = z280.union([
  publicPartnerCreateQuoteReqBodyFlightSegmentSchema,
  publicPartnerCreateQuoteReqBodyGenericSegmentSchema
]);

// src/resources/public/create-quote/public-partner-create-quote-req-body-journey.schema.ts
var publicPartnerCreateQuoteReqBodyJourneySchema = z281.strictObject({
  segments: publicPartnerCreateQuoteReqBodySegmentSchema.array().min(1).max(25),
  price: priceSchema29.optional()
});

// src/resources/public/create-quote/public-partner-create-quote-req-body-stay.schema.ts
import { z as z282 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema24, priceSchema as priceSchema30 } from "@repo/schema-common";
var publicPartnerCreateQuoteReqBodyStaySchema = z282.strictObject({
  name: z282.string().min(1).max(255),
  address: addressSchema,
  startDate: dateTimeWithUtcOffsetSchema24,
  endDate: dateTimeWithUtcOffsetSchema24,
  price: priceSchema30.optional()
});

// src/resources/public/create-quote/public-partner-create-quote-req-body-traveler.schema.ts
import { z as z283 } from "zod";
import { AgeRangeEnum as AgeRangeEnum5 } from "@repo/lib-koala-types";
import { countryCodeSchema as countryCodeSchema12 } from "@repo/schema-common";
var publicPartnerCreateQuoteReqBodyTravelerSchema = z283.strictObject({
  ageRange: z283.nativeEnum(AgeRangeEnum5),
  residenceCountryCode: countryCodeSchema12.optional(),
  numberOfCheckedBags: z283.number().int().max(25).positive().optional()
});

// src/resources/public/create-quote/public-partner-create-quote-req-body-trip.schema.ts
var publicPartnerCreateQuoteReqBodyTripSchema = z284.strictObject({
  price: priceSchema31.optional(),
  travelers: publicPartnerCreateQuoteReqBodyTravelerSchema.array().min(1).max(24),
  journeys: publicPartnerCreateQuoteReqBodyJourneySchema.array().max(25).default([]),
  stays: publicPartnerCreateQuoteReqBodyStaySchema.array().max(25).default([])
}).refine((trip) => trip.journeys.length > 0 || trip.stays.length > 0, {
  message: "Trip must contain at least one journey or one stay."
});

// src/resources/public/create-quote/public-partner-create-quote-req-body.schema.ts
var publicPartnerCreateQuoteReqBodySchema = z285.strictObject({
  data: z285.strictObject({
    currency: isoCurrencySchema2,
    bundles: z285.string().min(1).max(255).array().min(1).max(10),
    trip: publicPartnerCreateQuoteReqBodyTripSchema,
    distributionContext: distributionContextSchema.optional()
  })
});

// src/resources/public/create-quote/public-partner-create-quote-req-params.schema.ts
import { z as z286 } from "zod";
var publicPartnerCreateQuoteReqParamsSchema = z286.strictObject({
  id: z286.string().uuid()
});

// src/resources/public/create-quote/public-partner-create-quote-res-body.schema.ts
import { z as z293 } from "zod";

// src/resources/public/create-quote/public-partner-create-quote-res-body-document.schema.ts
import { z as z287 } from "zod";
import { ProductAttachmentTypeEnum as ProductAttachmentTypeEnum2, ProductTypeEnum as ProductTypeEnum32 } from "@repo/lib-koala-types";
var publicPartnerCreateQuoteResBodyDocumentSchema = z287.strictObject({
  bundles: z287.string().array().min(1).max(10),
  language: z287.string().min(2).max(14),
  productType: z287.nativeEnum(ProductTypeEnum32),
  type: z287.nativeEnum(ProductAttachmentTypeEnum2),
  url: z287.string().url()
});

// src/resources/public/create-quote/public-partner-create-quote-res-body-quote.schema.ts
import { z as z292 } from "zod";

// src/resources/public/create-quote/public-partner-create-quote-res-body-invalid-quote-schema.ts
import { z as z289 } from "zod";

// src/resources/public/create-quote/public-partner-create-quote-res-body-quote-base.schema.ts
import { z as z288 } from "zod";
var publicPartnerCreateQuoteResBodyQuoteBaseSchema = z288.strictObject({
  bundle: z288.string().min(1).max(255)
});

// src/resources/public/create-quote/public-partner-create-quote-res-body-invalid-quote-schema.ts
var publicPartnerCreateQuoteResBodyInvalidQuoteSchema = publicPartnerCreateQuoteResBodyQuoteBaseSchema.extend({
  valid: z289.literal(false),
  reasons: z289.string().array().max(50)
});

// src/resources/public/create-quote/public-partner-create-quote-res-body-valid-quote.schema.ts
import { z as z291 } from "zod";
import { priceSchema as priceSchema32 } from "@repo/schema-common";

// src/resources/public/create-quote/public-partner-create-quote-res-body-contract.schema.ts
import { DateTime as DateTime13 } from "luxon";
import { z as z290 } from "zod";
import { ProductTypeEnum as ProductTypeEnum33 } from "@repo/lib-koala-types";
import { isoCurrencySchema as isoCurrencySchema3 } from "@repo/schema-common";
var publicPartnerCreateQuoteResBodyContractSchema = z290.strictObject({
  type: z290.enum([
    ProductTypeEnum33.FLIGHT_DISRUPTION,
    ProductTypeEnum33.CHANGE_FOR_ANY_REASON,
    ProductTypeEnum33.WITHDRAWAL,
    ProductTypeEnum33.BAGGAGE_DISRUPTION,
    ProductTypeEnum33.CANCELLATION_WITHOUT_PROOF,
    ProductTypeEnum33.COMPREHENSIVE
  ]),
  deadline: z290.string().transform((date) => DateTime13.fromISO(date)),
  maximumCompensation: z290.strictObject({
    amount: z290.number().positive(),
    currency: isoCurrencySchema3
  })
});

// src/resources/public/create-quote/public-partner-create-quote-res-body-valid-quote.schema.ts
var publicPartnerCreateQuoteResBodyValidQuoteSchema = publicPartnerCreateQuoteResBodyQuoteBaseSchema.extend({
  valid: z291.literal(true),
  reference: z291.string(),
  contracts: publicPartnerCreateQuoteResBodyContractSchema.array().max(6).optional(),
  price: priceSchema32
});

// src/resources/public/create-quote/public-partner-create-quote-res-body-quote.schema.ts
var publicPartnerCreateQuoteResBodyQuoteSchema = z292.discriminatedUnion("valid", [
  publicPartnerCreateQuoteResBodyValidQuoteSchema,
  publicPartnerCreateQuoteResBodyInvalidQuoteSchema
]);

// src/resources/public/create-quote/public-partner-create-quote-res-body.schema.ts
var publicPartnerCreateQuoteResBodySchema = z293.strictObject({
  data: z293.strictObject({
    quoteId: z293.string().uuid(),
    quotes: publicPartnerCreateQuoteResBodyQuoteSchema.array().max(10),
    documents: publicPartnerCreateQuoteResBodyDocumentSchema.array().max(100)
  })
});

// src/resources/public/create-subscription/public-partner-create-subscription-req-body.schema.ts
import { z as z298 } from "zod";
import { bookingNumberSchema as bookingNumberSchema3 } from "@repo/schema-common";

// src/resources/subscriptions/create-subscription/create-subscription-req-body-customer.schema.ts
import { extendApi as extendApi63 } from "@anatine/zod-openapi";
import { z as z294 } from "zod";
import { emailSchema as emailSchema5, languageSchema as languageSchema2, phoneNumberSchema as phoneNumberSchema2 } from "@repo/schema-common";
var createSubscriptionReqBodyCustomerSchema = extendApi63(z294.strictObject({
  firstName: extendApi63(z294.string().trim().min(1).max(255), {
    description: "First name of the customer.",
    example: "Mary"
  }),
  lastName: extendApi63(z294.string().trim().min(1).max(255), {
    description: "Last name of the customer.",
    example: "Dancer"
  }),
  email: extendApi63(emailSchema5, {
    description: "Email address of the customer.",
    example: "mary.dancer@gmail.com",
    format: "email",
    maxLength: 255
  }),
  language: extendApi63(languageSchema2, {
    description: "Language of the customer. It must be a country language code like <code>[language designator]-[region designator]</code>, with an ISO 639-1 alpha-2 code for the <code>language designator</code> and an ISO 3166-1 for the <code>region designator</code>.<br /><strong>\u26A0\uFE0F Please be careful with filling this field as we use it to define the language used for the travelers in all our communications.</strong>",
    example: "en-GB",
    minLength: 5,
    maxLength: 5
  }),
  phoneNumber: extendApi63(phoneNumberSchema2.optional(), {
    description: "Phone number of the customer.",
    example: "+1 213 373 4253"
  }),
  birthday: birthdaySchema.optional(),
  address: addressSchema.optional()
}), {
  hideDefinitions: [
    "address"
  ]
});

// src/resources/subscriptions/create-subscription/create-subscription-req-body-traveler.schema.ts
import { extendApi as extendApi64 } from "@anatine/zod-openapi";
import { z as z295 } from "zod";
import { AgeRangeEnum as AgeRangeEnum6 } from "@repo/lib-koala-types";
import { countryCodeSchema as countryCodeSchema13 } from "@repo/schema-common";
var createSubscriptionReqBodyTravelerSchema = extendApi64(z295.strictObject({
  firstName: extendApi64(z295.string().min(1).max(255), {
    description: "First name of the traveler.",
    example: "Mary"
  }),
  lastName: extendApi64(z295.string().min(1).max(255), {
    description: "Last name of the traveler.",
    example: "Dancer"
  }),
  ageRange: extendApi64(z295.nativeEnum(AgeRangeEnum6).default(AgeRangeEnum6.ADULT), {
    default: AgeRangeEnum6.ADULT,
    description: `We need one age range per traveler.
The ageRange are the following:
- <code>${AgeRangeEnum6.INFANT}</code> (0-23 months old)
- <code>${AgeRangeEnum6.CHILD}</code> (2-11 years old)
- <code>${AgeRangeEnum6.ADULT}</code> (12 - 64 years old)
- <code>${AgeRangeEnum6.SENIOR}</code> (65+ years old)

<strong>\u26A0\uFE0F If you do not ask for the travelers' ages, please put <code>${AgeRangeEnum6.ADULT}</code> by default.</strong>`,
    example: AgeRangeEnum6.ADULT
  }),
  residenceCountryCode: extendApi64(countryCodeSchema13.optional(), {
    description: "You can precise a country of residence if the quote is restricted to specific areas",
    example: "FR"
  }),
  birthday: extendApi64(birthdaySchema.optional(), {
    description: "Birthday date of the traveler.",
    example: "1990-02-10"
  }),
  address: richAddressSchema.optional(),
  numberOfCheckedBags: extendApi64(z295.number().int().positive().optional(), {
    description: `The total number of checked bags that this traveler will bring.<br/>
        A checked bag is typically a larger piece of baggage that the traveler gives to the airline at the check-in counter to be transported in the airplane\u2019s cargo hold (not carried into the cabin).`,
    example: 2
  })
}), {
  hideDefinitions: [
    "address"
  ]
});

// src/resources/subscriptions/create-subscription/encrypted-card-payment-details.schema.ts
import { z as z297 } from "zod";

// src/resources/subscriptions/create-subscription/encrypted-field.schema.ts
import { z as z296 } from "zod";
var encryptedFieldSchema = z296.string().regex(/^ev:.*:\$$/, {
  message: "Field must be encrypted. Please use Koala's secure API endpoint to pass customer card payment details."
});

// src/resources/subscriptions/create-subscription/encrypted-card-payment-details.schema.ts
var encryptedCardPaymentDetailsSchema = z297.strictObject({
  cardType: z297.enum([
    "CREDIT",
    "DEBIT"
  ]).optional(),
  cardCode: z297.enum([
    "VI",
    "MC",
    "AX",
    "DI",
    "DC",
    "JC",
    "MA"
  ]),
  cardNumber: encryptedFieldSchema,
  expirationDate: encryptedFieldSchema,
  seriesCode: encryptedFieldSchema,
  cardHolderName: encryptedFieldSchema,
  threeDomainSecurity: z297.strictObject({
    eci: encryptedFieldSchema,
    cavv: encryptedFieldSchema,
    threeDSVersion: z297.string().optional()
  }).optional()
});

// src/resources/public/create-subscription/public-partner-create-subscription-req-body.schema.ts
var publicPartnerCreateSubscriptionReqBodySchema = z298.strictObject({
  data: z298.strictObject({
    bookingNumber: bookingNumberSchema3,
    customer: createSubscriptionReqBodyCustomerSchema,
    quotes: z298.strictObject({
      reference: z298.string(),
      travelers: createSubscriptionReqBodyTravelerSchema.array().min(1).max(20)
    }).array().min(1).max(20).refine((quotes) => new Set(quotes.map(({ reference }) => reference)).size === quotes.length, {
      message: "Quote references must be unique"
    }),
    payment: encryptedCardPaymentDetailsSchema.optional()
  })
});

// src/resources/public/create-subscription/public-partner-create-subscription-req-params.schema.ts
import { z as z299 } from "zod";
var publicPartnerCreateSubscriptionReqParamsSchema = z299.strictObject({
  id: z299.string().uuid()
});

// src/resources/public/create-subscription/public-partner-create-subscription-res-body.schema.ts
import { z as z302 } from "zod";
import { bookingNumberSchema as bookingNumberSchema4, priceSchema as priceSchema33 } from "@repo/schema-common";

// src/resources/subscriptions/contracts/get-all-subscription-contracts-req-params.schema.ts
import { extendApi as extendApi65 } from "@anatine/zod-openapi";
import { z as z300 } from "zod";
var getAllSubscriptionContractsReqParamsSchema = z300.strictObject({
  bookingNumber: extendApi65(z300.string().max(255), {
    description: "The booking reference number associated with the subscription.",
    example: "5K7SXD"
  })
});

// src/resources/subscriptions/contracts/get-all-subscription-contracts-res-body.schema.ts
import { extendApi as extendApi66 } from "@anatine/zod-openapi";
import { z as z301 } from "zod";
var getAllSubscriptionContractsResBodySchema = z301.strictObject({
  data: extendApi66(z301.array(z301.union([
    flightContractSchema,
    tripContractSchema
  ])).min(1), {
    description: "List of contracts linked to this subscription."
  })
});

// src/resources/public/create-subscription/public-partner-create-subscription-res-body.schema.ts
var publicPartnerCreateSubscriptionResBodySchema = z302.strictObject({
  data: z302.strictObject({
    bookingNumber: bookingNumberSchema4,
    contracts: z302.array(z302.union([
      flightContractSchema,
      tripContractSchema
    ])).min(1),
    price: priceSchema33
  })
});

// src/resources/public/delete-quote/public-partner-delete-quote-req-body.schema.ts
import { z as z303 } from "zod";
import { bookingNumberSchema as bookingNumberSchema5 } from "@repo/schema-common";
var publicPartnerDeleteQuoteReqBodySchema = z303.strictObject({
  data: z303.strictObject({
    quoteId: z303.string().uuid(),
    bookingNumber: bookingNumberSchema5
  })
});

// src/resources/public/delete-quote/public-partner-delete-quote-req-params.schema.ts
import { z as z304 } from "zod";
var publicPartnerDeleteQuoteReqParamsSchema = z304.strictObject({
  id: z304.string().uuid()
});

// src/resources/quote/create-bundle-quote/create-bundle-quote-req-body.schema.ts
import { z as z328 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema27, isoCurrencySchema as isoCurrencySchema9, priceSchema as priceSchema46 } from "@repo/schema-common";

// src/resources/quote/create-quote/create-quote-req-body.schema.ts
import { extendApi as extendApi76 } from "@anatine/zod-openapi";
import { z as z313 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema26, isoCurrencySchema as isoCurrencySchema8 } from "@repo/schema-common";

// src/resources/quote/create-quote/create-quote-req-body-trip.schema.ts
import { extendApi as extendApi75 } from "@anatine/zod-openapi";
import { z as z312 } from "zod";

// src/resources/quote/create-quote/create-quote-req-body-trip-with-journeys.schema.ts
import { extendApi as extendApi73 } from "@anatine/zod-openapi";
import { z as z310 } from "zod";
import { isoCurrencySchema as isoCurrencySchema7, priceSchema as priceSchema39 } from "@repo/schema-common";

// src/resources/quote/create-quote/create-quote-req-body-journey.schema.ts
import { z as z308 } from "zod";

// src/resources/quote/create-quote/create-quote-req-body-journey-with-flights.schema.ts
import { extendApi as extendApi69 } from "@anatine/zod-openapi";
import { z as z305 } from "zod";
import { isoCurrencySchema as isoCurrencySchema6, priceSchema as priceSchema36 } from "@repo/schema-common";

// src/resources/quote/create-quote/create-quote-req-body-flight-with-time-zone.schema.ts
import { extendApi as extendApi67 } from "@anatine/zod-openapi";
import { DateTime as DateTime14 } from "luxon";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema25, isoCurrencySchema as isoCurrencySchema4, priceSchema as priceSchema34 } from "@repo/schema-common";
var createQuoteReqBodyFlightWithTimeZoneSchema = extendApi67(flightWithTimeZoneSchema.extend({
  departureDate: extendApi67(dateTimeWithUtcOffsetSchema25, {
    description: `Expected <strong>local</strong> departure <strong>date time</strong> following the ISO 8601 format with a time zone.
    <br/><strong>\u26A0\uFE0F Please be careful to use the local departure date time and not the departure date of your time zone.</strong>`,
    example: DateTime14.utc().setZone("Europe/Paris").plus({
      months: 1
    }).startOf("hour").toISO(),
    format: "date-time",
    type: "string"
  }),
  arrivalDate: extendApi67(dateTimeWithUtcOffsetSchema25, {
    description: "Expected <strong>local</strong> arrival <strong>date time</strong> following the ISO 8601 format with a time zone.",
    example: DateTime14.utc().setZone("America/New_York").plus({
      months: 1
    }).startOf("hour").plus({
      hours: 8,
      minutes: 21
    }).toISO(),
    format: "date-time",
    type: "string"
  }),
  price: extendApi67(priceSchema34.extend({
    currency: extendApi67(isoCurrencySchema4, {
      description: `Currency of the price in ISO 4217 format.
                  <br/><strong>\u26A0\uFE0F Please make sure to provide the same currency in all provided prices, or we will return an error.</strong>`,
      example: "EUR",
      type: "string"
    })
  }).optional(), {
    description: "Price of the flight. It can be required for products which depend on the price of the flight."
  })
}), {
  title: "QuoteFlightWithTimeZoneInputDto"
});

// src/resources/quote/create-quote/create-quote-req-body-flight-without-time-zone.schema.ts
import { extendApi as extendApi68 } from "@anatine/zod-openapi";
import { DateTime as DateTime15 } from "luxon";
import { dateTimeWithoutUtcOffsetSchema as dateTimeWithoutUtcOffsetSchema6, isoCurrencySchema as isoCurrencySchema5, priceSchema as priceSchema35 } from "@repo/schema-common";
var createQuoteReqBodyFlightWithoutTimeZoneSchema = extendApi68(flightWithLocalDatesSchema.extend({
  localDepartureDate: extendApi68(dateTimeWithoutUtcOffsetSchema6, {
    description: `Expected <strong>local</strong> departure <strong>date time</strong> following the ISO 8601 format without time zone.
    <br/><strong>\u26A0\uFE0F Please be careful to use the local departure date time and not the departure date of your time zone.</strong>`,
    example: DateTime15.utc().setZone("America/Belem").plus({
      months: 1
    }).startOf("hour").toISO({
      includeOffset: false
    }),
    format: "date-time",
    type: "string"
  }),
  localArrivalDate: extendApi68(dateTimeWithoutUtcOffsetSchema6, {
    description: "Expected <strong>local</strong> arrival <strong>date time</strong> following the ISO 8601 format without time zone.",
    example: DateTime15.utc().plus({
      months: 1
    }).startOf("hour").plus({
      hours: 8,
      minutes: 21
    }).toISO({
      includeOffset: false
    }),
    format: "date-time",
    type: "string"
  }),
  price: extendApi68(priceSchema35.extend({
    currency: extendApi68(isoCurrencySchema5, {
      description: `Currency of the price in ISO 4217 format.
                  <br/><strong>\u26A0\uFE0F Please make sure to provide the same currency in all provided prices, or we will return an error.</strong>`,
      example: "EUR",
      type: "string"
    })
  }).optional(), {
    description: "Price of the flight. It can be required for products which depend on the price of the flight."
  })
}), {
  title: "QuoteFlightWithoutTimeZoneInputDto"
});

// src/resources/quote/create-quote/create-quote-req-body-journey-with-flights.schema.ts
var createQuoteReqBodyJourneyWithFlightsSchema = extendApi69(z305.strictObject({
  flights: extendApi69(z305.union([
    createQuoteReqBodyFlightWithTimeZoneSchema,
    createQuoteReqBodyFlightWithoutTimeZoneSchema
  ], {
    errorMap: createUnionErrorMap([
      "QuoteFlightWithTimeZoneInputDto",
      "QuoteFlightWithoutTimeZoneInputDto"
    ])
  }).array().min(1), {
    description: `Flights of the journey. It can be more than one if you have connections.
                  <br/><strong>\u26A0\uFE0F Some products have a maximum number of flights</strong>`
  }),
  price: extendApi69(priceSchema36.extend({
    currency: extendApi69(isoCurrencySchema6, {
      description: `Currency of the price in ISO 4217 format.
                  <br/><strong>\u26A0\uFE0F Please make sure to provide the same currency in all provided prices, or we will return an error.</strong>`,
      example: "EUR",
      type: "string"
    })
  }).optional(), {
    description: "Price of the entire journey. It can be required for products which depend on the price of the journey."
  })
}), {
  title: "QuoteJourneyWithFlightsInputDto"
});

// src/resources/quote/create-quote/create-quote-req-body-journey-with-segments.schema.ts
import { extendApi as extendApi71 } from "@anatine/zod-openapi";
import { z as z307 } from "zod";
import { priceSchema as priceSchema38 } from "@repo/schema-common";

// src/resources/quote/create-quote/create-quote-req-body-segment.schema.ts
import { extendApi as extendApi70 } from "@anatine/zod-openapi";
import { z as z306 } from "zod";
import { priceSchema as priceSchema37 } from "@repo/schema-common";
var flightSegmentSchemaTitle = "FlightSegmentInputDto";
var genericSegmentSchemaTitle = "GenericSegmentInputDto";
var createQuoteReqBodySegmentSchema = extendApi70(z306.union([
  extendApi70(flightSegmentSchema.extend({
    price: extendApi70(priceSchema37.optional(), {
      description: "Price of the flight. It can be required for products which depend on the price of the flight."
    })
  }), {
    title: flightSegmentSchemaTitle
  }),
  extendApi70(genericSegmentSchema.extend({
    price: extendApi70(priceSchema37.optional(), {
      description: "Price of the segment. It can be required for products which depend on the price of the segment."
    })
  }), {
    title: genericSegmentSchemaTitle
  })
], {
  errorMap: createUnionErrorMap([
    flightSegmentSchemaTitle,
    genericSegmentSchemaTitle
  ])
}));

// src/resources/quote/create-quote/create-quote-req-body-journey-with-segments.schema.ts
var createQuoteJourneyWithSegmentsSchema = extendApi71(z307.strictObject({
  price: extendApi71(priceSchema38.optional(), {
    description: "Price of the entire journey. It can be required for products which depend on the price of the journey."
  }),
  segments: extendApi71(createQuoteReqBodySegmentSchema.array().min(1), {
    description: `Segments of the journey. It can be more than one if you have connections.
                  <br/><strong>\u26A0\uFE0F Some products have a maximum number of segments</strong>`
  })
}), {
  title: "QuoteJourneyWithSegmentsInputDto"
});

// src/resources/quote/create-quote/create-quote-req-body-journey.schema.ts
var createQuoteReqBodyJourneySchema = z308.union([
  createQuoteReqBodyJourneyWithFlightsSchema,
  createQuoteJourneyWithSegmentsSchema
], {
  errorMap: createUnionErrorMap([
    "QuoteJourneyWithFlightsInputDto",
    "QuoteJourneyWithSegmentsInputDto"
  ])
});

// src/resources/quote/create-quote/create-quote-req-body-traveler.schema.ts
import { extendApi as extendApi72 } from "@anatine/zod-openapi";
import { z as z309 } from "zod";
import { AgeRangeEnum as AgeRangeEnum7 } from "@repo/lib-koala-types";
import { countryCodeSchema as countryCodeSchema14 } from "@repo/schema-common";
var createQuoteReqBodyTravelerSchema = extendApi72(z309.strictObject({
  ageRange: extendApi72(z309.nativeEnum(AgeRangeEnum7).default(AgeRangeEnum7.ADULT), {
    default: AgeRangeEnum7.ADULT,
    description: `We need one age range per traveler.
The ageRange are the following:
- <code>${AgeRangeEnum7.INFANT}</code> (0-23 months old)
- <code>${AgeRangeEnum7.CHILD}</code> (2-11 years old)
- <code>${AgeRangeEnum7.ADULT}</code> (12 - 64 years old)
- <code>${AgeRangeEnum7.SENIOR}</code> (65+ years old)

<strong>\u26A0\uFE0F If you do not ask for the travelers' ages, please put <code>${AgeRangeEnum7.ADULT}</code> by default.</strong>`,
    example: AgeRangeEnum7.ADULT
  }),
  residenceCountryCode: extendApi72(countryCodeSchema14.optional(), {
    description: "You can precise a country of residence if the quote is restricted to specific areas"
  }),
  numberOfCheckedBags: extendApi72(z309.number().int().positive().optional(), {
    description: `The total number of checked bags that this traveler will bring.<br/>
        A checked bag is typically a larger piece of baggage that the traveler gives to the airline at the check-in counter to be transported in the airplane\u2019s cargo hold (not carried into the cabin).`,
    example: 2
  })
}));

// src/resources/quote/create-quote/create-quote-req-body-trip-with-journeys.schema.ts
var createQuoteReqBodyTripWithJourneysSchema = extendApi73(z310.strictObject({
  price: extendApi73(priceSchema39.extend({
    currency: extendApi73(isoCurrencySchema7, {
      description: `Currency of the price in ISO 4217 format.
                  <br/><strong>\u26A0\uFE0F Please make sure to provide the same currency in all provided prices, or we will return an error.</strong>`,
      example: "EUR",
      type: "string"
    })
  }).optional(), {
    description: "Price of the entire trip."
  }),
  travelers: extendApi73(createQuoteReqBodyTravelerSchema.array().min(1), {
    description: "Travelers that will be part of the trip.",
    title: "QuoteTravelerDto"
  }),
  journeys: extendApi73(createQuoteReqBodyJourneySchema.array().min(1), {
    description: "Journeys that constitute the trip. Usually, you should have one or two journeys (outward and return)."
  }),
  stays: extendApi73(staySchema.extend({
    price: extendApi73(priceSchema39.extend({
      currency: extendApi73(isoCurrencySchema7, {
        description: `Currency of the price in ISO 4217 format.
                  <br/><strong>\u26A0\uFE0F Please make sure to provide the same currency in all provided prices, or we will return an error.</strong>`,
        example: "EUR",
        type: "string"
      })
    }).optional(), {
      description: "Price of the entire stay. It can be required for products which depend on the price of the stay."
    })
  }).array().default([]), {
    description: "Stays that constitute the trip.",
    title: "QuoteStayInputDto"
  })
}), {
  title: "QuoteTripWithJourneysInputDto"
});

// src/resources/quote/create-quote/create-quote-req-body-trip-with-stays.schema.ts
import { extendApi as extendApi74 } from "@anatine/zod-openapi";
import { z as z311 } from "zod";
import { priceSchema as priceSchema40 } from "@repo/schema-common";
var createQuoteReqBodyTripWithStaysSchema = extendApi74(z311.strictObject({
  price: extendApi74(priceSchema40.optional(), {
    description: "Price of the entire trip."
  }),
  travelers: extendApi74(createQuoteReqBodyTravelerSchema.array().min(1), {
    description: "Travelers that will be part of the trip.",
    title: "QuoteTravelerInputDto"
  }),
  journeys: extendApi74(createQuoteReqBodyJourneySchema.array().default([]), {
    description: "Journeys that constitute the trip. Usually, you should have one or two journeys (outward and return)."
  }),
  stays: extendApi74(staySchema.array().min(1), {
    description: "Stays that constitute the trip.",
    title: "QuoteStayInputDto"
  })
}), {
  title: "QuoteTripWithStaysInputDto"
});

// src/resources/quote/create-quote/create-quote-req-body-trip.schema.ts
var createQuoteReqBodyTripSchema = extendApi75(z312.union([
  createQuoteReqBodyTripWithJourneysSchema,
  createQuoteReqBodyTripWithStaysSchema
], {
  errorMap: createUnionErrorMap([
    "QuoteTripWithJourneysInputDto",
    "QuoteTripWithStaysInputDto"
  ])
}), {
  description: `If all travelers are on the same trip, and they can only purchase the products on a booking level (= all travelers is covered when they add products), you should provide only one trip.
                  <br/>If you offer the possibility for multi-travelers bookings to individually purchase products (or allow them to book different trips), multiple trips needs to be used.
                  <br/><strong>\u26A0\uFE0F By default, your partnership does not support multiple trips. The size of the array may be limited to 1.</strong>`
});

// src/resources/quote/create-quote/create-quote-req-body.schema.ts
var createQuoteReqBodySchema = z313.strictObject({
  data: extendApi76(z313.strictObject({
    bookingNumber: extendApi76(z313.string().max(255).optional(), {
      description: `The booking reference number associated with the subscription if you intend to update a subscription.
    If provided, parts of the trips already present in the original subscription will be ignored and you will get the
    result for the diff. Note that you must provide us the complete version of the trips, not just the difference.
    <br/><strong>\u26A0\uFE0F Please use the booking number that you share with your customers.</strong>`,
      example: "5K7SXD"
    }),
    currency: extendApi76(isoCurrencySchema8, {
      description: `Currency of the quote in ISO 4217 format.
      <br/><strong>\u26A0\uFE0F Inside the same quote, the currency used must be consistent. Otherwise, we will return an error.</strong>`,
      example: "EUR"
    }),
    trips: extendApi76(createQuoteReqBodyTripSchema.array().min(1).max(1, "should not contain more than one trip because the support for multiple trips has been disabled for your partnership."), {
      description: `If all travelers are on the same trip, and they can only purchase the products on a booking level (= all travelers is covered when they add products), you should provide only one trip.
                  <br/>If you offer the possibility for multi-travelers bookings to individually purchase products (or allow them to book different trips), multiple trips needs to be used.
                  <br/><strong>\u26A0\uFE0F By default, your partnership does not support multiple trips. The size of the array may be limited to 1.</strong>`
    }),
    quoteDate: dateTimeWithUtcOffsetSchema26.optional(),
    includePreConditionDetails: z313.boolean().optional(),
    distributionContext: distributionContextSchema.optional()
  }), {
    title: "CreateQuoteDataInputDto",
    hideDefinitions: [
      "quoteDate",
      "includePreConditionDetails"
    ]
  })
});

// src/resources/quote/create-quote/create-quote-req-body-flight-segment.schema.ts
import { priceSchema as priceSchema41 } from "@repo/schema-common";
var createQuoteReqBodyFlightSegmentSchema = flightSegmentSchema.extend({
  price: priceSchema41.optional()
});

// src/resources/quote/create-quote/create-quote-res-body.schema.ts
import { extendApi as extendApi88 } from "@anatine/zod-openapi";
import { z as z324 } from "zod";

// src/resources/quote/create-quote/create-quote-res-body-document.schema.ts
import { extendApi as extendApi77 } from "@anatine/zod-openapi";
import { z as z314 } from "zod";
import { ProductAttachmentTypeEnum as ProductAttachmentTypeEnum3, ProductTypeEnum as ProductTypeEnum34 } from "@repo/lib-koala-types";
var createQuoteResBodyDocumentSchema = z314.strictObject({
  bundles: extendApi77(z314.string().array(), {
    description: "List of bundles for which this document is relevant.",
    example: "Platinum"
  }),
  language: extendApi77(z314.string().min(2).max(14), {
    description: "Language of the document (using ISO 639-1 codes).",
    example: "fr"
  }),
  productType: extendApi77(z314.nativeEnum(ProductTypeEnum34), {
    description: `Product type associated to the document, it could be one of the following:
- <code>${ProductTypeEnum34.FLIGHT_DISRUPTION}</code> for Flight Disruption product
- <code>${ProductTypeEnum34.WITHDRAWAL}</code> for Flex product
- <code>${ProductTypeEnum34.COMPREHENSIVE}</code> for Comprehensive product
- <code>${ProductTypeEnum34.CHANGE_FOR_ANY_REASON}</code> for Change for any reason product`,
    example: ProductTypeEnum34.FLIGHT_DISRUPTION
  }),
  type: extendApi77(z314.nativeEnum(ProductAttachmentTypeEnum3), {
    description: "Type of the document.",
    example: ProductAttachmentTypeEnum3.TERMS_AND_CONDITIONS
  }),
  url: extendApi77(z314.string().url(), {
    description: "Link to the document."
  })
});

// src/resources/quote/create-quote/create-quote-res-body-quoted-bundle.schema.ts
import { z as z323 } from "zod";

// src/resources/quote/create-quote/create-quote-res-body-quoted-bundle-invalid.schema.ts
import { extendApi as extendApi85 } from "@anatine/zod-openapi";
import { z as z320 } from "zod";

// src/resources/quote/create-quote/create-quote-res-body-quoted-bundle-base.schema.ts
import { extendApi as extendApi84 } from "@anatine/zod-openapi";
import { DateTime as DateTime17 } from "luxon";
import { z as z319 } from "zod";

// src/resources/quote/create-quote/create-quote-res-body-trip.schema.ts
import { extendApi as extendApi83 } from "@anatine/zod-openapi";
import { z as z318 } from "zod";

// src/resources/quote/create-quote/create-quote-res-body-trip-with-journeys.schema.ts
import { extendApi as extendApi81 } from "@anatine/zod-openapi";

// src/resources/quote/create-quote/create-quote-res-body-journey.schema.ts
import { extendApi as extendApi78 } from "@anatine/zod-openapi";
import { DateTime as DateTime16 } from "luxon";
import { z as z315 } from "zod";
import { priceSchema as priceSchema42 } from "@repo/schema-common";
var createQuoteResBodyJourneySchema = z315.strictObject({
  flights: extendApi78(extendApi78(flightWithoutTimeZoneSchema.extend({
    arrivalDate: extendApi78(z315.string().transform((date) => DateTime16.fromISO(date)), {
      description: "The <strong>local</strong> arrival <strong>date time</strong> following the ISO 8601 format.",
      example: DateTime16.utc().setZone("Europe/Paris").plus({
        months: 1,
        hour: 2
      }).startOf("hour").toISO(),
      type: "string"
    }),
    departureDate: extendApi78(z315.string().transform((date) => DateTime16.fromISO(date)), {
      description: "The <strong>local</strong> departure <strong>date time</strong> following the ISO 8601 format.",
      example: DateTime16.utc().setZone("Europe/Paris").plus({
        months: 1
      }).startOf("hour").toISO(),
      type: "string"
    })
  }), {
    title: "FlightOutputDto"
  }).array()),
  segments: extendApi78(segmentSchema.array(), {
    description: "Segments of the journey. It also contains the flights even if you provided flights instead of segments."
  }),
  price: extendApi78(priceSchema42.optional(), {
    description: "Price of the journey."
  })
});

// src/resources/quote/create-quote/create-quote-res-body-trip-base.schema.ts
import { extendApi as extendApi80 } from "@anatine/zod-openapi";
import { z as z317 } from "zod";
import { priceSchema as priceSchema43 } from "@repo/schema-common";

// src/resources/quote/create-quote/create-quote-res-body-traveler.schema.ts
import { extendApi as extendApi79 } from "@anatine/zod-openapi";
import { z as z316 } from "zod";
import { AgeRangeEnum as AgeRangeEnum8 } from "@repo/lib-koala-types";
import { countryCodeSchema as countryCodeSchema15 } from "@repo/schema-common";
var createQuoteResBodyTravelerSchema = extendApi79(z316.strictObject({
  ageRange: extendApi79(z316.nativeEnum(AgeRangeEnum8).default(AgeRangeEnum8.ADULT), {
    default: AgeRangeEnum8.ADULT,
    description: `The ageRange are the following:
- <code>${AgeRangeEnum8.INFANT}</code> (0-23 months old)
- <code>${AgeRangeEnum8.CHILD}</code> (2-11 years old)
- <code>${AgeRangeEnum8.ADULT}</code> (12 - 64 years old)
- <code>${AgeRangeEnum8.SENIOR}</code> (65+ years old)`,
    example: AgeRangeEnum8.ADULT
  }),
  residenceCountryCode: extendApi79(countryCodeSchema15.optional(), {
    description: "A two-letter ISO 3166-1-alpha-2 country code.",
    example: "FR",
    type: "string"
  })
}));

// src/resources/quote/create-quote/create-quote-res-body-trip-base.schema.ts
var createQuoteResBodyTripBaseSchema = z317.strictObject({
  price: extendApi80(priceSchema43.optional(), {
    description: "Price of the trip"
  }),
  travelers: extendApi80(extendApi80(createQuoteResBodyTravelerSchema, {
    title: "TravelerDto"
  }).array().min(1), {
    description: "Travelers that will be part of the trip."
  })
});

// src/resources/quote/create-quote/create-quote-res-body-trip-with-journeys.schema.ts
var createQuoteResBodyTripWithJourneysSchema = extendApi81(createQuoteResBodyTripBaseSchema.extend({
  journeys: extendApi81(extendApi81(createQuoteResBodyJourneySchema, {
    title: "JourneyOutputDto"
  }).array(), {
    description: "Journeys that constitute the trip."
  }),
  stays: extendApi81(extendApi81(staySchema.array().optional(), {
    title: "StayOutputDto"
  }), {
    description: "Stays that constitute the trip."
  })
}), {
  title: "QuoteTripWithJourneysOutputDto"
});

// src/resources/quote/create-quote/create-quote-res-body-trip-with-stays.schema.ts
import { extendApi as extendApi82 } from "@anatine/zod-openapi";
var createQuoteResBodyTripWithStaysSchema = extendApi82(createQuoteResBodyTripBaseSchema.extend({
  stays: extendApi82(extendApi82(staySchema.array(), {
    title: "StayOutputDto"
  }), {
    description: "Stays that constitute the trip."
  }),
  journeys: extendApi82(extendApi82(createQuoteResBodyJourneySchema, {
    title: "JourneyOutputDto"
  }).array().optional(), {
    description: "Journeys that constitute the trip."
  })
}), {
  title: "QuoteTripWithStaysOutputDto"
});

// src/resources/quote/create-quote/create-quote-res-body-trip.schema.ts
var createQuoteResBodyTripSchema = extendApi83(z318.union([
  createQuoteResBodyTripWithJourneysSchema,
  createQuoteResBodyTripWithStaysSchema
]), {
  description: "The quoted trip."
});

// src/resources/quote/create-quote/create-quote-res-body-quoted-bundle-base.schema.ts
var createQuoteResBodyQuotedBundleBaseSchema = z319.strictObject({
  bundle: extendApi84(z319.string().max(255), {
    description: "Name of the quoted products bundle.",
    example: "Platinum"
  }),
  date: extendApi84(z319.string().transform((date) => DateTime17.fromISO(date)), {
    description: "Quote generation datetime.",
    example: DateTime17.utc().toString(),
    format: "date-time"
  }),
  trip: createQuoteResBodyTripSchema
});

// src/resources/quote/create-quote/create-quote-res-body-quoted-bundle-invalid.schema.ts
var createQuoteResBodyInvalidQuotedBundleSchema = extendApi85(createQuoteResBodyQuotedBundleBaseSchema.extend({
  reasons: extendApi85(z320.string().array(), {
    description: "Explanation of why the quote is invalid.",
    example: [
      "Too late to sell this product."
    ]
  }),
  valid: extendApi85(z320.literal(false), {
    description: `You must use this property to determine whether the quote is valid or not.
    <br/><strong>\u26A0\uFE0F Invalid quotes cannot be shown to traveler (= you cannot sell this product).</strong>`
  })
}), {
  description: "Invalid quote returned for information, you cannot show to your user.",
  title: "InvalidQuoteOutputDto"
});

// src/resources/quote/create-quote/create-quote-res-body-quoted-bundle-valid.schema.ts
import { extendApi as extendApi87 } from "@anatine/zod-openapi";
import { DateTime as DateTime19 } from "luxon";
import { z as z322 } from "zod";
import { MAX_DECIMAL_PRECISION, priceAmountSchema, priceSchema as priceSchema44 } from "@repo/schema-common";

// src/resources/quote/create-quote/create-quote-res-body-contract.schema.ts
import { extendApi as extendApi86 } from "@anatine/zod-openapi";
import { DateTime as DateTime18 } from "luxon";
import { z as z321 } from "zod";
import { ProductTypeEnum as ProductTypeEnum35 } from "@repo/lib-koala-types";
var createQuoteResBodyClaimMaximumCompensationSchema = z321.strictObject({
  amount: extendApi86(z321.number().positive(), {
    description: "The maximum amount of compensation that can be paid out.",
    example: 90
  }),
  currency: extendApi86(currenciesSchema, {
    description: "The currency of the maximum amount of compensation that can be paid out in ISO 4217 format.",
    example: "EUR"
  })
});
var createQuoteResBodyContractSchema = z321.strictObject({
  type: extendApi86(z321.enum([
    ProductTypeEnum35.FLIGHT_DISRUPTION,
    ProductTypeEnum35.CHANGE_FOR_ANY_REASON,
    ProductTypeEnum35.WITHDRAWAL,
    ProductTypeEnum35.BAGGAGE_DISRUPTION,
    ProductTypeEnum35.CANCELLATION_WITHOUT_PROOF
  ]), {
    description: "Type of the product.",
    enum: [
      ProductTypeEnum35.FLIGHT_DISRUPTION,
      ProductTypeEnum35.CHANGE_FOR_ANY_REASON,
      ProductTypeEnum35.WITHDRAWAL,
      ProductTypeEnum35.BAGGAGE_DISRUPTION,
      ProductTypeEnum35.CANCELLATION_WITHOUT_PROOF
    ]
  }),
  deadline: extendApi86(z321.string().transform((date) => DateTime18.fromISO(date)), {
    description: "If you create a subscription with the quote, this date marks the expiration of the customer's claim period, after which the contract can no longer be claimed.",
    example: DateTime18.utc().plus({
      years: 1
    }).toString(),
    format: "date-time"
  }),
  maximumCompensation: extendApi86(createQuoteResBodyClaimMaximumCompensationSchema, {
    description: "The contract's maximum liability, which is the highest amount of compensation that can be paid out."
  })
});

// src/resources/quote/create-quote/create-quote-res-body-quoted-bundle-valid.schema.ts
var createQuoteResBodyValidQuotedBundleSchema = extendApi87(createQuoteResBodyQuotedBundleBaseSchema.extend({
  expirationDate: extendApi87(z322.string().transform((date) => DateTime19.fromISO(date)), {
    description: "Date after which a quote will no longer be valid, i.e., you cannot subscribe to the given quote once this date is passed.",
    example: DateTime19.utc().plus({
      hours: 4
    }).toString(),
    format: "date-time"
  }),
  contracts: extendApi87(z322.array(createQuoteResBodyContractSchema).optional(), {
    description: "Details related to the potential contract that can be created with this quote."
  }),
  price: extendApi87(priceSchema44.extend({
    partnerCommission: extendApi87(priceAmountSchema.optional(), {
      description: "Partner commission with a maximum of 4 decimals.",
      example: 20.1
    })
  }), {
    description: "End customer price (including taxes and your commission)."
  }),
  pricePerTraveler: extendApi87(priceSchema44, {
    description: "The average end customer price per traveler."
  }).optional(),
  pricePerBag: extendApi87(priceSchema44, {
    description: "The average end customer price per bag. Relevant in the case of baggage disruption products."
  }).optional(),
  priceDelta: extendApi87(priceSchema44.extend({
    amount: extendApi87(z322.number(), {
      description: `Amount of the price with a maximum of ${MAX_DECIMAL_PRECISION} decimals. It might be negative.`,
      example: 3.86
    })
  }).optional(), {
    description: `Difference between the price of the original subscription and the price of this quote. This is typically the amount you should invoice or refund to your customer.
      <br/><strong>\u2139\uFE0F This field will be available only if you make a quote to update a subscription (i.e. by providing the \`bookingNumber\`) with a single planning trip.</strong>`
  }),
  reference: extendApi87(z322.string(), {
    description: "Quote identifier. This reference will be used for turning a quote into a subscription.",
    example: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.e30.UgdscV_5h6HnNxcA9UoIk36H9b1ISEUJ-meuZiLUwM8"
  }),
  valid: extendApi87(z322.literal(true), {
    description: `You must use this property to determine whether the quote is valid or not.
    <br/><strong>\u26A0\uFE0F Invalid quotes cannot be shown to traveler (= you cannot sell this product).</strong>`
  })
}), {
  description: "Valid quote which you can show to your user.",
  title: "ValidQuoteOutputDto"
});

// src/resources/quote/create-quote/create-quote-res-body-quoted-bundle.schema.ts
var createQuoteResBodyQuotedBundleSchema = z323.union([
  createQuoteResBodyValidQuotedBundleSchema,
  createQuoteResBodyInvalidQuotedBundleSchema
]);

// src/resources/quote/create-quote/create-quote-res-body.schema.ts
var createQuoteResBodySchema = z324.strictObject({
  data: extendApi88(z324.strictObject({
    id: extendApi88(z324.string().uuid(), {
      description: "Unique identifier of the quote result.",
      example: "94e70133-8108-4ed8-a929-8ded4113311c"
    }),
    documents: extendApi88(createQuoteResBodyDocumentSchema.array(), {
      description: "List of the documents related to the products quoted.",
      title: "DocumentOutputDto"
    }),
    quotes: extendApi88(createQuoteResBodyQuotedBundleSchema.array(), {
      description: `Generated quotes for the trips.
  \u26A0\uFE0F <strong>You can have a valid or invalid quote:</strong>
  - Valid quotes can be shown to the traveler.
  - Invalid quotes cannot be shown to traveler (= you cannot sell this product), we only show this for information purpose.
    `
    })
  }), {
    title: "CreateQuoteDataOutputDto"
  }).array()
});

// src/resources/quote/create-quote/quoted-bundle-base.schema.ts
import { DateTime as DateTime20 } from "luxon";
import { z as z325 } from "zod";
var quotedBundleBaseSchema = z325.strictObject({
  bundle: z325.string().max(255),
  date: z325.string().transform((date) => DateTime20.fromISO(date)),
  trip: createQuoteResBodyTripSchema
});

// src/resources/quote/create-quote/quoted-bundle-invalid.schema.ts
import { z as z326 } from "zod";
var quotedBundleInvalidSchema = quotedBundleBaseSchema.extend({
  reasons: z326.string().array(),
  valid: z326.literal(false)
});

// src/resources/quote/create-quote/quoted-bundle-valid.schema.ts
import { DateTime as DateTime21 } from "luxon";
import { z as z327 } from "zod";
import { priceSchema as priceSchema45 } from "@repo/schema-common";
var quotedBundleValidSchema = quotedBundleBaseSchema.extend({
  expirationDate: z327.string().transform((date) => DateTime21.fromISO(date)),
  contracts: z327.array(createQuoteResBodyContractSchema).optional(),
  price: priceSchema45,
  priceDelta: priceSchema45.optional(),
  reference: z327.string(),
  valid: z327.literal(true)
});

// src/resources/quote/create-bundle-quote/create-bundle-quote-req-body.schema.ts
var createBundleQuoteReqBodySchema = z328.strictObject({
  data: z328.strictObject({
    trip: createQuoteReqBodyTripSchema,
    currency: isoCurrencySchema9,
    bookingNumber: z328.string().max(255).optional(),
    quoteDate: dateTimeWithUtcOffsetSchema27.optional(),
    retailPrice: priceSchema46.optional(),
    distributionContext: distributionContextSchema.optional()
  })
});

// src/resources/quote/create-bundle-quote/create-bundle-quote-req-params.schema.ts
import { z as z329 } from "zod";
var createBundleQuoteReqParamsSchema = z329.strictObject({
  partnerId: z329.string().uuid(),
  bundleId: z329.string().uuid()
});

// src/resources/quote/create-bundle-quote/create-bundle-quote-res-body.schema.ts
import { z as z330 } from "zod";
var createBundleQuoteResBodySchema = z330.strictObject({
  data: z330.strictObject({
    quote: createQuoteResBodyQuotedBundleSchema
  })
});

// src/resources/quote/decode-quote/decode-quote.openapi-document.ts
import { generateSchema } from "@anatine/zod-openapi";
import z359 from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema33 } from "@repo/schema-common";

// src/exceptions/bad-request-exception.schema.ts
import { extendApi as extendApi90 } from "@anatine/zod-openapi";
import { z as z332 } from "zod";

// src/exceptions/base-exception.schema.ts
import { extendApi as extendApi89 } from "@anatine/zod-openapi";
import { z as z331 } from "zod";
var baseExceptionSchema = extendApi89(z331.strictObject({
  code: extendApi89(z331.number(), {
    description: "That should match the HTTP status code.",
    example: 400
  }),
  message: extendApi89(z331.string(), {
    description: "Small error message which is related to the code.",
    example: "Bad Request"
  }),
  reasons: extendApi89(z331.array(z331.string()), {
    description: "More explicit reasons why the error happened. It may help you understand what went wrong during the integration.",
    example: [
      "No price provided",
      "Invalid quote reference"
    ]
  }),
  type: extendApi89(z331.string(), {
    description: "A URI reference that identifies the problem type.",
    example: "/problems/empty-bundle"
  })
}), {
  title: "object"
});

// src/exceptions/bad-request-exception.schema.ts
var badRequestExceptionSchema = extendApi90(z332.strictObject({
  error: extendApi90(baseExceptionSchema, {
    example: {
      code: 400,
      message: "Bad Request",
      reasons: [
        "No price provided",
        "Currency CHF not supported"
      ],
      type: ""
    }
  })
}), {
  title: "BadRequestExceptionSchema"
});

// src/exceptions/conflict-exception.schema.ts
import { extendApi as extendApi91 } from "@anatine/zod-openapi";
import { z as z333 } from "zod";
var conflictExceptionSchema = extendApi91(z333.strictObject({
  error: extendApi91(baseExceptionSchema, {
    example: {
      code: 409,
      message: "Conflict",
      reasons: [
        "Subscription has already been created"
      ],
      type: ""
    }
  })
}), {
  title: "ConflictExceptionSchema"
});

// src/exceptions/exception-type.ts
var ExceptionType = /* @__PURE__ */ function(ExceptionType2) {
  ExceptionType2["DEFAULT"] = "/";
  ExceptionType2["MISSING_CONFIGURATION_PROPERTY"] = "MISSING_CONFIGURATION_PROPERTY";
  ExceptionType2["NO_ACTIVE_CONTRACTS"] = "NO_ACTIVE_CONTRACTS";
  return ExceptionType2;
}({});

// src/exceptions/forbidden-exception.schema.ts
import { extendApi as extendApi92 } from "@anatine/zod-openapi";
import { z as z334 } from "zod";
var forbiddenExceptionSchema = extendApi92(z334.strictObject({
  error: extendApi92(baseExceptionSchema, {
    example: {
      code: 403,
      message: "Forbidden",
      reasons: [
        "The contract has expired."
      ],
      type: ""
    }
  })
}), {
  title: "ForbiddenExceptionSchema"
});

// src/exceptions/gone-exception.schema.ts
import { extendApi as extendApi93 } from "@anatine/zod-openapi";
import { z as z335 } from "zod";
var goneExceptionSchema = extendApi93(z335.strictObject({
  error: extendApi93(baseExceptionSchema, {
    example: {
      code: 410,
      message: "Gone",
      reasons: [
        "Contract has already been deleted"
      ],
      type: ""
    }
  })
}), {
  title: "GoneExceptionSchema"
});

// src/exceptions/not-found-exception.schema.ts
import { extendApi as extendApi94 } from "@anatine/zod-openapi";
import { z as z336 } from "zod";
var notFoundExceptionSchema = extendApi94(z336.strictObject({
  error: extendApi94(baseExceptionSchema, {
    example: {
      code: 404,
      message: "Not found",
      reasons: [
        'Contract "4bccd3c0-b231-4745-a928-e378acd340d0" not found.'
      ],
      type: ""
    }
  })
}), {
  title: "NotFoundExceptionSchema"
});

// src/exceptions/not-implemented-exception.schema.ts
import { extendApi as extendApi95 } from "@anatine/zod-openapi";
import { z as z337 } from "zod";
var notImplementedExceptionSchema = extendApi95(z337.strictObject({
  error: extendApi95(baseExceptionSchema, {
    example: {
      code: 501,
      message: "Not implemented",
      reasons: [
        "The method has not been implemented yet"
      ],
      type: ""
    }
  })
}), {
  title: "NotImplementedExceptionSchema"
});

// src/exceptions/unprocessable-entity-exception.schema.ts
import { extendApi as extendApi96 } from "@anatine/zod-openapi";
import { z as z338 } from "zod";
var unprocessableEntityExceptionSchema = extendApi96(z338.strictObject({
  error: extendApi96(baseExceptionSchema, {
    example: {
      code: 422,
      message: "Unprocessable entity",
      reasons: [
        'Unprocessable quote from booking id "4bccd3c0-b231-4745-a928-e378acd340d0".'
      ],
      type: ""
    }
  })
}), {
  title: "GoneExceptionSchema"
});

// src/exceptions/unauthorized-exception.schema.ts
import { extendApi as extendApi97 } from "@anatine/zod-openapi";
import { z as z339 } from "zod";
var unauthorizedExceptionSchema = extendApi97(z339.strictObject({
  error: extendApi97(baseExceptionSchema, {
    example: {
      code: 401,
      message: "Invalid token",
      reasons: [
        "Token expired",
        "Corrupted token"
      ],
      type: ""
    }
  })
}), {
  title: "UnauthorizedExceptionSchema"
});

// src/resources/quote/decode-quote/decode-quote-req-body.schema.ts
import z340 from "zod";
var decodeQuoteReqBodySchema = z340.strictObject({
  data: z340.strictObject({
    quoteReference: z340.string()
  })
});

// src/resources/quote/decode-quote/partner-visible-quote-reference.schema.ts
import { priceSchema as priceSchema52 } from "@repo/schema-common";

// src/resources/quote/decode-quote/quote-reference.schema.ts
import { z as z358 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema32 } from "@repo/schema-common";

// src/resources/subscriptions/create-subscription-quote/amendment-quote-mode.ts
var AmendmentQuoteModes = {
  REPLACE: "REPLACE",
  ADD: "ADD"
};

// src/resources/quote/decode-quote/quote-reference-contract.schema.ts
import { z as z357 } from "zod";

// src/resources/quote/decode-quote/pricing-internal-data-output.schema.ts
import { z as z356 } from "zod";
import { countryCodeSchema as countryCodeSchema20, numberWithMaxDecimalPlaces } from "@repo/schema-common";

// src/resources/quote/decode-quote/quote-reference-flight.schema.ts
import { z as z343 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema28 } from "@repo/schema-common";

// src/resources/quote/decode-quote/quote-reference-airport.schema.ts
import z342 from "zod";
import { countryCodeSchema as countryCodeSchema17 } from "@repo/schema-common";

// src/resources/quote/decode-quote/quote-reference-country.schema.ts
import z341 from "zod";
import { GeographicalAreaEnum as GeographicalAreaEnum2 } from "@repo/lib-koala-types";
import { countryCodeSchema as countryCodeSchema16 } from "@repo/schema-common";
var quoteReferenceCountrySchema = z341.strictObject({
  geographicalArea: z341.nativeEnum(GeographicalAreaEnum2).or(z341.string()),
  id: countryCodeSchema16,
  name: z341.string().optional(),
  insuranceTax: z341.number().nullable().optional(),
  additionalTax: z341.number().nullable().optional(),
  serviceTax: z341.number().nullable().optional(),
  region: z341.string().nullable().optional(),
  subregion: z341.string().nullable().optional(),
  regionCode: z341.string().nullable().optional(),
  subregionCode: z341.string().nullable().optional()
});

// src/resources/quote/decode-quote/quote-reference-airport.schema.ts
var quoteReferenceAirportSchema = z342.strictObject({
  id: z342.string().length(3),
  country: quoteReferenceCountrySchema,
  timeZoneRegionName: z342.string(),
  name: z342.string().optional(),
  type: z342.string().optional(),
  countryId: countryCodeSchema17.optional(),
  active: z342.boolean().optional(),
  city: z342.string().optional(),
  icao: z342.string().optional(),
  iata: z342.string().optional(),
  latitude: z342.number().optional(),
  longitude: z342.number().optional()
});

// src/resources/quote/decode-quote/quote-reference-flight.schema.ts
var quoteReferenceFlightSchema = baseFlightSchema.extend({
  arrivalDate: dateTimeWithUtcOffsetSchema28,
  departureDate: dateTimeWithUtcOffsetSchema28,
  arrivalAirport: quoteReferenceAirportSchema.optional(),
  departureAirport: quoteReferenceAirportSchema.optional(),
  statuses: z343.array(z343.string()).optional()
});

// src/resources/quote/decode-quote/quote-reference-trip.schema.ts
import { z as z355 } from "zod";
import { priceSchema as priceSchema51 } from "@repo/schema-common";

// src/resources/quote/decode-quote/quote-reference-journey.schema.ts
import { z as z351 } from "zod";
import { priceSchema as priceSchema49 } from "@repo/schema-common";

// src/resources/quote/decode-quote/quote-reference-segment.schema.ts
import { z as z350 } from "zod";

// src/resources/quote/decode-quote/quote-reference-flight-segment.schema.ts
import { z as z346 } from "zod";
import { priceSchema as priceSchema47 } from "@repo/schema-common";

// src/resources/quote/decode-quote/quote-reference-flight-segment-route.schema.ts
import z344 from "zod";
import { RouteModeEnum as RouteModeEnum6 } from "@repo/lib-koala-types";
var quoteReferenceFlightSegmentRouteTransportIdentifierSchema = z344.strictObject({
  airlineIata: z344.string(),
  number: z344.string()
});
var quoteReferenceFlightSegmentRouteSchema = z344.strictObject({
  mode: z344.literal(RouteModeEnum6.FLIGHT),
  transportIdentifier: quoteReferenceFlightSegmentRouteTransportIdentifierSchema
});

// src/resources/quote/decode-quote/quote-reference-flight-segment-stop.schema.ts
import z345 from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema29 } from "@repo/schema-common";
var quoteReferenceFlightSegmentStopSchema = z345.strictObject({
  date: dateTimeWithUtcOffsetSchema29,
  iata: z345.string().min(3).max(3),
  country: quoteReferenceCountrySchema,
  airport: quoteReferenceAirportSchema
});

// src/resources/quote/decode-quote/quote-reference-flight-segment.schema.ts
var quoteReferenceFlightSegmentSchema = z346.strictObject({
  id: z346.string().uuid().optional(),
  arrivalStop: quoteReferenceFlightSegmentStopSchema,
  departureStop: quoteReferenceFlightSegmentStopSchema,
  price: priceSchema47.optional(),
  route: quoteReferenceFlightSegmentRouteSchema
});

// src/resources/quote/decode-quote/quote-reference-generic-segment.schema.ts
import { z as z349 } from "zod";
import { priceSchema as priceSchema48 } from "@repo/schema-common";

// src/resources/quote/decode-quote/quote-reference-generic-segment-route.schema.ts
import z347 from "zod";
import { RouteModeEnum as RouteModeEnum7 } from "@repo/lib-koala-types";
var quoteReferenceGenericSegmentRouteSchema = z347.strictObject({
  mode: z347.literal(RouteModeEnum7.BUS).or(z347.literal(RouteModeEnum7.TRAIN)).or(z347.literal(RouteModeEnum7.FERRY)),
  transportIdentifier: z347.string(),
  externalId: z347.string().optional(),
  partnerId: z347.string().optional()
});

// src/resources/quote/decode-quote/quote-reference-generic-segment-stop.schema.ts
import { z as z348 } from "zod";
import { StopStatusEnum, StopStatusProviderEnum } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema30 } from "@repo/schema-common";
var genericSegmentStopStatusSchema = z348.strictObject({
  status: z348.nativeEnum(StopStatusEnum),
  provider: z348.nativeEnum(StopStatusProviderEnum),
  delay: z348.number().optional(),
  providedAt: dateTimeWithUtcOffsetSchema30
});
var quoteReferenceGenericSegmentStopSchema = z348.strictObject({
  date: dateTimeWithUtcOffsetSchema30,
  externalId: z348.string(),
  partnerId: z348.string(),
  identifier: z348.string().optional(),
  latitude: z348.number().optional(),
  longitude: z348.number().optional(),
  name: z348.string(),
  timezone: z348.string(),
  statuses: z348.array(genericSegmentStopStatusSchema).optional(),
  uic: z348.string().optional(),
  country: quoteReferenceCountrySchema
});

// src/resources/quote/decode-quote/quote-reference-generic-segment.schema.ts
var quoteReferenceGenericSegmentSchema = z349.strictObject({
  id: z349.string().uuid().optional(),
  arrivalStop: quoteReferenceGenericSegmentStopSchema,
  departureStop: quoteReferenceGenericSegmentStopSchema,
  price: priceSchema48.optional(),
  route: quoteReferenceGenericSegmentRouteSchema
});

// src/resources/quote/decode-quote/quote-reference-segment.schema.ts
var quoteReferenceSegmentSchema = z350.union([
  quoteReferenceFlightSegmentSchema,
  quoteReferenceGenericSegmentSchema
]);

// src/resources/quote/decode-quote/quote-reference-journey.schema.ts
var quoteReferenceJourneySchema = z351.strictObject({
  id: z351.string().uuid(),
  flights: quoteReferenceFlightSchema.array().optional(),
  price: priceSchema49.optional(),
  segments: z351.array(quoteReferenceSegmentSchema)
});

// src/resources/quote/decode-quote/quote-reference-stay.schema.ts
import { z as z353 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema31, priceSchema as priceSchema50 } from "@repo/schema-common";

// src/resources/quote/decode-quote/quote-reference-address.schema.ts
import { z as z352 } from "zod";
import { countryCodeSchema as countryCodeSchema18 } from "@repo/schema-common";
var countryOnlyAddressSchema3 = z352.strictObject({
  countryCode: countryCodeSchema18,
  country: quoteReferenceCountrySchema
});
var richAddressSchema3 = countryOnlyAddressSchema3.extend({
  line1: z352.string().min(1).optional(),
  line2: z352.string().min(1).optional(),
  line3: z352.string().min(1).optional(),
  line4: z352.string().min(1).optional(),
  locality: z352.string().min(1).optional(),
  postcode: z352.string().min(1).optional(),
  region: z352.string().min(1).optional()
});
var quoteReferenceAddressSchema = z352.union([
  richAddressSchema3,
  countryOnlyAddressSchema3
]);

// src/resources/quote/decode-quote/quote-reference-stay.schema.ts
var quoteReferenceStaySchema = z353.strictObject({
  id: z353.string().uuid(),
  address: quoteReferenceAddressSchema,
  endDate: dateTimeWithUtcOffsetSchema31,
  name: z353.string().min(1).max(255),
  price: priceSchema50.optional(),
  startDate: dateTimeWithUtcOffsetSchema31
});

// src/resources/quote/decode-quote/quote-reference-travelers.schema.ts
import { z as z354 } from "zod";
import { AgeRangeEnum as AgeRangeEnum9 } from "@repo/lib-koala-types";
import { countryCodeSchema as countryCodeSchema19 } from "@repo/schema-common";
var quoteReferenceTravelers = z354.strictObject({
  index: z354.number().optional(),
  id: z354.string().uuid().optional(),
  ageRange: z354.nativeEnum(AgeRangeEnum9).default(AgeRangeEnum9.ADULT),
  residenceCountryCode: countryCodeSchema19.optional(),
  countryOfResidence: quoteReferenceCountrySchema.nullable().optional(),
  numberOfCheckedBags: z354.number().int().positive().optional()
});

// src/resources/quote/decode-quote/quote-reference-trip.schema.ts
var quoteReferenceTripSchema = z355.strictObject({
  id: z355.string().uuid().optional(),
  price: priceSchema51.optional(),
  travelers: z355.array(quoteReferenceTravelers),
  stays: z355.array(quoteReferenceStaySchema),
  journeys: z355.array(quoteReferenceJourneySchema)
});

// src/resources/quote/decode-quote/pricing-internal-data-output.schema.ts
var coverSchema = z356.strictObject({
  code: z356.enum([
    "FLC",
    "FLD",
    "WFG"
  ]),
  premiumAmountWithoutTaxes: numberWithMaxDecimalPlaces,
  taxesAmount: numberWithMaxDecimalPlaces,
  commissionsAmount: numberWithMaxDecimalPlaces,
  ceiling: numberWithMaxDecimalPlaces
});
var pricingInternalDataOutputSchema = z356.strictObject({
  trip: quoteReferenceTripSchema.optional(),
  minimumConnectionTimes: z356.array(z356.number()).optional(),
  quoteReference: z356.string().optional(),
  partnershipCode: z356.string().optional(),
  covers: z356.array(coverSchema).optional(),
  flight: quoteReferenceFlightSchema.optional(),
  battlefaceProductId: z356.number().optional(),
  destinationCountryCode: countryCodeSchema20.optional(),
  packages: z356.array(z356.string()).optional()
});

// src/resources/quote/decode-quote/quote-reference-contract.schema.ts
var quoteReferenceContractSchema = z357.strictObject({
  productBundleId: z357.string().uuid(),
  flight: quoteReferenceFlightSchema.optional(),
  segmentId: z357.string().uuid().optional(),
  trip: quoteReferenceTripSchema.optional(),
  travelers: z357.array(quoteReferenceTravelers),
  priceDetails: priceDetailsSchema,
  pricingEngineInternalData: pricingInternalDataOutputSchema,
  valid: z357.boolean().optional()
});

// src/resources/quote/decode-quote/quote-reference.schema.ts
var QuotePurposeEnum = /* @__PURE__ */ function(QuotePurposeEnum2) {
  QuotePurposeEnum2["CREATE"] = "CREATE";
  QuotePurposeEnum2["UPDATE"] = "UPDATE";
  return QuotePurposeEnum2;
}({});
var QuoteOriginEnum = /* @__PURE__ */ function(QuoteOriginEnum2) {
  QuoteOriginEnum2["KOALA"] = "KOALA";
  QuoteOriginEnum2["PARTNER"] = "PARTNER";
  QuoteOriginEnum2["WIDGET"] = "WIDGET";
  return QuoteOriginEnum2;
}({});
var QuoteDateSourceEnum = /* @__PURE__ */ function(QuoteDateSourceEnum2) {
  QuoteDateSourceEnum2["REQUEST"] = "REQUEST";
  QuoteDateSourceEnum2["SYSTEM"] = "SYSTEM";
  return QuoteDateSourceEnum2;
}({});
var quoteReferenceSchema = z358.strictObject({
  quoteId: z358.string().uuid(),
  quotePurpose: z358.nativeEnum(QuotePurposeEnum),
  quoteOrigin: z358.nativeEnum(QuoteOriginEnum).optional(),
  quoteDate: dateTimeWithUtcOffsetSchema32.optional(),
  quoteDateSource: z358.nativeEnum(QuoteDateSourceEnum).optional(),
  version: z358.string(),
  contracts: z358.array(quoteReferenceContractSchema),
  trip: quoteReferenceTripSchema,
  distributionContext: distributionContextSchema.optional(),
  amendment: z358.strictObject({
    mode: z358.nativeEnum(AmendmentQuoteModes),
    tripId: z358.string().uuid().optional()
  }).optional()
});

// src/resources/quote/decode-quote/partner-visible-quote-reference.schema.ts
var partnerVisibleQuoteReferenceSchema = quoteReferenceSchema.pick({
  quoteId: true,
  quotePurpose: true
}).extend({
  price: priceSchema52
});

// src/resources/quote/decode-quote/decode-quote.openapi-document.ts
var decodeQuoteResBodySchema = z359.strictObject({
  data: partnerVisibleQuoteReferenceSchema.extend({
    expirationDate: dateTimeWithUtcOffsetSchema33
  })
});
var decodeQuoteOpenApiDocument = {
  info: {
    title: "Partner API",
    version: "2.0.0"
  },
  openapi: "3.1.0",
  paths: {
    "/quotes/decode": {
      post: {
        summary: "Decipher and decode a quote reference JWE payload.",
        requestBody: {
          required: true,
          content: {
            "application/json": {
              schema: {
                $ref: "#/components/schemas/DecodeQuoteRequestBody"
              }
            }
          }
        },
        responses: {
          200: {
            description: "The deciphered/decoded quote payload.",
            content: {
              "application/json": {
                schema: {
                  $ref: "#/components/schemas/DecodeQuoteResponseBody"
                }
              }
            }
          },
          "400": {
            description: "Incorrect input.",
            content: {
              "application/json": {
                schema: {
                  $ref: "#/components/schemas/BadRequestResBody"
                }
              }
            }
          },
          "401": {
            description: "Authorization issue.",
            content: {
              "application/json": {
                schema: {
                  $ref: "#/components/schemas/UnauthorizedResBody"
                }
              }
            }
          }
        },
        tags: [
          "Quotes"
        ],
        security: [
          {
            Bearer: []
          }
        ]
      }
    }
  },
  components: {
    schemas: {
      DecodeQuoteRequestBody: generateSchema(decodeQuoteReqBodySchema),
      DecodeQuoteResponseBody: generateSchema(decodeQuoteResBodySchema),
      BadRequestResBody: generateSchema(badRequestExceptionSchema),
      UnauthorizedResBody: generateSchema(unauthorizedExceptionSchema)
    }
  }
};

// src/resources/quote/decode-quote/decode-quote-res-body.schema.ts
import z360 from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema34 } from "@repo/schema-common";
var decodeQuoteKoalaResBodySchema = z360.strictObject({
  data: quoteReferenceSchema.extend({
    expirationDate: dateTimeWithUtcOffsetSchema34
  })
});
var decodeQuoteResBodySchema2 = z360.strictObject({
  data: z360.union([
    quoteReferenceSchema.extend({
      expirationDate: dateTimeWithUtcOffsetSchema34
    }),
    partnerVisibleQuoteReferenceSchema.extend({
      expirationDate: dateTimeWithUtcOffsetSchema34
    })
  ])
});

// src/resources/quote/delete-quote/delete-quote-req-params.schema.ts
import { z as z361 } from "zod";
var deleteQuoteReqParamsSchema = z361.strictObject({
  id: z361.string().uuid()
});

// src/resources/quote/delete-quote/delete-quote-res-body.schema.ts
import { z as z362 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema35 } from "@repo/schema-common";
var deleteQuoteResBodyDataSchema = z362.strictObject({
  canceledAt: dateTimeWithUtcOffsetSchema35
});
var deleteQuoteResBodySchema = z362.strictObject({
  data: deleteQuoteResBodyDataSchema
});

// src/resources/subscriptions/create-subscription/card-payment-details.open-api-schema.ts
var cardPaymentDetailsSchema = {
  type: "object",
  description: "Payment details for processing the subscription. These fields are required **only when Koala acts as the merchant of record**. When provided, they are automatically tokenized by a PCI DSS Level 1\u2013compliant proxy service before reaching Koala's servers. Koala never stores or processes raw cardholder data (CHD).",
  properties: {
    cardType: {
      type: "string",
      enum: [
        "CREDIT",
        "DEBIT"
      ],
      description: "Type of the card used for payment. 'CREDIT' or 'DEBIT'.",
      example: "CREDIT"
    },
    cardCode: {
      type: "string",
      enum: [
        "VI",
        "MC",
        "AX",
        "DI",
        "DC",
        "JC",
        "MA"
      ],
      description: "Two-letter card brand code (e.g., 'VI' for Visa, 'MC' for Mastercard, 'AX' for American Express).",
      example: "VI"
    },
    cardNumber: {
      type: "string",
      description: "Primary Account Number (PAN).",
      example: "4111111111111111"
    },
    expirationDate: {
      type: "string",
      pattern: "^(0[1-9]|1[0-2])[0-9]{2}$",
      description: "Card expiration date in MMYY format.",
      example: "1226"
    },
    seriesCode: {
      type: "string",
      pattern: "^[0-9]{3,4}$",
      description: "Card Verification Value (CVV/CVC).",
      example: "123"
    },
    cardHolderName: {
      type: "string",
      minLength: 1,
      maxLength: 255,
      description: "Name of the cardholder as it appears on the card.",
      example: "Mary Dancer"
    },
    threeDomainSecurity: {
      type: "object",
      description: "Contains 3-D Secure (3DS 2.x) authentication results.",
      properties: {
        eci: {
          type: "string",
          pattern: "^[0-9]{2}$",
          description: "Electronic Commerce Indicator returned by the issuer. Common values: `05` (Visa fully authenticated), `06` (Visa attempted), `02` (Mastercard authenticated), `01` (Mastercard attempted).",
          example: "05"
        },
        cavv: {
          type: "string",
          description: "Cardholder Authentication Verification Value (CAVV/AVV) generated by the issuer during 3DS authentication.",
          example: "AAABBIIFmAAAAAABJRGABQAAAAA="
        },
        threeDSVersion: {
          type: "string",
          pattern: "^2\\.[0-9]+(\\.[0-9]+)?$",
          description: "Optional: EMV 3DS protocol version used during authentication (e.g., `2.2.0`, `2.3.1`).",
          example: "2.3.1"
        }
      },
      required: [
        "eci",
        "cavv"
      ],
      additionalProperties: false
    }
  },
  required: [
    "cardCode",
    "cardNumber",
    "seriesCode",
    "cardHolderName",
    "expirationDate"
  ],
  additionalProperties: false
};

// src/resources/subscriptions/create-subscription/create-subscription-req-body.schema.ts
import { extendApi as extendApi98 } from "@anatine/zod-openapi";
import { DateTime as DateTime22 } from "luxon";
import { z as z363 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema36 } from "@repo/schema-common";
var createSubscriptionReqBodySchema = z363.strictObject({
  data: extendApi98(z363.strictObject({
    bookingNumber: extendApi98(z363.string().min(4).max(255), {
      description: "The booking reference number associated with the subscription. It must be unique. This booking number may be used inside our communications and will help to identify the booking when we interact together. <br/><strong>\u26A0\uFE0F Please use the booking number that you share with your customers.</strong>",
      example: "5K7SXD"
    }),
    customer: extendApi98(createSubscriptionReqBodyCustomerSchema, {
      description: "Customer who booked the trip."
    }),
    quotes: extendApi98(z363.strictObject({
      reference: extendApi98(z363.string(), {
        description: "Reference you got when you call the POST /quotes endpoint."
      }),
      travelers: extendApi98(createSubscriptionReqBodyTravelerSchema.array().min(1), {
        description: "Details of the travelers participating in the trip.<br/><strong>\u26A0\uFE0F The age range of travelers should be equivalent to that of the quote.</strong>"
      })
    }).array().min(1), {
      description: "List of quotes you want to subscribe to."
    }),
    payment: encryptedCardPaymentDetailsSchema.optional(),
    subscriptionDate: extendApi98(dateTimeWithUtcOffsetSchema36.optional(), {
      example: DateTime22.utc().plus({
        months: 1
      }).startOf("hour").plus({
        hours: 8,
        minutes: 21
      }).toString()
    })
  }), {
    hideDefinitions: [
      "subscriptionDate"
    ]
  })
});

// src/resources/subscriptions/create-subscription/create-subscription-req-query.schema.ts
import { extendApi as extendApi99 } from "@anatine/zod-openapi";
var createSubscriptionReqQuerySchema = extendApi99(sideEffectsSchema, {
  hideDefinitions: [
    "sideEffects"
  ]
});

// src/resources/subscriptions/create-subscription/create-subscription-res-body.schema.ts
import { extendApi as extendApi100 } from "@anatine/zod-openapi";
import { z as z364 } from "zod";
import { bookingNumberSchema as bookingNumberSchema6, priceAmountSchema as priceAmountSchema2, priceSchema as priceSchema53 } from "@repo/schema-common";
var createSubscriptionResBodySchema = z364.strictObject({
  data: z364.strictObject({
    bookingNumber: extendApi100(bookingNumberSchema6, {
      description: "The booking reference number associated with the subscription. It must be unique. This booking number may be used inside our communications and will help to identify the booking when we interact together. <br/><strong>\u26A0\uFE0F Please use the booking number that you share with your customers.</strong>",
      example: "5K7SXD"
    }),
    contracts: extendApi100(z364.array(z364.union([
      flightContractSchema,
      tripContractSchema
    ])).min(1), {
      description: "List of contracts linked to this subscription."
    }),
    price: extendApi100(priceSchema53.extend({
      partnerCommission: extendApi100(priceAmountSchema2.optional(), {
        description: "Partner commission with a maximum of 4 decimals.",
        example: 20.1
      })
    }), {
      description: "Total price of the quotes you provided."
    })
  })
});

// src/resources/subscriptions/create-subscription/customer.schema.ts
import { z as z365 } from "zod";
import { emailSchema as emailSchema6, languageSchema as languageSchema3 } from "@repo/schema-common";
var customerSchema = z365.strictObject({
  birthday: birthdaySchema.optional(),
  email: emailSchema6,
  firstName: z365.string().min(1).max(255),
  language: languageSchema3,
  lastName: z365.string().min(1).max(255),
  phoneNumber: z365.string().min(1).optional()
});

// src/resources/subscriptions/create-subscription-quote/create-subscription-quote-req-body.schema.ts
import { z as z366 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema37, isoCurrencySchema as isoCurrencySchema10 } from "@repo/schema-common";
var createSubscriptionQuoteReqBodySchema = z366.strictObject({
  data: z366.strictObject({
    tripId: z366.string().uuid().optional(),
    currency: isoCurrencySchema10,
    trip: createQuoteReqBodyTripSchema,
    quoteDate: dateTimeWithUtcOffsetSchema37.optional(),
    distributionContext: distributionContextSchema.optional()
  })
});

// src/resources/subscriptions/create-subscription-quote/create-subscription-quote-req-params.schema.ts
import { z as z367 } from "zod";
import { bookingNumberSchema as bookingNumberSchema7 } from "@repo/schema-common";
var createSubscriptionQuoteReqParamsSchema = z367.strictObject({
  bookingNumber: bookingNumberSchema7
});

// src/resources/subscriptions/create-subscription-quote/create-subscription-quote-res-body.schema.ts
import { z as z371 } from "zod";
import { priceSchema as priceSchema54 } from "@repo/schema-common";

// src/resources/subscriptions/create-subscription-quote/create-subscription-quote-res-body-quoted-bundle.schema.ts
import { z as z370 } from "zod";

// src/resources/subscriptions/create-subscription-quote/create-subscription-quote-res-body-quoted-bundle-invalid.schema.ts
import { z as z368 } from "zod";
var createSubscriptionQuoteResBodyInvalidQuotedBundleSchema = createQuoteResBodyInvalidQuotedBundleSchema.extend({
  bundleId: z368.string().uuid(),
  isCurrent: z368.boolean()
});

// src/resources/subscriptions/create-subscription-quote/create-subscription-quote-res-body-quoted-bundle-valid.schema.ts
import { z as z369 } from "zod";
var createSubscriptionQuoteResBodyValidQuotedBundleSchema = createQuoteResBodyValidQuotedBundleSchema.extend({
  bundleId: z369.string().uuid(),
  isCurrent: z369.boolean()
});

// src/resources/subscriptions/create-subscription-quote/create-subscription-quote-res-body-quoted-bundle.schema.ts
var createSubscriptionQuoteResBodyQuotedBundleSchema = z370.union([
  createSubscriptionQuoteResBodyValidQuotedBundleSchema,
  createSubscriptionQuoteResBodyInvalidQuotedBundleSchema
]);

// src/resources/subscriptions/create-subscription-quote/create-subscription-quote-res-body.schema.ts
var createSubscriptionQuoteResBodySchema = z371.strictObject({
  data: z371.strictObject({
    id: z371.string().uuid(),
    mode: z371.nativeEnum(AmendmentQuoteModes),
    tripId: z371.string().uuid().optional(),
    currentPrice: priceSchema54.optional(),
    quotes: z371.array(createSubscriptionQuoteResBodyQuotedBundleSchema).min(1)
  })
});

// src/resources/subscriptions/list-subscriptions/list-subscriptions-req-query.schema.ts
import { extendApi as extendApi101 } from "@anatine/zod-openapi";
import { z as z372 } from "zod";
var LIST_SUBSCRIPTIONS_DEFAULT_PAGE_SIZE = 25;
var LIST_SUBSCRIPTIONS_MAX_PAGE_SIZE = 100;
var listSubscriptionsReqQuerySchema = z372.strictObject({
  search: extendApi101(z372.string().trim().min(1).max(255).optional(), {
    description: "Case insensitive search on the booking number, the customer first and last names, and the agent name and email.",
    example: "Doe"
  }),
  page: extendApi101(z372.coerce.number().int().min(1).default(1), {
    description: "Page to return, starting at 1.",
    example: 1,
    type: "integer"
  }),
  pageSize: extendApi101(z372.coerce.number().int().min(1).max(LIST_SUBSCRIPTIONS_MAX_PAGE_SIZE).default(LIST_SUBSCRIPTIONS_DEFAULT_PAGE_SIZE), {
    description: `Number of subscriptions per page, at most ${LIST_SUBSCRIPTIONS_MAX_PAGE_SIZE}.`,
    example: LIST_SUBSCRIPTIONS_DEFAULT_PAGE_SIZE,
    type: "integer"
  })
});

// src/resources/subscriptions/list-subscriptions/list-subscriptions-res-body.schema.ts
import { z as z376 } from "zod";

// src/resources/subscriptions/list-subscriptions/list-subscriptions-res-body-meta.schema.ts
import { extendApi as extendApi102 } from "@anatine/zod-openapi";
import { z as z373 } from "zod";
var listSubscriptionsResBodyMetaSchema = z373.strictObject({
  page: extendApi102(z373.number().int().min(1), {
    example: 1,
    type: "integer"
  }),
  pageSize: extendApi102(z373.number().int().min(1), {
    example: 25,
    type: "integer"
  }),
  total: extendApi102(z373.number().int().min(0), {
    description: "Number of subscriptions matching the query, across all pages.",
    example: 42,
    type: "integer"
  })
});

// src/resources/subscriptions/list-subscriptions/list-subscriptions-res-body-subscription.schema.ts
import { extendApi as extendApi104 } from "@anatine/zod-openapi";
import { z as z375 } from "zod";
import { bookingNumberSchema as bookingNumberSchema8, priceSchema as priceSchema55 } from "@repo/schema-common";

// src/resources/subscriptions/list-subscriptions/list-subscriptions-res-body-customer.schema.ts
import { extendApi as extendApi103 } from "@anatine/zod-openapi";
import { z as z374 } from "zod";
import { emailSchema as emailSchema7 } from "@repo/schema-common";
var listSubscriptionsResBodyCustomerSchema = z374.strictObject({
  email: extendApi103(emailSchema7, {
    example: "john.doe@example.com"
  }),
  firstName: extendApi103(z374.string(), {
    example: "John"
  }),
  lastName: extendApi103(z374.string(), {
    example: "Doe"
  })
});

// src/resources/subscriptions/list-subscriptions/list-subscriptions-res-body-subscription.schema.ts
var listSubscriptionsResBodySubscriptionSchema = z375.strictObject({
  travelAgentId: extendApi104(z375.string().nullable(), {
    description: "Identifier of the travel agent who created the subscription from an agent portal, null otherwise."
  }),
  bookingNumber: extendApi104(bookingNumberSchema8, {
    example: "5K7SXD"
  }),
  bundles: extendApi104(z375.array(z375.string()).min(1), {
    description: "Names of the bundles covered by the active contracts of the subscription.",
    example: [
      "Comprehensive"
    ]
  }),
  contractIds: extendApi104(z375.array(z375.string().uuid()).min(1), {
    description: "Identifiers of the active contracts of the subscription."
  }),
  createdAt: extendApi104(z375.string().datetime(), {
    description: "Creation date of the subscription, in UTC.",
    example: "2026-01-15T10:30:00.000Z"
  }),
  customer: listSubscriptionsResBodyCustomerSchema,
  id: z375.string().uuid(),
  price: extendApi104(priceSchema55, {
    description: "Total price of the active contracts of the subscription."
  })
});

// src/resources/subscriptions/list-subscriptions/list-subscriptions-res-body.schema.ts
var listSubscriptionsResBodySchema = z376.strictObject({
  data: z376.array(listSubscriptionsResBodySubscriptionSchema),
  meta: listSubscriptionsResBodyMetaSchema
});

// src/resources/subscriptions/payments/apexx-payment.schema.ts
import { z as z378 } from "zod";
import { ApexxPaymentStatuses, SubscriptionPaymentTypes as SubscriptionPaymentTypes2 } from "@repo/lib-koala-types";

// src/resources/subscriptions/payments/base-payment.schema.ts
import { z as z377 } from "zod";
import { SubscriptionPaymentTypes } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema38, isoCurrencySchema as isoCurrencySchema11, priceAmountSchema as priceAmountSchema3 } from "@repo/schema-common";
var basePaymentSchema = z377.strictObject({
  id: z377.string().uuid(),
  type: z377.nativeEnum(SubscriptionPaymentTypes),
  amount: priceAmountSchema3,
  currency: isoCurrencySchema11,
  createdAt: dateTimeWithUtcOffsetSchema38,
  updatedAt: dateTimeWithUtcOffsetSchema38
});

// src/resources/subscriptions/payments/apexx-payment.schema.ts
var apexxPaymentSchema = basePaymentSchema.extend({
  type: z378.literal(SubscriptionPaymentTypes2.APEXX),
  apexxPaymentStatus: z378.nativeEnum(ApexxPaymentStatuses).nullable(),
  apexxTransactionId: z378.string().min(1).nullable(),
  apexxMerchantReference: z378.string().min(1).nullable(),
  ctTransactionId: z378.string().min(1).nullable()
});

// src/resources/subscriptions/payments/apexx-payment-creation-data.schema.ts
import { z as z380 } from "zod";
import { ApexxPaymentStatuses as ApexxPaymentStatuses2, SubscriptionPaymentTypes as SubscriptionPaymentTypes4 } from "@repo/lib-koala-types";

// src/resources/subscriptions/payments/base-payment-creation-data.schema.ts
import { extendApi as extendApi105 } from "@anatine/zod-openapi";
import { DateTime as DateTime23 } from "luxon";
import { z as z379 } from "zod";
import { SubscriptionPaymentTypes as SubscriptionPaymentTypes3 } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema39, isoCurrencySchema as isoCurrencySchema12, priceAmountSchema as priceAmountSchema4 } from "@repo/schema-common";
var basePaymentCreationDataSchema = z379.strictObject({
  type: z379.nativeEnum(SubscriptionPaymentTypes3),
  amount: priceAmountSchema4,
  currency: isoCurrencySchema12,
  initiatedAt: extendApi105(dateTimeWithUtcOffsetSchema39.refine((initiatedAt) => initiatedAt <= DateTime23.utc(), "Must not be in the future"), {
    description: "Date time at which the payment was initiated, following the ISO 8601 format with a time zone. Used instead of the request date to check the subscription payment expiration date. Defaults to the request date.",
    example: DateTime23.utc().minus({
      minutes: 5
    }).startOf("second").toISO(),
    format: "date-time",
    type: "string"
  }).optional()
});

// src/resources/subscriptions/payments/apexx-payment-creation-data.schema.ts
var apexxPaymentCreationDataSchema = basePaymentCreationDataSchema.extend({
  type: z380.literal(SubscriptionPaymentTypes4.APEXX),
  apexxPaymentStatus: z380.nativeEnum(ApexxPaymentStatuses2),
  apexxTransactionId: z380.string(),
  apexxMerchantReference: z380.string(),
  ctTransactionId: z380.string()
});

// src/resources/subscriptions/payments/commercial-gesture-payment.schema.ts
import { z as z381 } from "zod";
import { SubscriptionPaymentTypes as SubscriptionPaymentTypes5 } from "@repo/lib-koala-types";
var commercialGesturePaymentSchema = basePaymentSchema.extend({
  type: z381.literal(SubscriptionPaymentTypes5.COMMERCIAL_GESTURE)
});

// src/resources/subscriptions/payments/commercial-gesture-payment-creation-data.schema.ts
import { z as z382 } from "zod";
import { SubscriptionPaymentTypes as SubscriptionPaymentTypes6 } from "@repo/lib-koala-types";
var commercialGesturePaymentCreationDataSchema = z382.strictObject({
  type: z382.literal(SubscriptionPaymentTypes6.COMMERCIAL_GESTURE),
  notes: z382.string().min(1).max(500),
  recordedBy: z382.string().min(1).max(255)
});

// src/resources/subscriptions/payments/create-subscription-payments-req-body.schema.ts
import { z as z386 } from "zod";
import { SubscriptionPaymentFacts } from "@repo/lib-koala";

// src/resources/subscriptions/payments/payment-creation-data.schema.ts
import { z as z385 } from "zod";

// src/resources/subscriptions/payments/manual-payment-creation-data.schema.ts
import { DateTime as DateTime24 } from "luxon";
import { z as z383 } from "zod";
import { ManualPaymentMethods, SubscriptionPaymentTypes as SubscriptionPaymentTypes7 } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema40 } from "@repo/schema-common";
var manualPaymentCreationDataSchema = z383.strictObject({
  type: z383.literal(SubscriptionPaymentTypes7.MANUAL),
  paymentMethod: z383.nativeEnum(ManualPaymentMethods),
  notes: z383.string().min(1).max(500),
  receivedAt: dateTimeWithUtcOffsetSchema40.refine((receivedAt) => receivedAt <= DateTime24.utc(), {
    message: "The payment cannot be received in the future."
  }),
  recordedBy: z383.string().min(1).max(255)
});

// src/resources/subscriptions/payments/stripe-payment-creation-data.schema.ts
import { z as z384 } from "zod";
import { StripePaymentIntentStatuses, SubscriptionPaymentTypes as SubscriptionPaymentTypes8 } from "@repo/lib-koala-types";
var stripePaymentCreationDataSchema = basePaymentCreationDataSchema.extend({
  type: z384.literal(SubscriptionPaymentTypes8.STRIPE),
  stripePaymentIntentId: z384.string(),
  stripePaymentIntentStatus: z384.nativeEnum(StripePaymentIntentStatuses),
  cardLast4: z384.string().min(1).optional(),
  cardExpiryDate: z384.string().min(1).optional(),
  paymentMethod: z384.string().min(1).optional()
});

// src/resources/subscriptions/payments/payment-creation-data.schema.ts
var paymentCreationDataSchema = z385.discriminatedUnion("type", [
  apexxPaymentCreationDataSchema,
  stripePaymentCreationDataSchema,
  manualPaymentCreationDataSchema,
  commercialGesturePaymentCreationDataSchema
]);

// src/resources/subscriptions/payments/create-subscription-payments-req-body.schema.ts
var createSubscriptionPaymentsReqBodySchema = z386.strictObject({
  payments: z386.array(paymentCreationDataSchema).min(1).refine((payments) => payments.length === 1 || payments.every(({ type }) => SubscriptionPaymentFacts.isOperatorRecorded(type) === false), {
    message: "A manual payment or a commercial gesture must be the only payment of the request."
  })
});

// src/resources/subscriptions/payments/create-subscription-payments-req-params.schema.ts
import { z as z387 } from "zod";
var createSubscriptionPaymentsReqParamsSchema = z387.strictObject({
  subscriptionId: z387.string().uuid()
});

// src/resources/subscriptions/payments/create-subscription-payments-res-body.schema.ts
import { z as z388 } from "zod";
var createSubscriptionPaymentsResBodySchema = z388.strictObject({
  data: z388.strictObject({
    payments: z388.array(paymentCreationDataSchema)
  })
});

// src/resources/subscriptions/payments/get-subscription-payments-req-params.schema.ts
import { z as z389 } from "zod";
var getSubscriptionPaymentsReqParamsSchema = z389.strictObject({
  subscriptionId: z389.string().uuid()
});

// src/resources/subscriptions/payments/get-subscription-payments-res-body.schema.ts
import { z as z394 } from "zod";

// src/resources/subscriptions/payments/get-subscription-payments-res-body-data-with-payments.schema.ts
import { z as z392 } from "zod";
import { SubscriptionPaymentStatuses as SubscriptionPaymentStatuses2 } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema42, priceSchema as priceSchema56 } from "@repo/schema-common";

// src/resources/subscriptions/payments/manual-payment.schema.ts
import { z as z390 } from "zod";
import { ManualPaymentMethods as ManualPaymentMethods2, SubscriptionPaymentTypes as SubscriptionPaymentTypes9 } from "@repo/lib-koala-types";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema41 } from "@repo/schema-common";
var manualPaymentSchema = basePaymentSchema.extend({
  type: z390.literal(SubscriptionPaymentTypes9.MANUAL),
  paymentMethod: z390.nativeEnum(ManualPaymentMethods2),
  receivedAt: dateTimeWithUtcOffsetSchema41
});

// src/resources/subscriptions/payments/stripe-payment.schema.ts
import { z as z391 } from "zod";
import { StripePaymentIntentStatuses as StripePaymentIntentStatuses2, SubscriptionPaymentTypes as SubscriptionPaymentTypes10 } from "@repo/lib-koala-types";
var stripePaymentSchema = basePaymentSchema.extend({
  type: z391.literal(SubscriptionPaymentTypes10.STRIPE),
  stripePaymentIntentId: z391.string().min(1),
  stripePaymentIntentStatus: z391.nativeEnum(StripePaymentIntentStatuses2)
});

// src/resources/subscriptions/payments/get-subscription-payments-res-body-data-with-payments.schema.ts
var getSubscriptionPaymentsResBodyDataWithPaymentsSchema = z392.strictObject({
  hasPayments: z392.literal(true),
  payments: z392.array(z392.discriminatedUnion("type", [
    apexxPaymentSchema,
    stripePaymentSchema,
    manualPaymentSchema,
    commercialGesturePaymentSchema
  ])),
  status: z392.nativeEnum(SubscriptionPaymentStatuses2),
  lastUpdatedAt: dateTimeWithUtcOffsetSchema42,
  price: priceSchema56
});

// src/resources/subscriptions/payments/get-subscription-payments-res-body-data-without-payments.schema.ts
import { z as z393 } from "zod";
import { priceSchema as priceSchema57 } from "@repo/schema-common";
var getSubscriptionPaymentsResBodyDataWithoutPaymentsSchema = z393.strictObject({
  hasPayments: z393.literal(false),
  price: priceSchema57
});

// src/resources/subscriptions/payments/get-subscription-payments-res-body.schema.ts
var getSubscriptionPaymentsResBodySchema = z394.strictObject({
  data: z394.discriminatedUnion("hasPayments", [
    getSubscriptionPaymentsResBodyDataWithPaymentsSchema,
    getSubscriptionPaymentsResBodyDataWithoutPaymentsSchema
  ])
});

// src/resources/subscriptions/reopen-payment-window/reopen-payment-window-req-body.schema.ts
import { z as z395 } from "zod";
import { durationSchema as durationSchema3 } from "@repo/schema-common";
var REOPEN_PAYMENT_WINDOW_DEFAULT_EXTENSION_DURATION = "P3D";
var reopenPaymentWindowReqBodySchema = z395.strictObject({
  extensionDuration: durationSchema3.default(REOPEN_PAYMENT_WINDOW_DEFAULT_EXTENSION_DURATION),
  operatorName: z395.string().min(1).max(255)
});

// src/resources/subscriptions/reopen-payment-window/reopen-payment-window-req-params.schema.ts
import { z as z396 } from "zod";
var reopenPaymentWindowReqParamsSchema = z396.strictObject({
  id: z396.string().uuid()
});

// src/resources/subscriptions/reopen-payment-window/reopen-payment-window-res-body.schema.ts
import { z as z397 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema43 } from "@repo/schema-common";
var reopenPaymentWindowResBodySchema = z397.strictObject({
  data: z397.strictObject({
    paymentExpirationDate: dateTimeWithUtcOffsetSchema43.optional(),
    paymentRetryLink: z397.string().url()
  })
});

// src/resources/subscriptions/update-subscription/update-subscription-customer.schema.ts
import { extendApi as extendApi106 } from "@anatine/zod-openapi";
import { z as z398 } from "zod";
import { emailSchema as emailSchema8, languageSchema as languageSchema4, phoneNumberSchema as phoneNumberSchema3 } from "@repo/schema-common";
var updateSubscriptionCustomerSchema = z398.strictObject({
  firstName: extendApi106(z398.string().trim().min(1).max(255), {
    description: "First name of the customer.",
    example: "Mary"
  }),
  lastName: extendApi106(z398.string().trim().min(1).max(255), {
    description: "Last name of the customer.",
    example: "Dancer"
  }),
  email: extendApi106(emailSchema8, {
    description: "Email address of the customer.",
    example: "mary.dancer@gmail.com",
    format: "email",
    maxLength: 255
  }),
  language: extendApi106(languageSchema4, {
    description: "Language of the customer. It must be a country language code like <code>[language designator]-[region designator]</code>, with an ISO 639-1 alpha-2 code for the <code>language designator</code> and an ISO 3166-1 for the <code>region designator</code>.<br /><strong>\u26A0\uFE0FPlease be careful with filling this field as we use it to define the language used for the travelers in all our communications.</strong>",
    example: "en-GB",
    minLength: 5,
    maxLength: 5
  }),
  phoneNumber: extendApi106(phoneNumberSchema3, {
    description: "Phone number of the customer.",
    example: "+1 213 373 4253"
  }),
  birthday: birthdaySchema,
  address: addressSchema
});

// src/resources/subscriptions/update-subscription/update-subscription-req-body.schema.ts
import { extendApi as extendApi108 } from "@anatine/zod-openapi";
import { z as z400 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema44 } from "@repo/schema-common";

// src/resources/subscriptions/update-subscription/update-subscription-traveler.schema.ts
import { extendApi as extendApi107 } from "@anatine/zod-openapi";
import { z as z399 } from "zod";
import { AgeRangeEnum as AgeRangeEnum10 } from "@repo/lib-koala-types";
import { countryCodeSchema as countryCodeSchema21 } from "@repo/schema-common";
var updateSubscriptionTravelerSchema = z399.strictObject({
  firstName: extendApi107(z399.string().min(1).max(255), {
    description: "First name of the traveler.",
    example: "Mary"
  }),
  lastName: extendApi107(z399.string().min(1).max(255), {
    description: "Last name of the traveler.",
    example: "Dancer"
  }),
  ageRange: extendApi107(z399.nativeEnum(AgeRangeEnum10).default(AgeRangeEnum10.ADULT), {
    default: AgeRangeEnum10.ADULT,
    description: `We need one age range per traveler.
The ageRange are the following:
- <code>${AgeRangeEnum10.INFANT}</code> (0-23 months old)
- <code>${AgeRangeEnum10.CHILD}</code> (2-11 years old)
- <code>${AgeRangeEnum10.ADULT}</code> (12 - 64 years old)
- <code>${AgeRangeEnum10.SENIOR}</code> (65+ years old)

<strong>\u26A0\uFE0F If you do not ask for the travelers' ages, please put <code>${AgeRangeEnum10.ADULT}</code> by default.</strong>`,
    example: AgeRangeEnum10.ADULT
  }),
  residenceCountryCode: extendApi107(countryCodeSchema21.optional(), {
    description: "You can precise a country of residence if the quote is restricted to specific areas",
    example: "FR"
  }),
  birthday: extendApi107(birthdaySchema.optional(), {
    description: "Birthday date of the traveler.",
    example: "1990-02-10"
  }),
  numberOfCheckedBags: extendApi107(z399.number().int().positive().optional(), {
    description: `The total number of checked bags that this traveler will bring.<br/>
      A checked bag is typically a larger piece of baggage that the traveler gives to the airline at the check-in counter to be transported in the airplane\u2019s cargo hold (not carried into the cabin).`,
    example: 2
  }),
  address: richAddressSchema.optional()
});

// src/resources/subscriptions/update-subscription/update-subscription-req-body.schema.ts
var updateSubscriptionReqBodySchema = z400.strictObject({
  data: extendApi108(z400.strictObject({
    subscriptionDate: dateTimeWithUtcOffsetSchema44.optional(),
    customer: extendApi108(updateSubscriptionCustomerSchema.partial().optional(), {
      description: "Customer who booked the trip.",
      hideDefinitions: [
        "address"
      ]
    }),
    quotes: extendApi108(z400.array(z400.strictObject({
      reference: extendApi108(z400.string().min(1), {
        description: "Reference you got when you call the `POST /quotes` endpoint.",
        example: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.e30.UgdscV_5h6HnNxcA9UoIk36H9b1ISEUJ-meuZiLUwM8"
      }),
      travelers: extendApi108(z400.array(extendApi108(updateSubscriptionTravelerSchema, {
        hideDefinitions: [
          "address"
        ]
      })).min(1), {
        description: "Details of the travelers participating in the trip.<br/><strong>\u26A0\uFE0F The age range of travelers should be equivalent to that of the quote.</strong>"
      })
    })).min(1).optional(), {
      description: "List of updated quotes you want to subscribe to."
    })
  }), {
    hideDefinitions: [
      "subscriptionDate"
    ]
  })
});

// src/resources/subscriptions/update-subscription/update-subscription-req-params.schema.ts
import { extendApi as extendApi109 } from "@anatine/zod-openapi";
import { z as z401 } from "zod";
import { bookingNumberSchema as bookingNumberSchema9 } from "@repo/schema-common";
var updateSubscriptionReqParamsSchema = z401.strictObject({
  bookingNumber: extendApi109(bookingNumberSchema9, {
    description: "The booking reference number associated with the subscription. It must be unique. This booking number may be used inside our communications and will help to identify the booking when we interact together. <br/><strong>\u26A0\uFE0F Please use the booking number that you share with your customers.</strong>",
    example: "5K7SXD"
  })
});

// src/resources/subscriptions/update-subscription/update-subscription-req-query.schema.ts
import { extendApi as extendApi110 } from "@anatine/zod-openapi";
import { z as z402 } from "zod";
var updateSubscriptionReqQuerySchema = extendApi110(z402.strictObject({
  sideEffects: z402.boolean().default(true)
}), {
  hideDefinitions: [
    "sideEffects"
  ]
});

// src/resources/subscriptions/update-subscription/update-subscription-res-body.schema.ts
import { extendApi as extendApi111 } from "@anatine/zod-openapi";
import { z as z403 } from "zod";
import { bookingNumberSchema as bookingNumberSchema10, languageSchema as languageSchema5, MAX_DECIMAL_PRECISION as MAX_DECIMAL_PRECISION2, priceAmountSchema as priceAmountSchema5, priceDeltaAmountSchema, priceSchema as priceSchema58 } from "@repo/schema-common";
var updateSubscriptionResBodySchema = z403.strictObject({
  data: z403.strictObject({
    bookingNumber: extendApi111(bookingNumberSchema10, {
      description: "The booking reference number associated with the subscription. It must be unique. This booking number may be used inside our communications and will help to identify the booking when we interact together.",
      example: "5K7SXD"
    }),
    contracts: extendApi111(z403.array(z403.union([
      flightContractSchema,
      tripContractSchema
    ])).min(1), {
      description: "List of contracts linked to this subscription."
    }),
    customer: extendApi111(updateSubscriptionCustomerSchema.pick({
      firstName: true,
      lastName: true,
      email: true,
      phoneNumber: true
    }).extend({
      language: extendApi111(languageSchema5, {
        description: "Language of the customer. It must be a country language code like <code>[language designator]-[region designator]</code>, with an ISO 639-1 alpha-2 code for the <code>language designator</code> and an ISO 3166-1 for the <code>region designator</code>.",
        example: "en-GB",
        minLength: 5,
        maxLength: 5
      })
    }).partial({
      phoneNumber: true
    }), {
      description: "Customer who booked the trip."
    }),
    price: extendApi111(priceSchema58.extend({
      partnerCommission: extendApi111(priceAmountSchema5.optional(), {
        description: "Partner commission with a maximum of 4 decimals.",
        example: 20.1
      })
    }), {
      description: "Total price of the quotes you provided."
    }),
    priceDelta: extendApi111(priceSchema58.pick({
      currency: true
    }).extend({
      amount: extendApi111(priceDeltaAmountSchema, {
        description: `Amount with a maximum of ${MAX_DECIMAL_PRECISION2} decimals.`,
        example: 103.86,
        type: "number"
      }),
      partnerCommission: extendApi111(priceDeltaAmountSchema.optional(), {
        description: "Partner commission delta with a maximum of 4 decimals.",
        example: 1.52
      })
    }), {
      description: "Delta between the new price and the previous price of the subscription before update."
    })
  })
});

// src/resources/subscriptions/update-subscription-trips/update-subscription-trips-req-body.schema.ts
import { z as z404 } from "zod";
import { dateTimeWithUtcOffsetSchema as dateTimeWithUtcOffsetSchema45 } from "@repo/schema-common";
var travelersSchema = z404.array(updateSubscriptionTravelerSchema).min(1);
var updateSubscriptionTripsReqBodySchema = z404.strictObject({
  data: z404.strictObject({
    subscriptionDate: dateTimeWithUtcOffsetSchema45.optional(),
    customer: updateSubscriptionCustomerSchema.partial().optional(),
    trips: z404.strictObject({
      replace: z404.array(z404.strictObject({
        tripId: z404.string().uuid(),
        reference: z404.string().min(1),
        travelers: travelersSchema
      })).optional(),
      add: z404.array(z404.strictObject({
        reference: z404.string().min(1),
        travelers: travelersSchema
      })).optional(),
      remove: z404.array(z404.strictObject({
        tripId: z404.string().uuid()
      })).optional()
    }).optional()
  })
});

// src/resources/subscriptions/update-subscription-trips/update-subscription-trips-req-params.schema.ts
import { z as z405 } from "zod";
import { bookingNumberSchema as bookingNumberSchema11 } from "@repo/schema-common";
var updateSubscriptionTripsReqParamsSchema = z405.strictObject({
  bookingNumber: bookingNumberSchema11
});

// src/resources/subscriptions/update-subscription-trips/update-subscription-trips-req-query.schema.ts
import { z as z406 } from "zod";
import { flexibleBooleanSchema as flexibleBooleanSchema5 } from "@repo/schema-common";
var updateSubscriptionTripsReqQuerySchema = z406.strictObject({
  dryRun: flexibleBooleanSchema5.default(false),
  sideEffects: flexibleBooleanSchema5.default(true)
});

// src/resources/subscriptions/update-subscription-trips/update-subscription-trips-res-body.schema.ts
import { z as z410 } from "zod";
import { bookingNumberSchema as bookingNumberSchema12, priceDeltaSchema as priceDeltaSchema2, priceSchema as priceSchema61 } from "@repo/schema-common";

// src/resources/subscriptions/update-subscription-trips/update-subscription-trips-res-body-trip.schema.ts
import { z as z409 } from "zod";
import { priceDeltaSchema, priceSchema as priceSchema60 } from "@repo/schema-common";

// src/resources/subscriptions/update-subscription-trips/update-subscription-trips-res-body-bundle.schema.ts
import { z as z407 } from "zod";
var updateSubscriptionTripsResBodyBundleSchema = z407.strictObject({
  id: z407.string().uuid(),
  name: z407.string()
});

// src/resources/subscriptions/update-subscription-trips/update-subscription-trips-res-body-created-contract.schema.ts
import { z as z408 } from "zod";
import { ProductTypeEnum as ProductTypeEnum36 } from "@repo/lib-koala-types";
import { priceSchema as priceSchema59 } from "@repo/schema-common";
var updateSubscriptionTripsResBodyCreatedContractSchema = z408.strictObject({
  id: z408.string().uuid().optional(),
  productName: z408.string(),
  productType: z408.nativeEnum(ProductTypeEnum36),
  price: priceSchema59
});

// src/resources/subscriptions/update-subscription-trips/updated-trip-operation.ts
var UpdatedTripOperations = {
  REPLACED: "REPLACED",
  ADDED: "ADDED",
  REMOVED: "REMOVED"
};

// src/resources/subscriptions/update-subscription-trips/update-subscription-trips-res-body-trip.schema.ts
var updateSubscriptionTripsResBodyTripSchema = z409.strictObject({
  operation: z409.nativeEnum(UpdatedTripOperations),
  tripId: z409.string().uuid().optional(),
  newTripId: z409.string().uuid().optional(),
  bundle: updateSubscriptionTripsResBodyBundleSchema,
  previousBundle: updateSubscriptionTripsResBodyBundleSchema.optional(),
  contracts: z409.strictObject({
    created: z409.array(updateSubscriptionTripsResBodyCreatedContractSchema),
    replaced: z409.array(z409.string().uuid()),
    renounced: z409.array(z409.string().uuid())
  }),
  price: priceSchema60.optional(),
  priceDelta: priceDeltaSchema
});

// src/resources/subscriptions/update-subscription-trips/update-subscription-trips-res-body.schema.ts
var updateSubscriptionTripsResBodySchema = z410.strictObject({
  data: z410.strictObject({
    bookingNumber: bookingNumberSchema12,
    trips: z410.array(updateSubscriptionTripsResBodyTripSchema),
    customer: updateSubscriptionResBodySchema.shape.data.shape.customer,
    price: priceSchema61,
    priceDelta: priceDeltaSchema2
  })
});

// src/resources/wise-quotes/refresh-wise-quote-account-requirements/refresh-wise-quote-account-requirements-req-body.schema.ts
import { z as z411 } from "zod";
var refreshWiseQuoteAccountRequirementsReqBodySchema = z411.strictObject({
  accountData: wiseRecipientAccountDataSchema
});

// src/resources/wise-quotes/refresh-wise-quote-account-requirements/refresh-wise-quote-account-requirements-req-params.schema.ts
import { z as z412 } from "zod";
var refreshWiseQuoteAccountRequirementsReqParamsSchema = z412.strictObject({
  quoteId: z412.string().uuid()
});

// src/resources/wise-quotes/refresh-wise-quote-account-requirements/refresh-wise-quote-account-requirements-res-body.schema.ts
import { z as z413 } from "zod";
var refreshWiseQuoteAccountRequirementsResBodySchema = z413.object({
  data: z413.object({
    accountRequirements: z413.array(wiseAccountRequirementSchema)
  })
});

// src/resources/wise-recipients/create-wise-recipient/create-wise-recipient-req-body.schema.ts
import { z as z414 } from "zod";
var createWiseRecipientReqBodySchema = z414.strictObject({
  accountData: wiseRecipientAccountDataSchema
});

// src/resources/wise-recipients/create-wise-recipient/create-wise-recipient-res-body.schema.ts
import { z as z415 } from "zod";
var createWiseRecipientResBodySchema = z415.strictObject({
  data: z415.strictObject({
    bankDetailId: z415.string().uuid(),
    wiseRecipientAccountId: z415.number().int()
  })
});

// src/resources/wise-recipients/wise-recipient-error.types.ts
var WiseRecipientErrorTypes = /* @__PURE__ */ function(WiseRecipientErrorTypes2) {
  WiseRecipientErrorTypes2["SUBSCRIPTION_ALREADY_HAS_PAID_CLAIM"] = "SUBSCRIPTION_ALREADY_HAS_PAID_CLAIM";
  WiseRecipientErrorTypes2["BANK_DETAILS_LIMIT_REACHED"] = "BANK_DETAILS_LIMIT_REACHED";
  return WiseRecipientErrorTypes2;
}({});

// src/resources/wise-recipients/wise-recipient-validation-error.schema.ts
import { z as z416 } from "zod";
var wiseRecipientValidationErrorSchema = z416.object({
  code: z416.string().nullish(),
  message: z416.string(),
  path: z416.string().nullish(),
  arguments: z416.array(z416.string()).nullish()
}).passthrough();

// src/compute-price-details/compute-price-details-req-body.schema.ts
var computePriceDetailsReqBodySchema = z417.strictObject({
  product: productSchema,
  currency: isoCurrencySchema13,
  coveredEvent: createQuoteReqBodyTripSchema
});

// src/compute-price-details/compute-price-details-res-body.schema.ts
import { z as z418 } from "zod";
var computePriceDetailsResBodySchema = z418.strictObject({
  pricer: z418.string(),
  retailPremiumTaxesIncluded: z418.number(),
  grossNetWrittenPremium: z418.number(),
  koalaCommission: z418.number(),
  partnerCommission: z418.number(),
  insurerCommission: z418.number(),
  businessContributorCommission: z418.number(),
  serviceTax: z418.number(),
  insuranceTax: z418.number(),
  additionalTax: z418.number(),
  brokerageFees: z418.number()
});
export {
  AmendmentQuoteModes,
  BaseConfigurationApiError,
  BundleAlreadyExistsError,
  BundleExistsInTargetStageError,
  BundleIsNotARootBundleError,
  BundleNotFoundError,
  BundleProductsCurrenciesDiscrepanciesError,
  BundlesHaveDifferentRootBundlesError,
  CampaignAlreadyExistsError,
  CampaignAndBundleMismatchOnRootBundleError,
  CampaignInvalidBundleWeightsError,
  CampaignNotFoundError,
  CampaignOverlapsOtherCampaignsError,
  CampaignType,
  CreateSubscribedCustomerTokenReqBodyDtoMapper,
  DASHBOARD_COMMISSION_CURVE_POINTS_COUNT,
  DASHBOARD_COMMISSION_CURVE_STEP_MINUTES,
  DefaultCampaignAlreadyExistsError,
  ExceptionType,
  LIST_SUBSCRIPTIONS_DEFAULT_PAGE_SIZE,
  LIST_SUBSCRIPTIONS_MAX_PAGE_SIZE,
  OMIO_CLAIM_RECONCILIATION_FALLBACK_REASONS,
  OMIO_CLAIM_RECONCILIATION_SOURCES,
  OMIO_PROVIDER_REFUNDABILITY_CATEGORIES,
  OperationNotAllowedOnTargetStageError,
  PARTNERSHIP_CONFIGURATION_API_ERRORS,
  PartnerAlreadyExistsError,
  PartnerCountryMissingTaxesError,
  PartnerCountryNotFoundError,
  PartnerNotFoundError,
  PartnerWebhookSecretNotFoundError,
  PartnershipConfigurationErrorTypes,
  ProductAlreadyExistsError,
  ProductExistsInTargetStageError,
  ProductInvalidConfigurationError,
  ProductNotFoundError,
  QuoteDateSourceEnum,
  QuoteOriginEnum,
  QuotePurposeEnum,
  REOPEN_PAYMENT_WINDOW_DEFAULT_EXTENSION_DURATION,
  RootBundleNotFoundError,
  SUBSCRIBED_CUSTOMER,
  UnknownError,
  UpdatedTripOperations,
  WiseForbiddenCountryAlreadyExistsError,
  WiseForbiddenCountryNotFoundError,
  WisePayoutMethodAlreadyExistsError,
  WisePayoutMethodNotFoundError,
  WiseRecipientErrorTypes,
  accountNumberSchema,
  accountSchema,
  addressSchema,
  apexxPaymentCreationDataSchema,
  apexxPaymentSchema,
  attachmentSchema,
  attachmentsCreationDataSchema,
  attachmentsSchema,
  attachmentsUpdateDataSchema,
  avaExternalDataSchema,
  badRequestExceptionSchema,
  baggageDelayDisruptionSchema,
  baggageDisruptionProductSchema,
  baggageLossDisruptionSchema,
  bankDetailsAccountNumberSchema,
  bankDetailsIbanSchema,
  bankDetailsSchema,
  baseCoefficientPricingSettingsSchema,
  baseExceptionSchema,
  baseFixedPricingSettingsSchema,
  baseFlightSchema,
  basePaymentCreationDataSchema,
  basePaymentSchema,
  basePricingSettingsSchema,
  baseProductCompensationSettingSchema,
  baseProductCreationDataSchema,
  baseProductSchema,
  baseProductUpdateDataSchema,
  battlefaceExternalDataSchema,
  bdcDisruptionSchema,
  beneficiaryAddressSchema,
  bicSchema,
  birthdaySchema,
  bundleSchema,
  campaignBundleCreationDataSchema,
  campaignBundleSchema,
  campaignSchema,
  cancellationWithoutProofProductSchema,
  cardPaymentDetailsSchema,
  changeForAnyReasonProductSchema,
  claimDataSchema,
  claimDistributionContextSchema,
  claimReasonsSchema,
  claimSettingsCreationDataSchema,
  claimSettingsSchema,
  coefficientCompensationSettingSchema,
  commercialGesturePaymentCreationDataSchema,
  commercialGesturePaymentSchema,
  commissionSettingsItemSchema,
  commissionSettingsSchema,
  commissionsSettingsCreationDataSchema,
  commissionsSettingsUpdateDataSchema,
  communicationSettingsCreationDataSchema,
  communicationSettingsSchema,
  compensationSettingSchema,
  compensationSettingsCreationDataSchema,
  comprehensivePerCurrencySettingsCreationDataSchema,
  comprehensivePerCurrencySettingsSchema,
  comprehensivePerCurrencySettingsUpdateDataSchema,
  comprehensiveProductAttachmentsCreationDataSchema,
  comprehensiveProductAttachmentsUpdateDataSchema,
  comprehensiveProductSchema,
  computePriceDetailsReqBodySchema,
  computePriceDetailsResBodySchema,
  confirmBookingCancellationContractSchema,
  confirmBookingCancellationReqParamsSchema,
  confirmBookingCancellationResBodySchema,
  conflictExceptionSchema,
  contractClaimSchema,
  contractDocumentSchema,
  contractFinancialInfoSchema,
  contractFlightSchema,
  contractTravelerSchema,
  countryOnlyAddressSchema,
  createAccountTokenReqBodySchema,
  createAccountTokenResBodySchema,
  createBaggageDisruptionProductReqBodySchema,
  createBundleQuoteReqBodySchema,
  createBundleQuoteReqParamsSchema,
  createBundleQuoteResBodySchema,
  createBundleReqBodySchema,
  createBundleReqParamsSchema,
  createBundleResBodySchema,
  createCampaignReqBodySchema,
  createCampaignReqParamsSchema,
  createCampaignResBodySchema,
  createCancellationWithoutProofProductReqBodySchema,
  createChangeForAnyReasonClaimSchema,
  createChangeForAnyReasonProductReqBodySchema,
  createClaimGoodwillGestureReqBodySchema,
  createClaimGoodwillGestureReqParamsSchema,
  createClaimGoodwillGestureReqQuerySchema,
  createClaimGoodwillGestureResBodySchema,
  createClaimRefundReqParamsSchema,
  createClaimRefundReqQuerySchema,
  createClaimRefundResBodySchema,
  createClaimRejectionReqBodySchema,
  createClaimRejectionReqParamsSchema,
  createClaimRejectionReqQuerySchema,
  createClaimRejectionResBodySchema,
  createClaimReqBodySchema,
  createClaimReqParamsSchema,
  createClaimReqQuerySchema,
  createClaimResBodySchema,
  createClaimStatusSchema,
  createClaimVerificationReqBodySchema,
  createClaimVerificationReqParamsSchema,
  createClaimVerificationReqQuerySchema,
  createClaimVerificationResBodySchema,
  createComprehensiveProductReqBodySchema,
  createCustomerContractClaimBdcReqBodyDataSchema,
  createCustomerContractClaimCwpReqBodyDataSchema,
  createCustomerContractClaimReqBodySchema,
  createCustomerContractClaimReqParamsSchema,
  createCustomerContractClaimResBodySchema,
  createCustomerContractDocumentReqBodySchema,
  createCustomerContractDocumentReqParamsSchema,
  createCustomerContractDocumentResBodySchema,
  createCustomerSubscriptionBankDetailsReqBodySchema,
  createCustomerSubscriptionBankDetailsReqParamsSchema,
  createCustomerSubscriptionBankDetailsResBodySchema,
  createDefaultCampaignReqBodySchema,
  createExchangeRateRequestsReqQuerySchema,
  createFdcClaimSchema,
  createFdcProductReqBodySchema,
  createFlightMonitoringReqQuerySchema,
  createFlightMonitoringResBodySchema,
  createFlightStatusReqBodySchema,
  createFlightStatusReqParamsSchema,
  createFlightStatusResBodySchema,
  createFlightTrackerReqBodySchema,
  createFlightTrackerReqParamsSchema,
  createFlightTrackerResBodySchema,
  createNewBundleVersionReqBodySchema,
  createNewBundleVersionReqParamsSchema,
  createNewBundleVersionResBodySchema,
  createNonDefaultCampaignReqBodySchema,
  createPartnerReqBodySchema,
  createPartnerResBodySchema,
  createProductReqBodySchema,
  createProductReqParamsSchema,
  createProductResBodySchema,
  createQuoteJourneyWithSegmentsSchema,
  createQuoteReqBodyFlightSegmentSchema,
  createQuoteReqBodyFlightWithTimeZoneSchema,
  createQuoteReqBodyFlightWithoutTimeZoneSchema,
  createQuoteReqBodyJourneySchema,
  createQuoteReqBodyJourneyWithFlightsSchema,
  createQuoteReqBodySchema,
  createQuoteReqBodySegmentSchema,
  createQuoteReqBodyTravelerSchema,
  createQuoteReqBodyTripSchema,
  createQuoteReqBodyTripWithJourneysSchema,
  createQuoteReqBodyTripWithStaysSchema,
  createQuoteResBodyContractSchema,
  createQuoteResBodyDocumentSchema,
  createQuoteResBodyInvalidQuotedBundleSchema,
  createQuoteResBodyJourneySchema,
  createQuoteResBodyQuotedBundleBaseSchema,
  createQuoteResBodyQuotedBundleSchema,
  createQuoteResBodySchema,
  createQuoteResBodyTravelerSchema,
  createQuoteResBodyTripBaseSchema,
  createQuoteResBodyTripSchema,
  createQuoteResBodyTripWithJourneysSchema,
  createQuoteResBodyTripWithStaysSchema,
  createQuoteResBodyValidQuotedBundleSchema,
  createSubscribedCustomerTokenReqBodySchema,
  createSubscribedCustomerTokenResBodySchema,
  createSubscriptionPaymentsReqBodySchema,
  createSubscriptionPaymentsReqParamsSchema,
  createSubscriptionPaymentsResBodySchema,
  createSubscriptionQuoteReqBodySchema,
  createSubscriptionQuoteReqParamsSchema,
  createSubscriptionQuoteResBodyInvalidQuotedBundleSchema,
  createSubscriptionQuoteResBodyQuotedBundleSchema,
  createSubscriptionQuoteResBodySchema,
  createSubscriptionQuoteResBodyValidQuotedBundleSchema,
  createSubscriptionReqBodyCustomerSchema,
  createSubscriptionReqBodySchema,
  createSubscriptionReqBodyTravelerSchema,
  createSubscriptionReqQuerySchema,
  createSubscriptionResBodySchema,
  createWiseForbiddenCountryReqBodySchema,
  createWiseForbiddenCountryResBodySchema,
  createWisePayoutMethodReqBodySchema,
  createWisePayoutMethodResBodySchema,
  createWiseRecipientReqBodySchema,
  createWiseRecipientResBodySchema,
  createWithdrawalClaimSchema,
  createWithdrawalProductReqBodySchema,
  currenciesSchema,
  customerSchema,
  decodeQuoteKoalaResBodySchema,
  decodeQuoteOpenApiDocument,
  decodeQuoteReqBodySchema,
  decodeQuoteResBodySchema2 as decodeQuoteResBodySchema,
  deleteClaimReqParamsSchema,
  deleteClaimResBodySchema,
  deleteContractReqBodySchema,
  deleteContractReqParamsSchema,
  deleteCustomerContractDocumentReqParamsSchema,
  deleteProductReqParamsSchema,
  deleteProductReqQuerySchema,
  deleteQuoteReqParamsSchema,
  deleteQuoteResBodySchema,
  deleteStaleContractDocumentsResBodySchema,
  deleteWiseForbiddenCountryReqParamsSchema,
  distributionContextSchema,
  distributionContextTravelAgentSchema,
  encryptedCardPaymentDetailsSchema,
  encryptedFieldSchema,
  eurAmountSchema,
  findOneContractReqParamsSchema,
  findOneContractResBodySchema,
  fixedCompensationSettingSchema,
  flightContractSchema,
  flightDisruptionProductSchema,
  flightSchema,
  flightSegmentSchema,
  flightWithLocalDatesSchema,
  flightWithTimeZoneSchema,
  flightWithoutTimeZoneSchema,
  forbiddenExceptionSchema,
  genericSegmentSchema,
  getActiveProductsReqParamsSchema,
  getActiveProductsResBodySchema,
  getAllSubscriptionContractsReqParamsSchema,
  getAllSubscriptionContractsResBodySchema,
  getBaggageDisruptionProductResBodySchema,
  getBundleOverviewReqParamsSchema,
  getBundleOverviewResBodySchema,
  getBundleReqParamsSchema,
  getBundleResBodySchema,
  getBundlesOverviewReqParamsSchema,
  getBundlesOverviewResBodySchema,
  getBundlesReqParamsSchema,
  getBundlesResBodySchema,
  getCampaignPublicationStatusReqParamsSchema,
  getCampaignPublicationStatusResBodySchema,
  getCampaignReqParamsSchema,
  getCampaignResBodySchema,
  getCampaignsOverviewReqParamsSchema,
  getCampaignsOverviewResBodySchema,
  getCampaignsPublicationStatusReqParamsSchema,
  getCampaignsPublicationStatusResBodySchema,
  getCampaignsReqParamsSchema,
  getCampaignsResBodySchema,
  getChangeForAnyReasonProductResBodySchema,
  getComprehensiveProductResBodySchema,
  getCustomerContractDocumentReqParamsSchema,
  getCustomerContractDocumentResBodySchema,
  getCustomerContractLatestClaimReqParamsSchema,
  getCustomerContractLatestClaimResBodySchema,
  getCustomerContractWiseAccountRequirementsReqParamsSchema,
  getCustomerContractWiseAccountRequirementsResBodySchema,
  getCustomerSubscriptionResBodyAddressSchema,
  getCustomerSubscriptionResBodyContractClaimSchema,
  getCustomerSubscriptionResBodyContractSchema,
  getCustomerSubscriptionResBodyCoveredEventSchema,
  getCustomerSubscriptionResBodyFlightCoveredEventSchema,
  getCustomerSubscriptionResBodyFlightSchema,
  getCustomerSubscriptionResBodyFlightSegmentSchema,
  getCustomerSubscriptionResBodyGenericSegmentSchema,
  getCustomerSubscriptionResBodyGenericSegmentStopSchema,
  getCustomerSubscriptionResBodyJourneySchema,
  getCustomerSubscriptionResBodySchema,
  getCustomerSubscriptionResBodyStaySchema,
  getCustomerSubscriptionResBodyTravelersSchema,
  getCustomerSubscriptionResBodyTripCoveredEventSchema,
  getCustomerSubscriptionResBodyTripSchema,
  getDashboardReferenceResBodySchema,
  getDashboardSnapshotResBodySchema,
  getDocumentReqParamsSchema,
  getDocumentResBodySchema,
  getFdcProductResBodySchema,
  getPartnerOverviewReqParamsSchema,
  getPartnerOverviewResBodySchema,
  getPartnerPublicationStatusReqParamsSchema,
  getPartnerPublicationStatusResBodySchema,
  getPartnerReqParamsSchema,
  getPartnerResBodySchema,
  getProductReqParamsSchema,
  getProductResBodySchema,
  getProductsOverviewReqParamsSchema,
  getProductsOverviewResBodySchema,
  getProductsReqParamsSchema,
  getProductsResBodySchema,
  getSubscriptionPaymentsReqParamsSchema,
  getSubscriptionPaymentsResBodySchema,
  getSubscriptionReqParamsSchema,
  getSubscriptionResBodyCustomerSchema,
  getSubscriptionResBodySchema,
  getSubscriptionResBodyTripSchema,
  getWiseForbiddenCountriesResBodySchema,
  getWisePayoutMethodsResBodySchema,
  getWisePayoutMethodsRichResBodySchema,
  getWithdrawalProductResBodySchema,
  goneExceptionSchema,
  ibanSchema,
  journeySchema,
  koalaTokenPayloadSchema,
  languagesSchema,
  listPartnersPublicationStatusResBodySchema,
  listPartnersResBodySchema,
  listSubscriptionsReqQuerySchema,
  listSubscriptionsResBodyCustomerSchema,
  listSubscriptionsResBodyMetaSchema,
  listSubscriptionsResBodySchema,
  listSubscriptionsResBodySubscriptionSchema,
  loginReqBodySchema,
  loginResBodySchema,
  manualPaymentCreationDataSchema,
  manualPaymentSchema,
  meetchExternalDataSchema,
  noStatusAvailableSchema,
  notFoundExceptionSchema,
  notImplementedExceptionSchema,
  omioClaimReconciliationSchema,
  omioTicketRefundContextSchema,
  partnerBankDetailsAccountNumberSchema,
  partnerBankDetailsBaseSchema,
  partnerBankDetailsIbanSchema,
  partnerBankDetailsSchema,
  partnerSchema,
  partnerTokenPayloadSchema,
  partnerVisibleQuoteReferenceSchema,
  paymentCreationDataSchema,
  paymentInfoMerchantOfRecordSchema,
  paymentInfoNotMerchantOfRecordSchema,
  paymentInfoSchema,
  perCurrencySettingsCreationDataSchema,
  perCurrencySettingsSchema,
  perCurrencySettingsUpdateDataSchema,
  perProductClassificationDataCreationDataSchema,
  perProductClassificationDataUpdateDataSchema,
  priceDetailsSchema,
  pricingInternalDataOutputSchema,
  pricingSettingsCreationDataSchema,
  pricingSettingsSchema,
  productAttachmentSchema,
  productPricingOverviewSchema,
  productProviderExternalDataSchema,
  productSchema,
  publicPartnerCreateQuoteReqBodyFlightSegmentSchema,
  publicPartnerCreateQuoteReqBodyGenericSegmentSchema,
  publicPartnerCreateQuoteReqBodyJourneySchema,
  publicPartnerCreateQuoteReqBodySchema,
  publicPartnerCreateQuoteReqBodySegmentSchema,
  publicPartnerCreateQuoteReqBodyStaySchema,
  publicPartnerCreateQuoteReqBodyTravelerSchema,
  publicPartnerCreateQuoteReqBodyTripSchema,
  publicPartnerCreateQuoteReqParamsSchema,
  publicPartnerCreateQuoteResBodyContractSchema,
  publicPartnerCreateQuoteResBodyDocumentSchema,
  publicPartnerCreateQuoteResBodyInvalidQuoteSchema,
  publicPartnerCreateQuoteResBodyQuoteBaseSchema,
  publicPartnerCreateQuoteResBodyQuoteSchema,
  publicPartnerCreateQuoteResBodySchema,
  publicPartnerCreateQuoteResBodyValidQuoteSchema,
  publicPartnerCreateSubscriptionReqBodySchema,
  publicPartnerCreateSubscriptionReqParamsSchema,
  publicPartnerCreateSubscriptionResBodySchema,
  publicPartnerDeleteQuoteReqBodySchema,
  publicPartnerDeleteQuoteReqParamsSchema,
  publishCampaignReqParamsSchema,
  publishCampaignResBodySchema,
  publishPartnerReqBodySchema,
  publishPartnerReqParamsSchema,
  publishPartnerResBodySchema,
  publishWisePayoutMethodReqParamsSchema,
  publishWisePayoutMethodResBodySchema,
  quoteReferenceAirportSchema,
  quoteReferenceContractSchema,
  quoteReferenceCountrySchema,
  quoteReferenceFlightSchema,
  quoteReferenceFlightSegmentSchema,
  quoteReferenceGenericSegmentSchema,
  quoteReferenceJourneySchema,
  quoteReferenceSchema,
  quoteReferenceSegmentSchema,
  quoteReferenceStaySchema,
  quoteReferenceTravelers,
  quoteReferenceTripSchema,
  quotedBundleBaseSchema,
  quotedBundleInvalidSchema,
  quotedBundleValidSchema,
  refreshWiseQuoteAccountRequirementsReqBodySchema,
  refreshWiseQuoteAccountRequirementsReqParamsSchema,
  refreshWiseQuoteAccountRequirementsResBodySchema,
  reopenPaymentWindowReqBodySchema,
  reopenPaymentWindowReqParamsSchema,
  reopenPaymentWindowResBodySchema,
  replaceDocumentReqBodySchema,
  replaceDocumentReqParamsSchema,
  replaceDocumentResBodySchema,
  richAddressSchema,
  richContractFlightSchema,
  richContractSchema,
  richFlightContractSchema,
  richFlightSegmentSchema,
  richTripContractSchema,
  richTripCoveredEventSchema,
  segmentSchema,
  segmentStopSchema,
  sideEffectsSchema,
  staySchema,
  stripePaymentCreationDataSchema,
  stripePaymentSchema,
  subscribedCustomerTokenPayloadSchema,
  subscriptionSettingsCreationDataSchema,
  subscriptionSettingsSchema,
  subscriptionSettingsUpdateDataSchema,
  subscriptionTravelerSchema,
  synchronizationSettingsCreationDataSchema,
  synchronizationSettingsSchema,
  synchronizationSettingsUpdateDataSchema,
  thirdPartyTokenPayloadSchema,
  tripContractSchema,
  unprocessableEntityExceptionSchema,
  updateBaggageDisruptionProductReqBodySchema,
  updateBundleReqBodySchema,
  updateBundleReqParamsSchema,
  updateBundleResBodySchema,
  updateCampaignReqBodySchema,
  updateCampaignReqParamsSchema,
  updateCampaignResBodySchema,
  updateCancellationWithoutProofProductReqBodySchema,
  updateChangeForAnyReasonProductReqBodySchema,
  updateComprehensiveProductReqBodySchema,
  updateDocumentReqBodySchema,
  updateDocumentReqParamsSchema,
  updateDocumentResBodySchema,
  updateFdcProductReqBodySchema,
  updatePartnerReqBodySchema,
  updatePartnerReqParamsSchema,
  updatePartnerResBodySchema,
  updateProductReqBodySchema,
  updateProductReqParamsSchema,
  updateProductResBodySchema,
  updateSubscriptionBankDetailsReqBodySchema,
  updateSubscriptionBankDetailsReqParamsSchema,
  updateSubscriptionBankDetailsReqQuerySchema,
  updateSubscriptionCustomerSchema,
  updateSubscriptionReqBodySchema,
  updateSubscriptionReqParamsSchema,
  updateSubscriptionReqQuerySchema,
  updateSubscriptionResBodySchema,
  updateSubscriptionTravelerSchema,
  updateSubscriptionTripsReqBodySchema,
  updateSubscriptionTripsReqParamsSchema,
  updateSubscriptionTripsReqQuerySchema,
  updateSubscriptionTripsResBodyBundleSchema,
  updateSubscriptionTripsResBodyCreatedContractSchema,
  updateSubscriptionTripsResBodySchema,
  updateSubscriptionTripsResBodyTripSchema,
  updateWiseForbiddenCountryReqBodySchema,
  updateWiseForbiddenCountryReqParamsSchema,
  updateWiseForbiddenCountryResBodySchema,
  updateWisePayoutMethodReqBodySchema,
  updateWisePayoutMethodReqParamsSchema,
  updateWisePayoutMethodResBodySchema,
  updateWithdrawalProductReqBodySchema,
  upsertExternalClaimReqBodySchema,
  upsertExternalClaimReqParamsSchema,
  upsertExternalClaimResBodySchema,
  wakamExternalDataSchema,
  wiseAccountRequirementFieldGroupSchema,
  wiseAccountRequirementFieldGroupValueAllowedSchema,
  wiseAccountRequirementFieldSchema,
  wiseAccountRequirementSchema,
  wiseForbiddenCountrySchema,
  wisePayoutMethodSchema,
  wiseRecipientAccountDataSchema,
  wiseRecipientValidationErrorSchema,
  withdrawalCoefficientPricingSettingsCreationDataSchema,
  withdrawalCoefficientPricingSettingsSchema,
  withdrawalFixedPricingSettingsCreationDataSchema,
  withdrawalFixedPricingSettingsSchema,
  withdrawalPerCurrencySettingsCreationDataSchema,
  withdrawalPerCurrencySettingsUpdateDataSchema,
  withdrawalPricingSettingsCreationDataSchema,
  withdrawalPricingSettingsSchema,
  withdrawalProductSchema
};
//# sourceMappingURL=index.js.map